// Visualization Filters
// Updated for OpenFOAM unstructured mesh data

class Filters {
  constructor(renderingEngine) {
    this.engine = renderingEngine;
    this.activeFilters = new Map();
    this.viewer = null;
    this.streamlineUserSettings = new Map();
    this.seedInputListenersAttached = false;
    this.lastCaseId = null; // Track case changes to reset defaults
    this.sliceWidget = null; // Interactive slice widget
    this.cuttingPlaneWidget = null; // Interactive cutting plane widget
    this.setupFilterUI();
  }

  getCurrentCasePath() {
    const viewer = window.viewer || this.viewer;
    if (!viewer) {
      return null;
    }
    if (viewer.currentCasePath) {
      return viewer.currentCasePath;
    }
    const data = viewer.currentData;
    if (!data) {
      return null;
    }
    return data.case_path
      || data.metadata?.case_path
      || data.metadata?.casePath
      || null;
  }

  /**
   * Reusable utility to execute an async operation with an inline progress bar
   * Matches the pattern used by Glyph filter
   *
   * @param {string} buttonId - ID of the button element
   * @param {Function} asyncOperation - Async function to execute
   * @param {Object} options - Options: { startMessage, successMessage, errorPrefix, progressBarId }
   */
  async applyWithProgressBar(buttonId, asyncOperation, options = {}) {
    const {
      startMessage = 'Processing...',
      successMessage = 'Operation completed successfully',
      errorPrefix = 'Error',
      progressBarId = `${buttonId}ProgressBar`,
      progressTextId = `${buttonId}ProgressText`
    } = options;

    const btn = document.getElementById(buttonId);
    if (!btn) {
      console.error(`[applyWithProgressBar] Button not found: ${buttonId}`);
      return;
    }

    let progressInterval = null;
    let originalButtonHTML = null;

    try {
      Utils.notify(startMessage, 'info');

      // Update button state with progress bar
      btn.disabled = true;
      originalButtonHTML = btn.innerHTML;
      btn.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="flex: 1; height: 8px; background: rgba(255,255,255,0.2); border-radius: 4px; overflow: hidden;">
            <div id="${progressBarId}" style="height: 100%; width: 0%; background: linear-gradient(90deg, #4CAF50, #8BC34A); transition: width 0.3s ease;"></div>
          </div>
          <span id="${progressTextId}" style="font-size: 11px;">0%</span>
        </div>
      `;

      // Start simulated progress indicator
      let progress = 0;
      progressInterval = setInterval(() => {
        if (progress < 90) {
          progress += Math.random() * 10; // Random increments for smooth animation
          if (progress > 90) progress = 90;
          const progressBar = document.getElementById(progressBarId);
          const progressText = document.getElementById(progressTextId);
          if (progressBar) progressBar.style.width = `${progress}%`;
          if (progressText) progressText.textContent = `${Math.round(progress)}%`;
        }
      }, 500); // Update every 500ms

      // Execute the async operation
      await asyncOperation();

      // Clear progress interval and show 100%
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      const progressBar = document.getElementById(progressBarId);
      const progressText = document.getElementById(progressTextId);
      if (progressBar) progressBar.style.width = '100%';
      if (progressText) progressText.textContent = '100%';

      Utils.notify(successMessage, 'success');

    } catch (error) {
      console.error(`[applyWithProgressBar] ${errorPrefix}:`, error);
      Utils.notify(`${errorPrefix}: ${error.message ?? error}`, 'error');
    } finally {
      // Clear progress interval if still running
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Restore button state
      btn.disabled = false;
      if (originalButtonHTML) {
        btn.innerHTML = originalButtonHTML;
      }
    }
  }

  // Setup UI controls for filters
  setupFilterUI() {
    // Slice Filter - Now using interactive widget
    const sliceEnabled = document.getElementById('sliceFilterEnabled');
    const sliceControls = document.getElementById('sliceControls');
    if (sliceEnabled && sliceControls) {
      sliceEnabled.addEventListener('change', (e) => {
        sliceControls.style.display = e.target.checked ? 'flex' : 'none';
        // Interactive widget will handle enable/disable automatically
        // via its own event listener

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      // Keep the Apply button for manual updates
      document.getElementById('btnApplySlice')?.addEventListener('click', () => {
        if (this.sliceWidget && this.sliceWidget.enabled) {
          this.sliceWidget.updateSlice();
        } else {
          this.applySliceFromUI();
        }
      });
    }

    // Contour Filter
    const contourEnabled = document.getElementById('contourFilterEnabled');
    const contourControls = document.getElementById('contourControls');
    if (contourEnabled && contourControls) {
      contourEnabled.addEventListener('change', (e) => {
        contourControls.style.display = e.target.checked ? 'flex' : 'none';

        // Toggle visibility instead of removing the filter
        const filterEntry = this.activeFilters.get('contour-filter');
        if (filterEntry) {
          if (filterEntry.mesh) {
            filterEntry.mesh.visible = e.target.checked;
          }
          // Toggle label visibility (HTML elements use style.display)
          if (filterEntry.labels && Array.isArray(filterEntry.labels)) {
            filterEntry.labels.forEach(label => {
              label.style.display = e.target.checked ? 'block' : 'none';
            });
          }
        }

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      // Auto-spacing toggle
      const autoSpacingCheckbox = document.getElementById('contourAutoSpacing');
      if (autoSpacingCheckbox) {
        autoSpacingCheckbox.addEventListener('change', (e) => {
          const manualControls = document.getElementById('contourManualControls');
          const autoControls = document.getElementById('contourAutoControls');
          if (manualControls && autoControls) {
            manualControls.style.display = e.target.checked ? 'none' : 'block';
            autoControls.style.display = e.target.checked ? 'block' : 'none';
          }
        });
      }

      document.getElementById('btnApplyContour')?.addEventListener('click', () => {
        this.applyContourFromUI();
      });
    }

    // Streamline Filter
    const streamlineEnabled = document.getElementById('streamlineFilterEnabled');
    const streamlineControls = document.getElementById('streamlineControls');
    if (streamlineEnabled && streamlineControls) {
      streamlineEnabled.addEventListener('change', (e) => {
        streamlineControls.style.display = e.target.checked ? 'flex' : 'none';

        // Populate field dropdowns when showing controls
        if (e.target.checked) {
          this.populateStreamlineFields();
          // Don't auto-apply on first enable - let user configure first
          // Auto-apply will trigger when they change any parameter
        } else {
          // Disable streamline plane widget when streamlines are disabled
          if (window.viewer && window.viewer.filters.streamlinePlaneWidget) {
            window.viewer.filters.streamlinePlaneWidget.disable();
          }
        }

        // Toggle visibility instead of removing the filter
        const filterEntry = this.engine.objects.get('streamline-filter');
        if (filterEntry && filterEntry.object) {
          filterEntry.object.visible = e.target.checked;
        }

        // Show/hide the streamline legend
        const legend = document.getElementById('streamlineLegend');
        if (legend) {
          legend.style.display = e.target.checked ? 'block' : 'none';
        }

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      const seedTypeSelect = document.getElementById('streamlineSeedType');
      const seedGroups = streamlineControls.querySelectorAll('[data-seed-group]');
      const updateSeedGroups = () => {
        const active = seedTypeSelect?.value || 'point';
        seedGroups.forEach(group => {
          if (!group.dataset.seedGroup) return;
          const isActive = group.dataset.seedGroup === active;
          group.classList.toggle('hidden', !isActive);
        });

        // Control streamline plane widget visibility based on seed type
        if (window.viewer && window.viewer.filters.streamlinePlaneWidget) {
          const showPlaneWidget = document.getElementById('streamlinePlaneShowWidget');
          if (active === 'plane') {
            // Enable widget if checkbox is checked and we're in plane mode
            if (showPlaneWidget && showPlaneWidget.checked) {
              window.viewer.filters.streamlinePlaneWidget.enable();
            }
          } else {
            // Disable widget when not in plane mode
            window.viewer.filters.streamlinePlaneWidget.disable();
          }
        }
      };

      // Debounced auto-apply for streamlines (define early so we can use it below)
      this.streamlineUpdateTimer = null;
      this.streamlineSyncInProgress = false; // Flag to prevent auto-apply during programmatic updates
      const scheduleStreamlineUpdate = () => {
        // AUTO-APPLY DISABLED: Users must click "Apply Streamlines" button
        console.log('[scheduleStreamlineUpdate] AUTO-APPLY DISABLED - Use Apply button instead');
        return;
      };

      // Apply Streamlines Button Handler - Uses reusable progress bar utility
      const btnApplyStreamlines = document.getElementById('btnApplyStreamlines');
      if (btnApplyStreamlines) {
        btnApplyStreamlines.addEventListener('click', async () => {
          await this.applyWithProgressBar('btnApplyStreamlines', async () => {
            await this.applyStreamlinesFromUI();
          }, {
            startMessage: 'Computing streamlines...',
            successMessage: 'Streamlines applied successfully',
            errorPrefix: 'Streamline error'
          });
        });

        console.log('[Streamlines] Apply button handler registered');
      }

      if (seedTypeSelect) {
        seedTypeSelect.addEventListener('change', () => {
          console.log('[Seed Type Change] Seed type changed to:', seedTypeSelect.value);

          // Auto-enable plane widget when seed type is "plane"
          if (seedTypeSelect.value === 'plane') {
            const showPlaneWidget = document.getElementById('streamlinePlaneShowWidget');
            if (showPlaneWidget && !showPlaneWidget.checked) {
              console.log('[Seed Type Change] Auto-enabling plane widget');
              showPlaneWidget.checked = true;
              showPlaneWidget.dispatchEvent(new Event('change'));
            }
          }

          updateSeedGroups();
          updateSpherePreview(); // Show/hide sphere preview based on seed type
          updateLinePreview(); // Show/hide line preview based on seed type
          updatePlanePreview(); // Show/hide plane preview based on seed type
          scheduleStreamlineUpdate(); // Auto-apply when seed type changes
        });
        updateSeedGroups();
      }

      const colormapSelect = document.getElementById('streamlineColormap');
      if (colormapSelect) {
        // Populate using centralized Colormap utility
        Colormap.populateSelect(colormapSelect, {
          includeSolid: true,
          defaultValue: window.viewer?.colormap?.preset || 'jet'
        });

        // Add color picker for solid color
        Colormap.createColorPicker(colormapSelect, () => {
          // Fast color-only update instead of full recompute
          this.updateStreamlineColors();
        });

        // Auto-apply when colormap changes (fast color-only update)
        colormapSelect.addEventListener('change', () => {
          this.updateStreamlineColors();
        });
      }

      // Auto-apply on field changes
      const vectorFieldSelect = document.getElementById('streamlineVectorField');
      if (vectorFieldSelect) {
        vectorFieldSelect.addEventListener('change', scheduleStreamlineUpdate);
      }

      const colorFieldSelect = document.getElementById('streamlineColorField');
      if (colorFieldSelect) {
        colorFieldSelect.addEventListener('change', scheduleStreamlineUpdate);
      }

      // Helper function to update sphere preview
      const updateSpherePreview = () => {
        const seedTypeSelect = document.getElementById('streamlineSeedType');
        if (seedTypeSelect?.value !== 'point') {
          // Remove preview if not in point mode
          if (this.engine) {
            this.engine.removeObject('streamline-sphere-preview');
          }
          return;
        }

        const centerX = parseFloat(document.getElementById('streamlinePointCenterX')?.value || 0);
        const centerY = parseFloat(document.getElementById('streamlinePointCenterY')?.value || 0);
        const centerZ = parseFloat(document.getElementById('streamlinePointCenterZ')?.value || 0);
        const radius = parseFloat(document.getElementById('streamlinePointRadius')?.value || 0.4);

        if (!this.engine) return;

        // Create preview group
        const previewGroup = new THREE.Group();

        // Create wireframe sphere with nice visualization
        const sphereGeometry = new THREE.SphereGeometry(radius, 24, 24); // More segments for smoother look
        const wireframeMaterial = new THREE.MeshBasicMaterial({
          color: 0xffff00,
          wireframe: true,
          opacity: 0.7,
          transparent: true,
          depthTest: true  // Respect depth for better 3D perception
        });
        const wireframeSphere = new THREE.Mesh(sphereGeometry, wireframeMaterial);
        wireframeSphere.position.set(centerX, centerY, centerZ);
        previewGroup.add(wireframeSphere);

        // Add blinking sphere at center - use fixed small size for elegance
        const indicatorSize = Math.min(radius * 0.05, 0.02); // Small, max 0.02 units
        const centerSphereGeometry = new THREE.SphereGeometry(indicatorSize, 12, 12);
        const centerSphereMaterial = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          opacity: 1.0,
          transparent: true,
          depthTest: false  // Always visible through other objects
        });
        const centerSphere = new THREE.Mesh(centerSphereGeometry, centerSphereMaterial);
        centerSphere.position.set(centerX, centerY, centerZ);
        centerSphere.renderOrder = 999; // Render on top
        previewGroup.add(centerSphere);

        // Store reference for blinking
        previewGroup.userData.centerIndicator = centerSphere;

        // Remove old preview and add new one
        this.engine.removeObject('streamline-sphere-preview');
        this.engine.addObject('streamline-sphere-preview', previewGroup, {
          type: 'preview',
          name: 'Streamline Preview',
          removable: false
        });

        // Start blinking animation
        if (this.sphereBlinkInterval) {
          clearInterval(this.sphereBlinkInterval);
        }
        let visible = true;
        this.sphereBlinkInterval = setInterval(() => {
          if (centerSphere) {
            visible = !visible;
            centerSphere.material.opacity = visible ? 1.0 : 0.2;
          }
        }, 300);
      };

      // Auto-apply on parameter changes for point seeds
      ['streamlinePointCenterX', 'streamlinePointCenterY', 'streamlinePointCenterZ',
        'streamlinePointRadius', 'streamlinePointCount'].forEach(id => {
          const input = document.getElementById(id);
          if (input) {
            console.log('[Setup] Attaching change listener to:', id);

            // Update preview in real-time as user types (for center/radius only)
            if (id !== 'streamlinePointCount') {
              input.addEventListener('input', () => {
                updateSpherePreview();
              });
            }

            input.addEventListener('change', () => {
              console.log('[Point Parameter Change]', id, 'changed to:', input.value);
              updateSpherePreview();
              scheduleStreamlineUpdate();
            });

            // Also listen to 'blur' in case change doesn't fire
            input.addEventListener('blur', () => {
              console.log('[Point Parameter Blur]', id, 'value:', input.value);
              updateSpherePreview();
              scheduleStreamlineUpdate();
            });
          } else {
            console.warn('[Setup] Input not found:', id);
          }
        });

      // Line preview visualization
      const updateLinePreview = () => {
        const seedTypeSelect = document.getElementById('streamlineSeedType');
        if (seedTypeSelect?.value !== 'line') {
          if (this.engine) {
            this.engine.removeObject('streamline-line-preview');
          }
          if (this.lineBlinkInterval) {
            clearInterval(this.lineBlinkInterval);
            this.lineBlinkInterval = null;
          }
          return;
        }

        const startX = parseFloat(document.getElementById('streamlineLineStartX')?.value || 0);
        const startY = parseFloat(document.getElementById('streamlineLineStartY')?.value || 0);
        const startZ = parseFloat(document.getElementById('streamlineLineStartZ')?.value || 0);
        const endX = parseFloat(document.getElementById('streamlineLineEndX')?.value || 1);
        const endY = parseFloat(document.getElementById('streamlineLineEndY')?.value || 1);
        const endZ = parseFloat(document.getElementById('streamlineLineEndZ')?.value || 1);

        const previewGroup = new THREE.Group();

        const startPoint = new THREE.Vector3(startX, startY, startZ);
        const endPoint = new THREE.Vector3(endX, endY, endZ);
        const lineLength = startPoint.distanceTo(endPoint);

        // Create yellow tube line connecting the points
        const direction = new THREE.Vector3().subVectors(endPoint, startPoint);
        const tubeRadius = Math.max(lineLength * 0.01, 0.005); // Proportional to line length
        const tubeGeometry = new THREE.CylinderGeometry(tubeRadius, tubeRadius, lineLength, 8);
        const tubeMaterial = new THREE.MeshBasicMaterial({
          color: 0xffff00,
          opacity: 0.7,
          transparent: true
        });
        const tubeLine = new THREE.Mesh(tubeGeometry, tubeMaterial);

        // Position and orient the tube
        const midPoint = new THREE.Vector3().addVectors(startPoint, endPoint).multiplyScalar(0.5);
        tubeLine.position.copy(midPoint);
        tubeLine.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          direction.clone().normalize()
        );
        previewGroup.add(tubeLine);

        // Add blinking red sphere at start point
        const indicatorSize = Math.max(lineLength * 0.03, 0.015);
        const startSphereGeometry = new THREE.SphereGeometry(indicatorSize, 12, 12);
        const startSphereMaterial = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          opacity: 1.0,
          transparent: true,
          depthTest: false
        });
        const startSphere = new THREE.Mesh(startSphereGeometry, startSphereMaterial);
        startSphere.position.copy(startPoint);
        startSphere.renderOrder = 999;
        previewGroup.add(startSphere);

        // Add blinking red sphere at end point
        const endSphereGeometry = new THREE.SphereGeometry(indicatorSize, 12, 12);
        const endSphereMaterial = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          opacity: 1.0,
          transparent: true,
          depthTest: false
        });
        const endSphere = new THREE.Mesh(endSphereGeometry, endSphereMaterial);
        endSphere.position.copy(endPoint);
        endSphere.renderOrder = 999;
        previewGroup.add(endSphere);

        // Store references for blinking
        previewGroup.userData.startIndicator = startSphere;
        previewGroup.userData.endIndicator = endSphere;

        // Remove old preview and add new one
        this.engine.removeObject('streamline-line-preview');
        this.engine.addObject('streamline-line-preview', previewGroup, {
          type: 'preview',
          name: 'Streamline Line Preview',
          removable: false
        });

        // Start blinking animation for both spheres
        if (this.lineBlinkInterval) {
          clearInterval(this.lineBlinkInterval);
        }
        let visible = true;
        this.lineBlinkInterval = setInterval(() => {
          if (startSphere && endSphere) {
            visible = !visible;
            startSphere.material.opacity = visible ? 1.0 : 0.2;
            endSphere.material.opacity = visible ? 1.0 : 0.2;
          }
        }, 300);
      };

      // Auto-apply on parameter changes for line seeds
      ['streamlineLineStartX', 'streamlineLineStartY', 'streamlineLineStartZ',
        'streamlineLineEndX', 'streamlineLineEndY', 'streamlineLineEndZ',
        'streamlineLineResolution'].forEach(id => {
          const input = document.getElementById(id);
          if (input) {
            // Update preview in real-time as user types (for coordinates only)
            if (id !== 'streamlineLineResolution') {
              input.addEventListener('input', () => {
                updateLinePreview();
              });
            }

            input.addEventListener('change', () => {
              updateLinePreview();
              scheduleStreamlineUpdate();
            });
          }
        });

      // Plane preview visualization - DISABLED (user request)
      // Yellow preview plane is no longer shown - use interactive widget instead
      const updatePlanePreview = () => {
        // Remove any existing yellow preview plane
        if (this.engine) {
          this.engine.removeObject('streamline-plane-preview');
        }
        if (this.planeBlinkInterval) {
          clearInterval(this.planeBlinkInterval);
          this.planeBlinkInterval = null;
        }
        // Yellow preview disabled - interactive widget is used instead
      };

      // Auto-apply on parameter changes for plane seeds
      ['streamlinePlaneOriginX', 'streamlinePlaneOriginY', 'streamlinePlaneOriginZ',
        'streamlinePlaneNormalX', 'streamlinePlaneNormalY', 'streamlinePlaneNormalZ',
        'streamlinePlaneWidth', 'streamlinePlaneHeight',
        'streamlinePlaneResolutionU', 'streamlinePlaneResolutionV'].forEach(id => {
          const input = document.getElementById(id);
          if (input) {
            // Update preview in real-time as user types (for geometric parameters only)
            if (id !== 'streamlinePlaneResolutionU' && id !== 'streamlinePlaneResolutionV') {
              input.addEventListener('input', () => {
                const showPlaneWidget = document.getElementById('streamlinePlaneShowWidget');

                // If interactive widget is active, don't show yellow preview
                if (showPlaneWidget?.checked) {
                  console.log(`[Streamline Input] Widget active - skipping yellow preview for ${id}`);
                  // If W/H input, update widget size directly
                  if ((id === 'streamlinePlaneWidth' || id === 'streamlinePlaneHeight') &&
                    window.viewer?.filters?.streamlinePlaneWidget?.enabled) {
                    console.log(`[Streamline Input] Updating interactive widget size: ${id} = ${input.value}`);
                    window.viewer.filters.streamlinePlaneWidget.updatePlaneSizeFromInputs();
                  }
                  return; // Don't call updatePlanePreview
                }

                updatePlanePreview();
              });
            }

            input.addEventListener('change', () => {
              const showPlaneWidget = document.getElementById('streamlinePlaneShowWidget');

              // If interactive widget is active, don't show yellow preview
              if (showPlaneWidget?.checked) {
                console.log(`[Streamline Input] Widget active - skipping yellow preview for ${id}`);
                // If W/H input, update widget size directly
                if ((id === 'streamlinePlaneWidth' || id === 'streamlinePlaneHeight') &&
                  window.viewer?.filters?.streamlinePlaneWidget?.enabled) {
                  console.log(`[Streamline Input] Updating interactive widget size: ${input.value}`);
                  window.viewer.filters.streamlinePlaneWidget.updatePlaneSizeFromInputs();
                }
                scheduleStreamlineUpdate();
                return; // Don't call updatePlanePreview
              }

              updatePlanePreview();
              scheduleStreamlineUpdate();
            });
          }
        });

      // Listen to widget checkbox - ensure yellow preview is always removed
      const showPlaneWidgetCheckbox = document.getElementById('streamlinePlaneShowWidget');
      if (showPlaneWidgetCheckbox) {
        showPlaneWidgetCheckbox.addEventListener('change', () => {
          console.log('[Streamline Widget] Checkbox toggled:', showPlaneWidgetCheckbox.checked);
          // Always ensure yellow preview is removed (yellow preview is disabled)
          if (this.engine) {
            this.engine.removeObject('streamline-plane-preview');
          }
          if (this.planeBlinkInterval) {
            clearInterval(this.planeBlinkInterval);
            this.planeBlinkInterval = null;
          }
        });
      }

      // Auto-apply on max length and step size changes
      const maxLengthInput = document.getElementById('streamlineMaxLength');
      if (maxLengthInput) {
        maxLengthInput.addEventListener('change', scheduleStreamlineUpdate);
      }

      const stepSizeInput = document.getElementById('streamlineStepSize');
      if (stepSizeInput) {
        stepSizeInput.addEventListener('change', scheduleStreamlineUpdate);
      }

      // Render mode controls
      const renderModeSelect = document.getElementById('streamlineRenderMode');
      const tubeControls = document.getElementById('streamlineTubeControls');
      const ribbonControls = document.getElementById('streamlineRibbonControls');

      if (renderModeSelect) {
        renderModeSelect.addEventListener('change', (e) => {
          const mode = e.target.value;
          if (tubeControls) tubeControls.style.display = mode === 'tubes' ? 'block' : 'none';
          if (ribbonControls) ribbonControls.style.display = mode === 'ribbons' ? 'block' : 'none';
          scheduleStreamlineUpdate(); // Auto-apply when render mode changes
        });
      }

      // Auto-apply on tube parameter changes
      ['streamlineTubeRadius', 'streamlineTubeSides', 'streamlineTubeVaryBy'].forEach(id => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('change', scheduleStreamlineUpdate);
        }
      });

      // Auto-apply on ribbon parameter changes
      const ribbonWidthInput = document.getElementById('streamlineRibbonWidth');
      if (ribbonWidthInput) {
        ribbonWidthInput.addEventListener('change', scheduleStreamlineUpdate);
      }

      // Auto-apply on preview parameter changes (visualization-only updates)
      const previewCheckbox = document.getElementById('streamlinePreviewEnabled');
      if (previewCheckbox) {
        previewCheckbox.addEventListener('change', scheduleStreamlineUpdate);
      }

      const previewDotScaleInput = document.getElementById('streamlinePreviewDotScale');
      if (previewDotScaleInput) {
        previewDotScaleInput.addEventListener('change', scheduleStreamlineUpdate);
      }

      const previewSpeedInput = document.getElementById('streamlinePreviewSpeed');
      if (previewSpeedInput) {
        previewSpeedInput.addEventListener('change', scheduleStreamlineUpdate);
      }

      const pickCenterBtn = document.getElementById('btnPickStreamlineCenter');
      if (pickCenterBtn) {
        pickCenterBtn.addEventListener('click', () => {
          if (!window.viewer || typeof window.viewer.pickStreamlineSeedCenter !== 'function') {
            return;
          }
          window.viewer.pickStreamlineSeedCenter((point) => {
            console.log('[Pick Point] User picked point:', point);
            const [x, y, z] = point;
            const centerX = document.getElementById('streamlinePointCenterX');
            const centerY = document.getElementById('streamlinePointCenterY');
            const centerZ = document.getElementById('streamlinePointCenterZ');
            if (centerX) centerX.value = x.toFixed(4);
            if (centerY) centerY.value = y.toFixed(4);
            if (centerZ) centerZ.value = z.toFixed(4);

            const seedTypeSelect = document.getElementById('streamlineSeedType');
            console.log('[Pick Point] Current seed type:', seedTypeSelect?.value);
            if (seedTypeSelect && seedTypeSelect.value !== 'point') {
              console.log('[Pick Point] Changing seed type to point');
              seedTypeSelect.value = 'point';
              seedTypeSelect.dispatchEvent(new Event('change'));
            }
            console.log('[Pick Point] Seed type after setting:', seedTypeSelect?.value);

            const defaults = window.viewer?.filters?.getStreamlineDefaultParams(window.viewer.currentData);
            if (defaults) {
              const radiusInput = document.getElementById('streamlinePointRadius');
              if (radiusInput) radiusInput.value = defaults.pointRadius.toFixed(3);
              const stepInput = document.getElementById('streamlineStepSize');
              const lengthInput = document.getElementById('streamlineMaxLength');
              if (stepInput) stepInput.value = defaults.stepSize.toFixed(4);
              if (lengthInput) lengthInput.value = defaults.maxLength;
            }

            // Show sphere preview after picking point
            updateSpherePreview();

            // Auto-apply after picking point
            scheduleStreamlineUpdate();
          });
        });
      }

      // Line start point picker
      const pickLineStartBtn = document.getElementById('btnPickStreamlineLineStart');
      if (pickLineStartBtn) {
        pickLineStartBtn.addEventListener('click', () => {
          if (!window.viewer || typeof window.viewer.pickStreamlineSeedCenter !== 'function') {
            return;
          }
          window.viewer.pickStreamlineSeedCenter((point) => {
            console.log('[Pick Line Start] User picked point:', point);
            const [x, y, z] = point;
            const startX = document.getElementById('streamlineLineStartX');
            const startY = document.getElementById('streamlineLineStartY');
            const startZ = document.getElementById('streamlineLineStartZ');
            if (startX) startX.value = x.toFixed(4);
            if (startY) startY.value = y.toFixed(4);
            if (startZ) startZ.value = z.toFixed(4);

            const seedTypeSelect = document.getElementById('streamlineSeedType');
            if (seedTypeSelect && seedTypeSelect.value !== 'line') {
              seedTypeSelect.value = 'line';
              seedTypeSelect.dispatchEvent(new Event('change'));
            }

            // Show line preview after picking point
            updateLinePreview();

            // Auto-apply after picking point
            scheduleStreamlineUpdate();
          });
        });
      }

      // Line end point picker
      const pickLineEndBtn = document.getElementById('btnPickStreamlineLineEnd');
      if (pickLineEndBtn) {
        pickLineEndBtn.addEventListener('click', () => {
          if (!window.viewer || typeof window.viewer.pickStreamlineSeedCenter !== 'function') {
            return;
          }
          window.viewer.pickStreamlineSeedCenter((point) => {
            console.log('[Pick Line End] User picked point:', point);
            const [x, y, z] = point;
            const endX = document.getElementById('streamlineLineEndX');
            const endY = document.getElementById('streamlineLineEndY');
            const endZ = document.getElementById('streamlineLineEndZ');
            if (endX) endX.value = x.toFixed(4);
            if (endY) endY.value = y.toFixed(4);
            if (endZ) endZ.value = z.toFixed(4);

            const seedTypeSelect = document.getElementById('streamlineSeedType');
            if (seedTypeSelect && seedTypeSelect.value !== 'line') {
              seedTypeSelect.value = 'line';
              seedTypeSelect.dispatchEvent(new Event('change'));
            }

            // Show line preview after picking point
            updateLinePreview();

            // Auto-apply after picking point
            scheduleStreamlineUpdate();
          });
        });
      }

      // Plane origin picker
      const pickPlaneOriginBtn = document.getElementById('btnPickStreamlinePlaneOrigin');
      if (pickPlaneOriginBtn) {
        pickPlaneOriginBtn.addEventListener('click', () => {
          if (!window.viewer || typeof window.viewer.pickStreamlineSeedCenter !== 'function') {
            return;
          }
          window.viewer.pickStreamlineSeedCenter((point) => {
            console.log('[Pick Plane Origin] User picked point:', point);
            const [x, y, z] = point;
            const originX = document.getElementById('streamlinePlaneOriginX');
            const originY = document.getElementById('streamlinePlaneOriginY');
            const originZ = document.getElementById('streamlinePlaneOriginZ');
            if (originX) originX.value = x.toFixed(4);
            if (originY) originY.value = y.toFixed(4);
            if (originZ) originZ.value = z.toFixed(4);

            const seedTypeSelect = document.getElementById('streamlineSeedType');
            if (seedTypeSelect && seedTypeSelect.value !== 'plane') {
              seedTypeSelect.value = 'plane';
              seedTypeSelect.dispatchEvent(new Event('change'));
            }

            // Show plane preview after picking origin
            updatePlanePreview();

            // Auto-apply after picking point
            scheduleStreamlineUpdate();
          });
        });
      }

      // Plane normal preset buttons (direction doesn't matter for streamlines)
      const setPlaneNormal = (x, y, z, name) => {
        const seedTypeSelect = document.getElementById('streamlineSeedType');
        if (seedTypeSelect && seedTypeSelect.value !== 'plane') {
          seedTypeSelect.value = 'plane';
          seedTypeSelect.dispatchEvent(new Event('change'));
        }

        const normalX = document.getElementById('streamlinePlaneNormalX');
        const normalY = document.getElementById('streamlinePlaneNormalY');
        const normalZ = document.getElementById('streamlinePlaneNormalZ');
        if (normalX) normalX.value = x.toFixed(1);
        if (normalY) normalY.value = y.toFixed(1);
        if (normalZ) normalZ.value = z.toFixed(1);

        // If streamline plane widget is enabled, update it directly
        if (window.viewer && window.viewer.filters.streamlinePlaneWidget && window.viewer.filters.streamlinePlaneWidget.enabled) {
          const widget = window.viewer.filters.streamlinePlaneWidget;
          widget.setPlaneOrientation(x, y, z);
        }

        updatePlanePreview();
        scheduleStreamlineUpdate();
        Utils.notify(`${name} plane selected`, 'success');
      };

      const presets = [
        { id: 'btnPlaneNormalX', x: 1, y: 0, z: 0, name: 'X' },
        { id: 'btnPlaneNormalY', x: 0, y: 1, z: 0, name: 'Y' },
        { id: 'btnPlaneNormalZ', x: 0, y: 0, z: 1, name: 'Z' }
      ];

      presets.forEach(preset => {
        const btn = document.getElementById(preset.id);
        if (btn) {
          btn.addEventListener('click', () => {
            setPlaneNormal(preset.x, preset.y, preset.z, preset.name);
          });
        }
      });

      // Direction toggle for particle preview
      this.particleDirection = 1; // 1 = forward, -1 = backward
      const directionToggleBtn = document.getElementById('btnToggleParticleDirection');
      if (directionToggleBtn) {
        directionToggleBtn.addEventListener('click', () => {
          this.particleDirection = -this.particleDirection;
          directionToggleBtn.textContent = this.particleDirection > 0 ? '→' : '←';
          directionToggleBtn.title = this.particleDirection > 0 ? 'Particles moving forward' : 'Particles moving backward';

          // Update preview speed direction for all existing streamlines
          if (window.viewer && window.viewer.streamlinePreviews) {
            window.viewer.streamlinePreviews.forEach((preview, filterId) => {
              if (preview.animations) {
                preview.animations.forEach(anim => {
                  const absSpeed = Math.abs(anim.speed);
                  anim.speed = absSpeed * this.particleDirection;
                });
              }
            });
          }

          Utils.notify(`Particle direction: ${this.particleDirection > 0 ? 'Forward' : 'Backward'}`, 'success');
        });

        // Set initial arrow
        directionToggleBtn.textContent = '→';
        directionToggleBtn.title = 'Particles moving forward';
      }
    }

    // Glyph Filter
    const glyphEnabled = document.getElementById('glyphFilterEnabled');
    const glyphControls = document.getElementById('glyphControls');
    if (glyphEnabled && glyphControls) {
      glyphEnabled.addEventListener('change', (e) => {
        glyphControls.style.display = e.target.checked ? 'flex' : 'none';

        // Populate field dropdowns when showing controls (same pattern as streamlines)
        if (e.target.checked) {
          this.populateGlyphFields();
        }

        // Toggle visibility instead of removing the filter
        const filterEntry = this.activeFilters.get('glyph-filter');
        if (filterEntry && filterEntry.mesh) {
          filterEntry.mesh.visible = e.target.checked;
        }

        // Toggle glyph legend visibility along with the filter
        const glyphLegend = document.getElementById('glyphLegend');
        if (glyphLegend && filterEntry) {
          glyphLegend.style.display = e.target.checked ? 'flex' : 'none';
        }

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      document.getElementById('btnApplyGlyph')?.addEventListener('click', () => {
        this.applyGlyphFromUI();
      });

      // Update glyph colors when colormap changes (without re-fetching data)
      document.getElementById('glyphColormap')?.addEventListener('change', () => {
        this.updateGlyphColors();
      });
    }

    // Surface LIC Filter
    const surfaceLICEnabled = document.getElementById('surfaceLICFilterEnabled');
    const surfaceLICControls = document.getElementById('surfaceLICControls');
    if (surfaceLICEnabled && surfaceLICControls) {
      surfaceLICEnabled.addEventListener('change', (e) => {
        surfaceLICControls.style.display = e.target.checked ? 'flex' : 'none';

        // Populate vector field dropdown when showing controls
        if (e.target.checked) {
          this.populateSurfaceLICFields();
        } else {
          // Remove the filter when unchecked (for collaboration sync)
          this.removeFilter('surface-lic-filter');
        }

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      // Slider value displays
      const kernelLengthSlider = document.getElementById('surfaceLICKernelLength');
      const kernelLengthValue = document.getElementById('surfaceLICKernelLengthValue');
      if (kernelLengthSlider && kernelLengthValue) {
        kernelLengthSlider.addEventListener('input', () => {
          kernelLengthValue.textContent = kernelLengthSlider.value;
        });
      }

      const contrastSlider = document.getElementById('surfaceLICContrast');
      const contrastValue = document.getElementById('surfaceLICContrastValue');
      if (contrastSlider && contrastValue) {
        contrastSlider.addEventListener('input', () => {
          contrastValue.textContent = contrastSlider.value;
          // Update contrast in real-time
          if (this.surfaceLICRenderer) {
            this.surfaceLICRenderer.updateParameters({
              contrast: parseFloat(contrastSlider.value)
            });
          }
        });
      }

      const opacitySlider = document.getElementById('surfaceLICOpacity');
      const opacityValue = document.getElementById('surfaceLICOpacityValue');
      if (opacitySlider && opacityValue) {
        opacitySlider.addEventListener('input', () => {
          opacityValue.textContent = opacitySlider.value;
          // Update opacity in real-time
          if (this.surfaceLICRenderer) {
            this.surfaceLICRenderer.updateParameters({
              opacity: parseFloat(opacitySlider.value)
            });
          }
        });
      }

      // Colormap change handler
      const colormapSelect = document.getElementById('surfaceLICColormap');
      if (colormapSelect) {
        colormapSelect.addEventListener('change', () => {
          if (this.surfaceLICRenderer) {
            this.surfaceLICRenderer.updateParameters({
              colormap: colormapSelect.value
            });
          }
        });
      }

      // Color mode change handler
      const colorModeSelect = document.getElementById('surfaceLICColorMode');
      if (colorModeSelect) {
        colorModeSelect.addEventListener('change', () => {
          if (this.surfaceLICRenderer) {
            this.surfaceLICRenderer.updateParameters({
              colorMode: colorModeSelect.value
            });
          }
        });
      }

      const animationSpeedSlider = document.getElementById('surfaceLICAnimationSpeed');
      const animationSpeedValue = document.getElementById('surfaceLICAnimationSpeedValue');
      if (animationSpeedSlider && animationSpeedValue) {
        animationSpeedSlider.addEventListener('input', () => {
          animationSpeedValue.textContent = animationSpeedSlider.value;
          if (this.surfaceLICRenderer) {
            this.surfaceLICRenderer.setAnimationSpeed(parseFloat(animationSpeedSlider.value));
          }
        });
      }

      // Animation toggle
      const animateCheckbox = document.getElementById('surfaceLICAnimate');
      const animationControls = document.getElementById('surfaceLICAnimationControls');
      if (animateCheckbox && animationControls) {
        animateCheckbox.addEventListener('change', (e) => {
          animationControls.style.display = e.target.checked ? 'block' : 'none';
          if (this.surfaceLICRenderer) {
            if (e.target.checked) {
              const speed = parseFloat(document.getElementById('surfaceLICAnimationSpeed')?.value || 1.0);
              this.surfaceLICRenderer.startAnimation(speed);
            } else {
              this.surfaceLICRenderer.stopAnimation();
            }
          }
        });
      }

      // Apply button
      document.getElementById('btnApplySurfaceLIC')?.addEventListener('click', () => {
        this.applySurfaceLICFromUI();
      });
    }

    // Volume LIC Filter
    const volumeLICEnabled = document.getElementById('volumeLICFilterEnabled');
    const volumeLICControls = document.getElementById('volumeLICControls');
    if (volumeLICEnabled && volumeLICControls) {
      volumeLICEnabled.addEventListener('change', (e) => {
        volumeLICControls.style.display = e.target.checked ? 'flex' : 'none';

        // Populate vector field dropdown when showing controls
        if (e.target.checked) {
          this.populateVolumeLICFields();
        } else {
          // Remove the filter when unchecked (for collaboration sync)
          this.removeFilter('volume-lic-filter');
        }

        // Notify view lock manager of visibility change
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      });

      // Slider value displays
      const setupSlider = (sliderId, valueId) => {
        const slider = document.getElementById(sliderId);
        const valueSpan = document.getElementById(valueId);
        if (slider && valueSpan) {
          slider.addEventListener('input', () => {
            valueSpan.textContent = slider.value;
          });
        }
      };

      setupSlider('volumeLICKernelLength', 'volumeLICKernelLengthValue');
      setupSlider('volumeLICStepSize', 'volumeLICStepSizeValue');
      setupSlider('volumeLICContrast', 'volumeLICContrastValue');
      setupSlider('volumeLICOpacity', 'volumeLICOpacityValue');
      setupSlider('volumeLICThreshold', 'volumeLICThresholdValue');
      setupSlider('volumeLICSteps', 'volumeLICStepsValue');
      setupSlider('volumeLICBrightness', 'volumeLICBrightnessValue');
      setupSlider('volumeLICWeight', 'volumeLICWeightValue');

      // Real-time parameter updates for rendering parameters
      const opacitySlider = document.getElementById('volumeLICOpacity');
      if (opacitySlider) {
        opacitySlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              opacity: parseFloat(opacitySlider.value)
            });
          }
        });
      }

      const thresholdSlider = document.getElementById('volumeLICThreshold');
      if (thresholdSlider) {
        thresholdSlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              threshold: parseFloat(thresholdSlider.value)
            });
          }
        });
      }

      const stepsSlider = document.getElementById('volumeLICSteps');
      if (stepsSlider) {
        stepsSlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              steps: parseInt(stepsSlider.value)
            });
          }
        });
      }

      const brightnessSlider = document.getElementById('volumeLICBrightness');
      if (brightnessSlider) {
        brightnessSlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              brightness: parseFloat(brightnessSlider.value)
            });
          }
        });
      }

      const weightSlider = document.getElementById('volumeLICWeight');
      if (weightSlider) {
        weightSlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              licWeight: parseFloat(weightSlider.value)
            });
          }
        });
      }

      // Colormap change handler
      const colormapSelect = document.getElementById('volumeLICColormap');
      if (colormapSelect) {
        colormapSelect.addEventListener('change', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              colormap: colormapSelect.value
            });
          }
        });
      }

      // Animation speed slider
      setupSlider('volumeLICAnimationSpeed', 'volumeLICAnimationSpeedValue');
      const animSpeedSlider = document.getElementById('volumeLICAnimationSpeed');
      if (animSpeedSlider) {
        animSpeedSlider.addEventListener('input', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              animationSpeed: parseFloat(animSpeedSlider.value)
            });
          }
        });
      }

      // Animation toggle checkbox
      const animatedCheckbox = document.getElementById('volumeLICAnimated');
      if (animatedCheckbox) {
        animatedCheckbox.addEventListener('change', () => {
          if (this.volumeLICRenderer) {
            this.volumeLICRenderer.updateParameters({
              animated: animatedCheckbox.checked
            });
          }
        });
      }

      // Apply button
      document.getElementById('btnApplyVolumeLIC')?.addEventListener('click', () => {
        this.applyVolumeLICFromUI();
      });
    }

    // Field Calculator
    const fieldCalculatorEnabled = document.getElementById('fieldCalculatorEnabled');
    const fieldCalculatorControls = document.getElementById('fieldCalculatorControls');
    if (fieldCalculatorEnabled && fieldCalculatorControls) {
      fieldCalculatorEnabled.addEventListener('change', (e) => {
        fieldCalculatorControls.style.display = e.target.checked ? 'flex' : 'none';
        if (e.target.checked) {
          this.populateCalculatorFields();
        }
      });

      document.getElementById('btnApplyCalculator')?.addEventListener('click', () => {
        this.applyFieldCalculator();
      });
    }

    // Advanced Streamlines Filter
    const advancedStreamlinesEnabled = document.getElementById('advancedStreamlinesEnabled');
    const advancedStreamlinesControls = document.getElementById('advancedStreamlinesControls');
    if (advancedStreamlinesEnabled && advancedStreamlinesControls) {
      advancedStreamlinesEnabled.addEventListener('change', (e) => {
        advancedStreamlinesControls.style.display = e.target.checked ? 'flex' : 'none';
        if (!e.target.checked) {
          this.clearAdvancedStreamlines('all');
        }
      });

      // Type selector - show/hide type-specific controls
      const typeSelect = document.getElementById('advancedStreamlineType');
      if (typeSelect) {
        typeSelect.addEventListener('change', () => {
          const type = typeSelect.value;
          document.getElementById('tubeControls').style.display = type === 'tubes' ? 'block' : 'none';
          document.getElementById('particleControls').style.display = type === 'particles' ? 'block' : 'none';
          document.getElementById('ribbonControls').style.display = type === 'ribbons' ? 'block' : 'none';

          // Show/hide animate button
          const btnAnimate = document.getElementById('btnAnimateParticles');
          if (btnAnimate && type === 'particles' && window.particleAnimator && window.particleAnimator.isRunning()) {
            btnAnimate.style.display = 'inline-block';
          } else if (btnAnimate) {
            btnAnimate.style.display = 'none';
          }
        });
      }

      // Seed type selector
      const seedTypeSelect = document.getElementById('advancedStreamlineSeedType');
      const seedGroups = advancedStreamlinesControls.querySelectorAll('[data-advanced-seed-group]');
      const updateSeedGroups = () => {
        const active = seedTypeSelect?.value || 'line';
        seedGroups.forEach(group => {
          if (!group.dataset.advancedSeedGroup) return;
          const isActive = group.dataset.advancedSeedGroup === active;
          group.classList.toggle('hidden', !isActive);
        });
      };

      if (seedTypeSelect) {
        seedTypeSelect.addEventListener('change', updateSeedGroups);
        updateSeedGroups();
      }

      // Slider value updates
      const tubeRadiusSlider = document.getElementById('tubeRadius');
      if (tubeRadiusSlider) {
        tubeRadiusSlider.addEventListener('input', (e) => {
          document.getElementById('tubeRadiusValue').textContent = parseFloat(e.target.value).toFixed(3);
        });
      }

      const particleSizeSlider = document.getElementById('particleSize');
      if (particleSizeSlider) {
        particleSizeSlider.addEventListener('input', (e) => {
          const value = parseFloat(e.target.value);
          document.getElementById('particleSizeValue').textContent = value.toFixed(3);
          // Update particle size in real-time if animator exists
          if (window.particleAnimator) {
            window.particleAnimator.setParticleSize(value);
          }
        });
      }

      const particleSpeedSlider = document.getElementById('particleSpeed');
      if (particleSpeedSlider) {
        particleSpeedSlider.addEventListener('input', (e) => {
          const value = parseFloat(e.target.value);
          document.getElementById('particleSpeedValue').textContent = value.toFixed(1);
          // Update animation speed in real-time if animator exists
          if (window.particleAnimator) {
            window.particleAnimator.setSpeed(value);
          }
        });
      }

      const ribbonWidthSlider = document.getElementById('ribbonWidth');
      if (ribbonWidthSlider) {
        ribbonWidthSlider.addEventListener('input', (e) => {
          document.getElementById('ribbonWidthValue').textContent = parseFloat(e.target.value).toFixed(3);
        });
      }

      // Button handlers
      document.getElementById('btnGenerateAdvancedStreamlines')?.addEventListener('click', () => {
        this.applyAdvancedStreamlinesFromUI();
      });

      document.getElementById('btnClearAdvancedStreamlines')?.addEventListener('click', () => {
        this.clearAdvancedStreamlines('all');
      });

      document.getElementById('btnAnimateParticles')?.addEventListener('click', () => {
        this.toggleParticleAnimation();
      });
    }

    // Calculator Filter
    const calculatorEnabled = document.getElementById('calculatorFilterEnabled');
    const calculatorControls = document.getElementById('calculatorControls');
    if (calculatorEnabled && calculatorControls) {
      calculatorEnabled.addEventListener('change', (e) => {
        calculatorControls.style.display = e.target.checked ? 'flex' : 'none';
      });

      document.getElementById('btnApplyCalculator')?.addEventListener('click', () => {
        this.applyCalculatorFromUI();
      });
    }

    // Gradient Filter
    const gradientEnabled = document.getElementById('gradientFilterEnabled');
    const gradientControls = document.getElementById('gradientControls');
    if (gradientEnabled && gradientControls) {
      gradientEnabled.addEventListener('change', (e) => {
        gradientControls.style.display = e.target.checked ? 'flex' : 'none';
      });

      document.getElementById('btnApplyGradient')?.addEventListener('click', () => {
        this.applyGradientFromUI();
      });
    }

    // Extract Selection Filter
    const selectionEnabled = document.getElementById('selectionFilterEnabled');
    const selectionControls = document.getElementById('selectionControls');
    if (selectionEnabled && selectionControls) {
      selectionEnabled.addEventListener('change', (e) => {
        selectionControls.style.display = e.target.checked ? 'flex' : 'none';
        if (!e.target.checked) {
          this.removeFilter('selection-filter');
          this.clearSelectionUI();
        }
      });

      // Selection type change
      const selectionType = document.getElementById('selectionType');
      if (selectionType) {
        selectionType.addEventListener('change', () => {
          this.updateSelectionTypeUI();
        });
      }

      // Rubber band controls
      document.getElementById('btnStartRubberBand')?.addEventListener('click', () => {
        this.startRubberBandSelection();
      });
      document.getElementById('btnClearSelection')?.addEventListener('click', () => {
        this.clearSelectionUI();
      });

      // Polygon controls
      document.getElementById('btnStartPolygon')?.addEventListener('click', () => {
        this.startPolygonSelection();
      });
      document.getElementById('btnClearPolygon')?.addEventListener('click', () => {
        this.clearSelectionUI();
      });

      // Apply selection
      document.getElementById('btnApplySelection')?.addEventListener('click', () => {
        this.applySelectionFromUI();
      });
    }

    // Initialize selection state
    this.selectionState = {
      active: false,
      type: null,
      points: [],
      frustumRect: null
    };

    // Volume Rendering
    const volumeEnabled = document.getElementById('volumeRenderEnabled');
    const volumeControls = document.getElementById('volumeControls');
    if (volumeEnabled && volumeControls) {
      volumeEnabled.addEventListener('change', (e) => {
        volumeControls.style.display = e.target.checked ? 'flex' : 'none';
        if (!e.target.checked) {
          this.removeVolume();
        }
      });

      // Slider updates
      document.getElementById('volumeStepSize')?.addEventListener('input', (e) => {
        document.getElementById('volumeStepSizeValue').textContent = e.target.value;
        if (this.volumeRenderer) {
          this.volumeRenderer.updateStepSize(parseFloat(e.target.value));
        }
      });

      document.getElementById('volumeOpacityScale')?.addEventListener('input', (e) => {
        document.getElementById('volumeOpacityScaleValue').textContent = e.target.value;
        if (this.volumeRenderer) {
          this.volumeRenderer.updateOpacityScale(parseFloat(e.target.value));
        }
      });

      // Transfer function change
      document.getElementById('volumeTransferFunction')?.addEventListener('change', () => {
        if (this.volumeRenderer && this.currentVolumeData) {
          this.updateVolumeTransferFunction();
        }
      });

      // Buttons
      document.getElementById('btnApplyVolume')?.addEventListener('click', async () => {
        await this.applyWithProgressBar('btnApplyVolume', async () => {
          await this.applyVolumeFromUI();
        }, {
          startMessage: 'Applying volume rendering...',
          successMessage: 'Volume rendering applied successfully',
          errorPrefix: 'Volume rendering error'
        });
      });

      document.getElementById('btnEditTransferFunction')?.addEventListener('click', () => {
        this.openTransferFunctionEditor();
      });

      document.getElementById('btnRemoveVolume')?.addEventListener('click', () => {
        this.removeVolume();
      });
    }

    // Initialize volume renderer
    this.volumeRenderer = null;
    this.currentVolumeData = null;
  }

  // Update available fields in contour dropdown
  updateAvailableFields(fields) {
    console.log('[updateAvailableFields] Called with fields:', fields ? Object.keys(fields) : 'null/undefined');
    const contourField = document.getElementById('contourField');
    if (contourField) {
      contourField.innerHTML = '<option value="">Select field...</option>';
      if (fields) {
        Object.keys(fields).forEach(fieldName => {
          // Skip vector component fields (velocity_x, velocity_y, velocity_z)
          if (!fieldName.includes('_')) {
            const option = document.createElement('option');
            option.value = fieldName;
            option.textContent = fieldName;
            contourField.appendChild(option);
          }
        });
      }
    }

    const streamlineField = document.getElementById('streamlineVectorField');
    if (streamlineField) {
      console.log('[updateAvailableFields] Streamline vector field - viewer:', this.viewer);
      console.log('[updateAvailableFields] Streamline vector field - renderingMode:', this.viewer?.renderingMode);
      console.log('[updateAvailableFields] Streamline vector field - availableFields:', this.viewer?.availableFields);
      console.log('[updateAvailableFields] Streamline vector field - fields param:', fields);

      streamlineField.innerHTML = '';
      const vectorFields = [];

      // Backend mode - use availableFields list
      if (this.viewer?.renderingMode === 'geometry' && this.viewer?.availableFields) {
        const fieldNames = this.viewer.availableFields;
        console.log('[updateAvailableFields] Backend mode detected, using availableFields:', fieldNames);
        fieldNames.forEach(name => {
          // Vector fields are typically uppercase single letters or contain velocity/speed
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          }
        });
      } else if (fields) {
        // Frontend mode - check if field data is 3D array
        Object.entries(fields).forEach(([name, value]) => {
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });

        if (vectorFields.length === 0) {
          vectorFields.push(...Object.keys(fields));
        }
      }

      const uniqueFields = [...new Set(vectorFields)];
      uniqueFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        streamlineField.appendChild(option);
      });

      if (streamlineField.options.length === 0) {
        const option = document.createElement('option');
        option.value = 'velocity';
        option.textContent = 'velocity';
        streamlineField.appendChild(option);
      }

      const defaultVector = this.getDefaultVectorField({ fields });
      if (defaultVector && streamlineField.querySelector(`option[value="${defaultVector}"]`)) {
        streamlineField.value = defaultVector;
      } else if (streamlineField.options.length > 0) {
        streamlineField.selectedIndex = 0;
      }
    }

    // Update 3D Streamline color field dropdown
    const streamlineColorField = document.getElementById('streamlineColorField');
    if (streamlineColorField) {
      console.log('[updateAvailableFields] Streamline color field - viewer:', this.viewer);
      console.log('[updateAvailableFields] Streamline color field - renderingMode:', this.viewer?.renderingMode);
      console.log('[updateAvailableFields] Streamline color field - availableFields:', this.viewer?.availableFields);

      const previousValue = streamlineColorField.value || '';
      streamlineColorField.innerHTML = '';

      // Add Auto option
      const autoOption = document.createElement('option');
      autoOption.value = '';
      autoOption.textContent = 'Auto';
      streamlineColorField.appendChild(autoOption);

      // Separate scalar and vector fields
      const scalarFields = [];
      const vectorFields = [];

      // Backend mode - use availableFields list
      if (this.viewer?.renderingMode === 'geometry' && this.viewer?.availableFields) {
        const fieldNames = this.viewer.availableFields;
        console.log('[updateAvailableFields] Color field backend mode, fieldNames:', fieldNames);
        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      } else if (fields) {
        // Frontend mode - check if field data is 3D array
        Object.entries(fields).forEach(([name, value]) => {
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      }

      // Add scalar fields first (more common for coloring)
      if (scalarFields.length > 0) {
        const scalarGroup = document.createElement('optgroup');
        scalarGroup.label = 'Scalar Fields';
        scalarFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          scalarGroup.appendChild(option);
        });
        streamlineColorField.appendChild(scalarGroup);
      }

      // Add vector fields
      if (vectorFields.length > 0) {
        const vectorGroup = document.createElement('optgroup');
        vectorGroup.label = 'Vector Fields';
        vectorFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          vectorGroup.appendChild(option);
        });
        streamlineColorField.appendChild(vectorGroup);
      }

      // Set preferred value
      let preferred = '';
      if (previousValue && streamlineColorField.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (streamlineColorField.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (streamlineColorField.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (streamlineColorField.querySelector('option[value="U"]')) {
        preferred = 'U';
      }
      streamlineColorField.value = preferred;
    }

    // Update Advanced Streamlines vector field dropdown
    const advancedStreamlineField = document.getElementById('advancedStreamlineVectorField');
    if (advancedStreamlineField) {
      advancedStreamlineField.innerHTML = '<option value="">Select field...</option>';
      const vectorFields = [];
      if (fields) {
        Object.entries(fields).forEach(([name, value]) => {
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      if (vectorFields.length === 0 && fields) {
        vectorFields.push(...Object.keys(fields));
      }

      const uniqueFields = [...new Set(vectorFields)];
      uniqueFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        advancedStreamlineField.appendChild(option);
      });

      const defaultVector = this.getDefaultVectorField({ fields });
      if (defaultVector && advancedStreamlineField.querySelector(`option[value="${defaultVector}"]`)) {
        advancedStreamlineField.value = defaultVector;
      } else if (advancedStreamlineField.options.length > 1) {
        advancedStreamlineField.selectedIndex = 1; // Select first non-empty option
      }
    }

    // Update Glyph filter field dropdowns (uses same pattern as streamlines)
    this.populateGlyphFields();

    // Update Surface LIC vector field dropdown
    const surfaceLICVectorField = document.getElementById('surfaceLICVectorField');
    if (surfaceLICVectorField) {
      surfaceLICVectorField.innerHTML = '<option value="">Select field...</option>';
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - use availableFields and naming convention
        const fieldNames = this.viewer?.availableFields || [];
        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          }
        });
      } else if (fields) {
        // Frontend mode - check if field data is 3D array
        Object.entries(fields).forEach(([name, value]) => {
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        if (name === 'U') option.selected = true;  // Default to velocity field
        surfaceLICVectorField.appendChild(option);
      });
    }

    // Update slice streamline vector field dropdown
    const sliceVectorFieldSelect = document.getElementById('sliceStreamlineVectorField');
    if (sliceVectorFieldSelect && fields) {
      const previousValue = sliceVectorFieldSelect.value || '';
      sliceVectorFieldSelect.innerHTML = '';

      const vectorFields = [];
      Object.entries(fields).forEach(([name, value]) => {
        if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
          vectorFields.push(name);
        }
      });

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        sliceVectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && sliceVectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (sliceVectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      sliceVectorFieldSelect.value = preferred;
    }

    const sliceColorFieldSelect = document.getElementById('sliceStreamlineColorField');
    console.log('[updateAvailableFields] sliceStreamlineColorField element:', sliceColorFieldSelect);
    if (sliceColorFieldSelect) {
      console.log('[updateAvailableFields] Populating sliceStreamlineColorField, fields:', fields);
      const previousValue = sliceColorFieldSelect.value || '';
      sliceColorFieldSelect.innerHTML = '';

      // Add Auto option
      const autoOption = document.createElement('option');
      autoOption.value = '';
      autoOption.textContent = 'Auto';
      sliceColorFieldSelect.appendChild(autoOption);

      if (fields) {
        console.log('[updateAvailableFields] fields is truthy, Object.keys(fields):', Object.keys(fields));
        // Separate scalar and vector fields
        const scalarFields = [];
        const vectorFields = [];

        Object.entries(fields).forEach(([name, value]) => {
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });

        console.log('[updateAvailableFields] scalarFields:', scalarFields, 'vectorFields:', vectorFields);

        // Add scalar fields first (more common for coloring)
        if (scalarFields.length > 0) {
          const scalarGroup = document.createElement('optgroup');
          scalarGroup.label = 'Scalar Fields';
          scalarFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
            const option = document.createElement('option');
            option.value = name;
            option.textContent = name;
            scalarGroup.appendChild(option);
          });
          sliceColorFieldSelect.appendChild(scalarGroup);
        }

        // Add vector fields
        if (vectorFields.length > 0) {
          const vectorGroup = document.createElement('optgroup');
          vectorGroup.label = 'Vector Fields';
          vectorFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
            const option = document.createElement('option');
            option.value = name;
            option.textContent = name;
            vectorGroup.appendChild(option);
          });
          sliceColorFieldSelect.appendChild(vectorGroup);
        }
      }

      // Set preferred value
      let preferred = '';
      if (previousValue && sliceColorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (sliceColorFieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (sliceColorFieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (sliceColorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      }
      sliceColorFieldSelect.value = preferred;
      console.log('[updateAvailableFields] Final dropdown options count:', sliceColorFieldSelect.options.length, 'selected:', preferred);
    }

    // Update Gradient filter field dropdown
    const gradientField = document.getElementById('gradientField');
    if (gradientField && fields) {
      gradientField.innerHTML = '<option value="">Select field...</option>';
      Object.keys(fields).forEach(fieldName => {
        const option = document.createElement('option');
        option.value = fieldName;
        option.textContent = fieldName;
        gradientField.appendChild(option);
      });
    }

    // Update Calculator available fields reference
    const calculatorFieldReference = document.getElementById('calculatorFieldReference');
    if (calculatorFieldReference && fields) {
      calculatorFieldReference.innerHTML = '';
      Object.keys(fields).forEach(fieldName => {
        const option = document.createElement('option');
        option.value = fieldName;
        option.textContent = fieldName;
        calculatorFieldReference.appendChild(option);
      });
    }

    // Update Selection threshold field dropdown
    const selectionThresholdField = document.getElementById('selectionThresholdField');
    if (selectionThresholdField && fields) {
      selectionThresholdField.innerHTML = '<option value="">Select field...</option>';
      Object.keys(fields).forEach(fieldName => {
        // Include scalar fields for thresholding
        const option = document.createElement('option');
        option.value = fieldName;
        option.textContent = fieldName;
        selectionThresholdField.appendChild(option);
      });
    }

    // Update Volume field dropdown
    const volumeField = document.getElementById('volumeField');
    if (volumeField && fields) {
      volumeField.innerHTML = '<option value="">Select field...</option>';
      Object.keys(fields).forEach(fieldName => {
        // Include scalar fields for volume rendering
        const option = document.createElement('option');
        option.value = fieldName;
        option.textContent = fieldName;
        volumeField.appendChild(option);
      });
    }

    // Don't sync streamline controls here - it overrides user selections
    // Controls will be synced only when streamlines are explicitly created/updated
  }

  // Populate 3D streamline field dropdowns (called when streamline controls are shown)
  populateStreamlineFields() {
    console.log('[populateStreamlineFields] Called');
    console.log('[populateStreamlineFields] renderingMode:', this.viewer?.renderingMode);

    // Get fields - in backend mode use availableFields, in frontend mode use currentData.fields
    let fieldNames = [];
    let fields = null;

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateStreamlineFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateStreamlineFields] Frontend mode - fields:', fields);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateStreamlineFields] No fields available');
      return;
    }

    // Populate "Integrate Along" dropdown (vector fields only)
    const vectorFieldSelect = document.getElementById('streamlineVectorField');
    if (vectorFieldSelect) {
      const previousValue = vectorFieldSelect.value || '';
      vectorFieldSelect.innerHTML = '';

      // Find vector fields
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention
        // Check for Fluent-style velocity components (X_VELOCITY, Y_VELOCITY, Z_VELOCITY)
        const hasFluentVelocityComponents =
          fieldNames.includes('X_VELOCITY') &&
          fieldNames.includes('Y_VELOCITY') &&
          fieldNames.includes('Z_VELOCITY');

        if (hasFluentVelocityComponents) {
          // Add combined velocity option (backend combines X/Y/Z_VELOCITY)
          vectorFields.push('velocity');
        }

        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            if (!vectorFields.includes(name)) {
              vectorFields.push(name);
            }
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      console.log('[populateStreamlineFields] vectorFields:', vectorFields);

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        vectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && vectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (vectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      vectorFieldSelect.value = preferred;
      console.log('[populateStreamlineFields] Vector field set to:', preferred);
    }

    // Populate "Color By" dropdown (all fields)
    const colorFieldSelect = document.getElementById('streamlineColorField');
    if (colorFieldSelect) {
      const previousValue = colorFieldSelect.value || '';
      colorFieldSelect.innerHTML = '';

      // Add Auto option
      const autoOption = document.createElement('option');
      autoOption.value = '';
      autoOption.textContent = 'Auto';
      colorFieldSelect.appendChild(autoOption);

      // Separate scalar and vector fields
      const scalarFields = [];
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention
        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      }

      console.log('[populateStreamlineFields] Color dropdown - scalarFields:', scalarFields, 'vectorFields:', vectorFields);

      // Add scalar fields first (more common for coloring)
      if (scalarFields.length > 0) {
        const scalarGroup = document.createElement('optgroup');
        scalarGroup.label = 'Scalar Fields';
        scalarFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          scalarGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(scalarGroup);
      }

      // Add vector fields
      if (vectorFields.length > 0) {
        const vectorGroup = document.createElement('optgroup');
        vectorGroup.label = 'Vector Fields';
        vectorFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          vectorGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(vectorGroup);
      }

      // Set preferred value
      let preferred = '';
      if (previousValue && colorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (colorFieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (colorFieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      } else if (colorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      }
      colorFieldSelect.value = preferred;
      console.log('[populateStreamlineFields] Color field set to:', preferred);
      console.log('[populateStreamlineFields] Final dropdown has', colorFieldSelect.options.length, 'options');
    }

    // Apply defaults to seed point inputs based on mesh bounds
    this.syncStreamlineControls();
  }

  /**
   * Populate Glyph filter dropdowns (vector field for direction, color field for coloring)
   * Works in both frontend and backend (geometry) rendering modes
   * Follows the exact same pattern as populateStreamlineFields()
   */
  populateGlyphFields() {
    console.log('[populateGlyphFields] Called');
    console.log('[populateGlyphFields] renderingMode:', this.viewer?.renderingMode);

    // Get fields - in backend mode use availableFields, in frontend mode use currentData.fields
    let fieldNames = [];
    let fields = null;

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateGlyphFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateGlyphFields] Frontend mode - fields:', fields);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateGlyphFields] No fields available');
      return;
    }

    // Populate "Vector Field (Direction)" dropdown (vector fields only)
    const vectorFieldSelect = document.getElementById('glyphVectorField');
    if (vectorFieldSelect) {
      const previousValue = vectorFieldSelect.value || '';
      vectorFieldSelect.innerHTML = '<option value="">Select field...</option>';

      // Find vector fields
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention (same as streamlines)
        // Check for Fluent-style velocity components (X_VELOCITY, Y_VELOCITY, Z_VELOCITY)
        const hasFluentVelocityComponents =
          fieldNames.includes('X_VELOCITY') &&
          fieldNames.includes('Y_VELOCITY') &&
          fieldNames.includes('Z_VELOCITY');

        if (hasFluentVelocityComponents) {
          // Add combined velocity option (backend combines X/Y/Z_VELOCITY)
          vectorFields.push('velocity');
        }

        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            if (!vectorFields.includes(name)) {
              vectorFields.push(name);
            }
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      console.log('[populateGlyphFields] vectorFields:', vectorFields);

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        vectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && vectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (vectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      vectorFieldSelect.value = preferred;
      console.log('[populateGlyphFields] Vector field set to:', preferred);
    }

    // Populate "Color By" dropdown (all fields)
    const colorFieldSelect = document.getElementById('glyphColorField');
    if (colorFieldSelect) {
      const previousValue = colorFieldSelect.value || '';
      colorFieldSelect.innerHTML = '';

      // Add "Vector Magnitude" as default option (similar to streamlines' "Auto")
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = '(Vector Magnitude)';
      colorFieldSelect.appendChild(defaultOption);

      // Separate scalar and vector fields
      const scalarFields = [];
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention (same as streamlines)
        // Check for Fluent-style velocity components (X_VELOCITY, Y_VELOCITY, Z_VELOCITY)
        const hasFluentVelocityComponents =
          fieldNames.includes('X_VELOCITY') &&
          fieldNames.includes('Y_VELOCITY') &&
          fieldNames.includes('Z_VELOCITY');

        if (hasFluentVelocityComponents && !fieldNames.includes('velocity')) {
          // Add combined velocity option (backend combines X/Y/Z_VELOCITY)
          vectorFields.push('velocity');
        }

        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            if (!vectorFields.includes(name)) {
              vectorFields.push(name);
            }
          } else {
            scalarFields.push(name);
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          } else {
            scalarFields.push(name);
          }
        });
      }

      console.log('[populateGlyphFields] Color dropdown - scalarFields:', scalarFields, 'vectorFields:', vectorFields);

      // Add scalar fields first (more common for coloring)
      if (scalarFields.length > 0) {
        const scalarGroup = document.createElement('optgroup');
        scalarGroup.label = 'Scalar Fields';
        scalarFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          scalarGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(scalarGroup);
      }

      // Add vector fields (will use magnitude for coloring)
      if (vectorFields.length > 0) {
        const vectorGroup = document.createElement('optgroup');
        vectorGroup.label = 'Vector Fields (Magnitude)';
        vectorFields.sort((a, b) => a.localeCompare(b)).forEach(name => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          vectorGroup.appendChild(option);
        });
        colorFieldSelect.appendChild(vectorGroup);
      }

      // Set preferred value
      let preferred = '';
      if (previousValue && colorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (colorFieldSelect.querySelector('option[value="p"]')) {
        preferred = 'p';
      } else if (colorFieldSelect.querySelector('option[value="pressure"]')) {
        preferred = 'pressure';
      }
      colorFieldSelect.value = preferred;
      console.log('[populateGlyphFields] Color field set to:', preferred);
      console.log('[populateGlyphFields] Final dropdown has', colorFieldSelect.options.length, 'options');
    }
  }

  /**
   * Populate Field Calculator available fields display
   * Shows all available fields that can be used in expressions
   */
  populateCalculatorFields() {
    console.log('[populateCalculatorFields] Called');

    // Get available fields
    let fieldNames = [];
    if (this.viewer?.renderingMode === 'geometry') {
      fieldNames = this.viewer?.availableFields || [];
    } else {
      const fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
    }

    // Get field metadata for type information
    const fieldMetadata = this.viewer?.fieldMetadata || {};
    console.log('[populateCalculatorFields] Field metadata:', fieldMetadata);

    const availableFieldsDiv = document.getElementById('calculatorAvailableFields');
    if (!availableFieldsDiv) return;

    // Filter out non-field entries (files like .xml, system arrays, etc.)
    fieldNames = fieldNames.filter(name => {
      // Filter out file extensions
      if (name.includes('.xml') || name.includes('.json') || name.includes('.dat')) return false;
      // Filter out internal/system arrays
      if (name.startsWith('vtk') || name.startsWith('GlobalNodeId') || name.startsWith('GlobalCellId')) return false;
      return true;
    });

    if (fieldNames.length === 0) {
      availableFieldsDiv.innerHTML = '<em>No fields available. Load a case first.</em>';
      return;
    }

    // Check for Fluent velocity components and add synthetic velocity
    const hasFluentVelocity = fieldNames.includes('X_VELOCITY') &&
      fieldNames.includes('Y_VELOCITY') &&
      fieldNames.includes('Z_VELOCITY');
    if (hasFluentVelocity && !fieldNames.includes('velocity')) {
      fieldNames = ['velocity', ...fieldNames];
      // Add metadata for synthetic velocity
      fieldMetadata['velocity'] = { type: 'vector', components: 3 };
    }

    // Helper to get field type label (extended format)
    const getFieldTypeLabel = (name) => {
      const meta = fieldMetadata[name];
      if (!meta) return '';
      const components = meta.components || 1;
      const type = meta.type || (components === 1 ? 'scalar' : components === 3 ? 'vector' : 'unknown');
      if (type === 'scalar' || components === 1) {
        return '<span style="color: #8cf; font-size: 9px; margin-left: 4px; opacity: 0.8;">(scalar)</span>';
      } else if (type === 'vector' || components === 3) {
        return '<span style="color: #fc8; font-size: 9px; margin-left: 4px; opacity: 0.8;">(vector, 3)</span>';
      } else if (type === 'symmTensor' || components === 6) {
        return '<span style="color: #c8f; font-size: 9px; margin-left: 4px; opacity: 0.8;">(symmTensor, 6)</span>';
      } else if (type === 'tensor' || components === 9) {
        return '<span style="color: #c8f; font-size: 9px; margin-left: 4px; opacity: 0.8;">(tensor, 9)</span>';
      } else if (components > 1) {
        return `<span style="color: #ccc; font-size: 9px; margin-left: 4px; opacity: 0.8;">(${components} components)</span>`;
      }
      return '';
    };

    // Helper to get tooltip with type info
    const getFieldTooltip = (name) => {
      const meta = fieldMetadata[name];
      if (!meta) return `Click to insert '${name}'`;
      const components = meta.components || 1;
      const type = meta.type || (components === 1 ? 'scalar' : 'unknown');
      return `${name} (${type}, ${components} component${components !== 1 ? 's' : ''}) - Click to insert`;
    };

    // Display as clickable field chips with dark theme styling and type indicators
    availableFieldsDiv.innerHTML = fieldNames.map(name => {
      const typeLabel = getFieldTypeLabel(name);
      const tooltip = getFieldTooltip(name);
      return `<span class="calculator-field-chip" style="display: inline-block; background: rgba(255,255,255,0.1); color: var(--text-primary, #fff); padding: 3px 8px; border-radius: 4px; cursor: pointer; font-family: monospace; font-size: 10px; border: 1px solid rgba(255,255,255,0.2); white-space: nowrap; transition: background 0.2s;" onmouseover="this.style.background='rgba(255,255,255,0.2)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'" onclick="document.getElementById('calculatorExpression').value += '${name}'" title="${tooltip}">${name}${typeLabel}</span>`;
    }).join('');

    console.log('[populateCalculatorFields] Displayed', fieldNames.length, 'fields');
  }

  /**
   * Set a quick expression for the Field Calculator
   * Auto-detects the correct velocity field name (U for OpenFOAM, velocity for Fluent)
   */
  setCalculatorQuickExpression(operation) {
    // Get available fields to detect velocity field name
    let fieldNames = [];
    if (this.viewer?.renderingMode === 'geometry') {
      fieldNames = this.viewer?.availableFields || [];
    } else {
      const fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
    }

    // Detect velocity field name
    let velocityField = 'U'; // Default for OpenFOAM
    if (fieldNames.includes('velocity')) {
      velocityField = 'velocity'; // Fluent combined velocity
    } else if (fieldNames.includes('U')) {
      velocityField = 'U'; // OpenFOAM
    } else if (fieldNames.includes('X_VELOCITY')) {
      velocityField = 'velocity'; // Fluent components available
    }

    const expressionInput = document.getElementById('calculatorExpression');
    const resultNameInput = document.getElementById('calculatorResultName');

    if (!expressionInput || !resultNameInput) return;

    // Set expression and result name based on operation
    switch (operation) {
      case 'vorticity':
        expressionInput.value = `vorticity(${velocityField})`;
        resultNameInput.value = 'vorticity';
        break;
      case 'q_criterion':
        expressionInput.value = `q_criterion(${velocityField})`;
        resultNameInput.value = 'Q_criterion';
        break;
      case 'divergence':
        expressionInput.value = `divergence(${velocityField})`;
        resultNameInput.value = 'divergence';
        break;
      case 'mag':
        expressionInput.value = `mag(${velocityField})`;
        resultNameInput.value = 'velocity_mag';
        break;
      default:
        console.warn('[Calculator] Unknown quick operation:', operation);
    }
  }

  /**
   * Apply the Field Calculator - compute a new field from an expression
   * Sends request to backend and optionally saves to disk
   */
  async applyFieldCalculator() {
    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    const expression = document.getElementById('calculatorExpression')?.value?.trim();
    const resultName = document.getElementById('calculatorResultName')?.value?.trim();
    const applyToAllTimesteps = document.getElementById('calculatorApplyAllTimesteps')?.checked || false;

    if (!expression) {
      Utils.notify('Please enter an expression', 'error');
      return;
    }

    if (!resultName) {
      Utils.notify('Please enter a result field name', 'error');
      return;
    }

    // Validate result name (no spaces, valid identifier)
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(resultName)) {
      Utils.notify('Invalid field name. Use letters, numbers, and underscores only.', 'error');
      return;
    }

    const resultDiv = document.getElementById('calculatorResult');

    await this.applyWithProgressBar('btnApplyCalculator', async () => {
      // Get current time from viewer
      const currentTime = this.viewer?.currentTime || null;

      // Determine data source for correct API endpoint
      const geometryDataSource = this.viewer?.geometryParams?.dataSource;

      const payload = {
        case_path: casePath,
        expression: expression,
        result_name: resultName,
        time: currentTime,
        apply_to_all_timesteps: applyToAllTimesteps,
        save_to_disk: true,
        mesh_parts: this.getVisibleMeshParts()
      };

      console.log('[applyFieldCalculator] Sending request:', payload);

      const token = window.Auth?.getToken();
      const response = await fetch('/api/filters/calculator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      console.log('[applyFieldCalculator] Response:', data);

      // Show result summary
      if (resultDiv) {
        resultDiv.style.display = 'block';
        const stats = data.statistics || {};
        const timestepInfo = data.timesteps_processed
          ? `<br>Processed ${data.timesteps_processed} timesteps`
          : '';
        const savedInfo = data.saved_to_disk
          ? '<br>Field saved to disk'
          : '';

        resultDiv.innerHTML = `
          <strong>Field "${resultName}" computed</strong>${timestepInfo}${savedInfo}<br>
          <span style="font-size: 10px; color: var(--text-secondary);">
            Min: ${stats.min?.toFixed(4) ?? 'N/A'} |
            Max: ${stats.max?.toFixed(4) ?? 'N/A'} |
            Mean: ${stats.mean?.toFixed(4) ?? 'N/A'}
          </span>
        `;
      }

      // Add the new field to available fields (always saved to disk)
      if (this.viewer?.availableFields) {
        if (!this.viewer.availableFields.includes(resultName)) {
          this.viewer.availableFields.push(resultName);
          // Refresh all field dropdowns so the new field appears everywhere
          this.refreshAllFieldDropdowns();

          // Broadcast to other collaborators so they can see the new field
          this.broadcastNewField(resultName, expression);
        }
      }

    }, {
      startMessage: `Computing field "${resultName}"...`,
      successMessage: `Field "${resultName}" computed successfully`,
      errorPrefix: 'Calculator error'
    });
  }

  /**
   * Refresh ALL field dropdowns in the UI
   * Called when a new field is computed (locally or by a collaborator)
   */
  refreshAllFieldDropdowns() {
    console.log('[Filters] Refreshing all field dropdowns');

    // Main colormap Variable dropdown (top of Color Map panel)
    this.refreshColormapVariableDropdown();

    // Calculator available fields
    this.populateCalculatorFields();

    // Streamlines (vector field + color field)
    this.populateStreamlineFields();

    // Glyphs (vector field + color field)
    this.populateGlyphFields();

    // Surface LIC
    if (typeof this.populateSurfaceLICFields === 'function') {
      this.populateSurfaceLICFields();
    }

    // Volume LIC
    if (typeof this.populateVolumeLICFields === 'function') {
      this.populateVolumeLICFields();
    }

    // Slice-related fields (contour, vector, isolines)
    this.refreshSliceFieldDropdowns();

    // Contour filter field
    this.refreshContourFieldDropdown();

    // Volume rendering field
    this.refreshVolumeFieldDropdown();

    // Selection threshold field
    this.refreshSelectionFieldDropdown();

    // XY Plot field
    this.refreshXYPlotFieldDropdown();

    // Integration field
    this.refreshIntegrationFieldDropdown();
  }

  /**
   * Refresh the main colormap Variable dropdown
   */
  refreshColormapVariableDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('colormapVariable', fieldNames);
  }

  /**
   * Refresh slice-related field dropdowns
   */
  refreshSliceFieldDropdowns() {
    const fieldNames = this.viewer?.availableFields || [];
    if (fieldNames.length === 0) return;

    // Slice Contour Field
    this.refreshDropdown('sliceContourField', fieldNames);

    // Slice Vector Field
    this.refreshDropdown('sliceVectorField', fieldNames, true); // vectors only

    // Slice Isolines Field
    this.refreshDropdown('sliceIsolinesField', fieldNames);
  }

  /**
   * Refresh contour filter field dropdown
   */
  refreshContourFieldDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('contourField', fieldNames);
  }

  /**
   * Refresh volume rendering field dropdown
   */
  refreshVolumeFieldDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('volumeField', fieldNames);
  }

  /**
   * Refresh selection threshold field dropdown
   */
  refreshSelectionFieldDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('selectionThresholdField', fieldNames);
  }

  /**
   * Refresh XY plot field dropdown
   */
  refreshXYPlotFieldDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('xyPlotField', fieldNames);
  }

  /**
   * Refresh integration field dropdown
   */
  refreshIntegrationFieldDropdown() {
    const fieldNames = this.viewer?.availableFields || [];
    this.refreshDropdown('integrationField', fieldNames);
  }

  /**
   * Helper to refresh a single dropdown with field names
   * Preserves the current selection if it still exists
   */
  refreshDropdown(elementId, fieldNames, vectorsOnly = false) {
    const select = document.getElementById(elementId);
    if (!select) return;

    const previousValue = select.value;

    // Filter to vectors if needed (heuristic based on naming)
    let fields = fieldNames;
    if (vectorsOnly) {
      fields = fieldNames.filter(name =>
        name === 'U' || name === 'velocity' || name === 'U_mean' ||
        name.toLowerCase().includes('velocity') ||
        name.toLowerCase().includes('vorticity')
      );
      // If no vector fields found, show all
      if (fields.length === 0) fields = fieldNames;
    }

    // Rebuild options, preserving special first options like "None" or placeholder
    const firstOption = select.options[0];
    const hasPlaceholder = firstOption && (firstOption.value === '' || firstOption.value === 'none');

    select.innerHTML = '';

    if (hasPlaceholder) {
      const opt = document.createElement('option');
      opt.value = firstOption.value;
      opt.textContent = firstOption.textContent;
      select.appendChild(opt);
    }

    fields.forEach(name => {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      select.appendChild(opt);
    });

    // Restore previous selection if it still exists
    if (previousValue && Array.from(select.options).some(o => o.value === previousValue)) {
      select.value = previousValue;
    }
  }

  /**
   * Broadcast new computed field to other collaborators via WebSocket
   * So they can immediately see and use the new field
   */
  broadcastNewField(fieldName, expression) {
    const ws = this.viewer?.websocket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'field_computed',
        field_name: fieldName,
        expression: expression
      }));
      console.log(`[Filters] Broadcasted new field '${fieldName}' to collaborators`);
    }
  }

  /**
   * Populate Surface LIC vector field dropdown
   * Works in both frontend and backend (geometry) rendering modes
   */
  populateSurfaceLICFields() {
    console.log('[populateSurfaceLICFields] Called');
    console.log('[populateSurfaceLICFields] renderingMode:', this.viewer?.renderingMode);

    let fieldNames = [];
    let fields = null;

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateSurfaceLICFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateSurfaceLICFields] Frontend mode - fields:', fields);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateSurfaceLICFields] No fields available');
      return;
    }

    const vectorFieldSelect = document.getElementById('surfaceLICVectorField');
    if (vectorFieldSelect) {
      const previousValue = vectorFieldSelect.value || '';
      vectorFieldSelect.innerHTML = '';

      // Find vector fields
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention
        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      console.log('[populateSurfaceLICFields] vectorFields:', vectorFields);

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        vectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && vectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (vectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      vectorFieldSelect.value = preferred;
      console.log('[populateSurfaceLICFields] Vector field set to:', preferred);
    }
  }

  populateVolumeLICFields() {
    console.log('[populateVolumeLICFields] Called');
    console.log('[populateVolumeLICFields] renderingMode:', this.viewer?.renderingMode);

    let fieldNames = [];
    let fields = null;

    if (this.viewer?.renderingMode === 'geometry') {
      // Backend mode - use availableFields list
      fieldNames = this.viewer?.availableFields || [];
      console.log('[populateVolumeLICFields] Backend mode - availableFields:', fieldNames);
    } else {
      // Frontend mode - use fields data structure
      fields = this.viewer?.currentData?.fields;
      if (fields) {
        fieldNames = Object.keys(fields);
      }
      console.log('[populateVolumeLICFields] Frontend mode - fields:', fields);
    }

    if (fieldNames.length === 0) {
      console.warn('[populateVolumeLICFields] No fields available');
      return;
    }

    const vectorFieldSelect = document.getElementById('volumeLICVectorField');
    if (vectorFieldSelect) {
      const previousValue = vectorFieldSelect.value || '';
      vectorFieldSelect.innerHTML = '';

      // Find vector fields
      const vectorFields = [];

      if (this.viewer?.renderingMode === 'geometry') {
        // Backend mode - classify by naming convention
        fieldNames.forEach(name => {
          if (name === 'U' || name === 'U_mean' || name === 'velocity' ||
            name.toLowerCase().includes('velocity') || name.toLowerCase().includes('speed')) {
            vectorFields.push(name);
          }
        });
      } else {
        // Frontend mode - check if field data is 3D array
        fieldNames.forEach(name => {
          const value = fields[name];
          if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
            vectorFields.push(name);
          }
        });
      }

      console.log('[populateVolumeLICFields] vectorFields:', vectorFields);

      vectorFields.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        vectorFieldSelect.appendChild(option);
      });

      // Set default to U or previously selected
      let preferred = '';
      if (previousValue && vectorFieldSelect.querySelector(`option[value="${previousValue}"]`)) {
        preferred = previousValue;
      } else if (vectorFieldSelect.querySelector('option[value="U"]')) {
        preferred = 'U';
      } else if (vectorFields.length > 0) {
        preferred = vectorFields[0];
      }
      vectorFieldSelect.value = preferred;
      console.log('[populateVolumeLICFields] Vector field set to:', preferred);
    }
  }

  syncStreamlineControls(options = null) {
    const seedTypeSelect = document.getElementById('streamlineSeedType');
    if (!seedTypeSelect) return;

    // Set flag to prevent auto-apply during programmatic updates
    this.streamlineSyncInProgress = true;
    console.log('[syncStreamlineControls] Starting sync, flag set to true');

    const defaults = this.getStreamlineDefaultParams(this.viewer?.currentData || null);
    const isOpenFoamSource = (this.viewer?.currentData?.source === 'OpenFOAM')
      || (this.viewer?.currentData?.metadata?.format === 'OpenFOAM');

    let targetOptions = options;
    if (!targetOptions) {
      for (const [, entry] of this.activeFilters.entries()) {
        if (entry?.type === 'streamlines') {
          targetOptions = entry.options;
          break;
        }
      }
    }

    const setValue = (id, value, formatter) => {
      const input = document.getElementById(id);
      if (!input || value == null) return;
      input.value = formatter ? formatter(value) : value;
    };

    const applyDefaultsToInputs = (defaults) => {
      if (!defaults) return;
      setValue('streamlinePointCenterX', defaults.pointCenter?.[0] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePointCenterY', defaults.pointCenter?.[1] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePointCenterZ', defaults.pointCenter?.[2] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePointRadius', defaults.pointRadius ?? 1, (v) => Number(v).toFixed(4));
      setValue('streamlinePointCount', defaults.pointCount ?? 200);
      setValue('streamlineLineStartX', defaults.lineStart?.[0] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineStartY', defaults.lineStart?.[1] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineStartZ', defaults.lineStart?.[2] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineEndX', defaults.lineEnd?.[0] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineEndY', defaults.lineEnd?.[1] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineEndZ', defaults.lineEnd?.[2] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlineLineResolution', defaults.lineResolution ?? 100);
      setValue('streamlinePlaneOriginX', defaults.planeOrigin?.[0] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneOriginY', defaults.planeOrigin?.[1] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneOriginZ', defaults.planeOrigin?.[2] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneNormalX', defaults.planeNormal?.[0] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneNormalY', defaults.planeNormal?.[1] ?? 0, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneNormalZ', defaults.planeNormal?.[2] ?? 1, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneWidth', defaults.planeWidth ?? 1, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneHeight', defaults.planeHeight ?? 1, (v) => Number(v).toFixed(4));
      setValue('streamlinePlaneResolutionU', defaults.planeResolutionU ?? 20);
      setValue('streamlinePlaneResolutionV', defaults.planeResolutionV ?? 20);
      setValue('streamlineMaxLength', defaults.maxLength ?? 100, (v) => Number(v).toFixed(3));
      setValue('streamlineStepSize', defaults.stepSize ?? 0.01, (v) => Number(v).toFixed(4));
      setValue('streamlinePreviewDotScale', defaults.previewDotScale ?? 0.004, (v) => Number(v).toFixed(4));
      setValue('streamlinePreviewSpeed', defaults.previewSpeed ?? 1, (v) => Number(v).toFixed(3));
      const previewCheckbox = document.getElementById('streamlinePreviewEnabled');
      if (previewCheckbox) {
        previewCheckbox.checked = defaults.previewEnabled === true;
      }
    };

    const subscribePointSeedInputs = () => {
      if (this.seedInputListenersAttached) return;
      const radiusInput = document.getElementById('streamlinePointRadius');
      if (radiusInput) {
        const persistRadius = () => {
          const value = parseFloat(radiusInput.value);
          if (!Number.isFinite(value)) return;
          this.updateStoredStreamlineSettings(this.viewer?.currentData || null, {
            pointRadius: value
          });
        };
        radiusInput.addEventListener('change', persistRadius);
        radiusInput.addEventListener('blur', persistRadius);
      }

      const countInput = document.getElementById('streamlinePointCount');
      if (countInput) {
        const persistCount = () => {
          const value = parseInt(countInput.value, 10);
          if (!Number.isFinite(value)) return;
          this.updateStoredStreamlineSettings(this.viewer?.currentData || null, {
            pointCount: value
          });
        };
        countInput.addEventListener('change', persistCount);
        countInput.addEventListener('blur', persistCount);
      }

      this.seedInputListenersAttached = true;
    };

    if (!targetOptions) {
      // Detect if case has changed by checking case ID
      const currentCaseId = this.viewer?.currentData?.metadata?.case_id ||
        this.viewer?.currentData?.metadata?.case_name ||
        this.viewer?.currentData?.metadata?.filename;
      const caseChanged = this.lastCaseId !== currentCaseId;

      if (caseChanged) {
        this.lastCaseId = currentCaseId;
      }

      seedTypeSelect.value = isOpenFoamSource ? 'point' : 'line';
      seedTypeSelect.dispatchEvent(new Event('change'));

      // Only apply defaults if:
      // 1. Case has changed (new data loaded), OR
      // 2. Inputs are empty (first time ever)
      const maxLengthInput = document.getElementById('streamlineMaxLength');
      const planeOriginXInput = document.getElementById('streamlinePlaneOriginX');
      const pointCenterXInput = document.getElementById('streamlinePointCenterX');
      const hasEmptyCommonInputs = !maxLengthInput || !maxLengthInput.value || maxLengthInput.value === '';
      const hasEmptyPlaneInputs = !planeOriginXInput || !planeOriginXInput.value;
      const hasEmptyPointInputs = !pointCenterXInput || !pointCenterXInput.value;

      if (caseChanged) {
        // Case changed - reset everything to defaults for new case
        const defaults = this.getStreamlineDefaultParams(this.viewer?.currentData || null);
        applyDefaultsToInputs(defaults);
      } else if (hasEmptyCommonInputs || hasEmptyPointInputs || hasEmptyPlaneInputs) {
        // Apply defaults to empty inputs
        const defaults = this.getStreamlineDefaultParams(this.viewer?.currentData || null);
        if (hasEmptyCommonInputs) {
          setValue('streamlineMaxLength', defaults.maxLength ?? 100, (v) => Number(v).toFixed(3));
          setValue('streamlineStepSize', defaults.stepSize ?? 0.01, (v) => Number(v).toFixed(4));
        }
        // Apply point seed defaults if point inputs are empty
        if (hasEmptyPointInputs) {
          setValue('streamlinePointCenterX', defaults.pointCenter?.[0] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePointCenterY', defaults.pointCenter?.[1] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePointCenterZ', defaults.pointCenter?.[2] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePointRadius', defaults.pointRadius ?? 1, (v) => Number(v).toFixed(4));
          setValue('streamlinePointCount', defaults.pointCount ?? 200);
        }
        // Apply plane defaults if plane inputs are empty
        if (hasEmptyPlaneInputs) {
          setValue('streamlinePlaneOriginX', defaults.planeOrigin?.[0] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneOriginY', defaults.planeOrigin?.[1] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneOriginZ', defaults.planeOrigin?.[2] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneNormalX', defaults.planeNormal?.[0] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneNormalY', defaults.planeNormal?.[1] ?? 0, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneNormalZ', defaults.planeNormal?.[2] ?? 1, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneWidth', defaults.planeWidth ?? 1, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneHeight', defaults.planeHeight ?? 1, (v) => Number(v).toFixed(4));
          setValue('streamlinePlaneResolutionU', defaults.planeResolutionU ?? 20);
          setValue('streamlinePlaneResolutionV', defaults.planeResolutionV ?? 20);
        }
      }

      subscribePointSeedInputs();

      // Reset flag before returning
      this.streamlineSyncInProgress = false;
      console.log('[syncStreamlineControls] Sync complete (no options path), flag set to false');
      return;
    }

    seedTypeSelect.value = targetOptions.seedType || (isOpenFoamSource ? 'point' : 'line');
    seedTypeSelect.dispatchEvent(new Event('change'));

    const fallbackDefaults = this.getStreamlineDefaultParams(this.viewer?.currentData || null);
    const previewCheckbox = document.getElementById('streamlinePreviewEnabled');

    const colormapSelect = document.getElementById('streamlineColormap');
    if (colormapSelect && targetOptions.colormap && colormapSelect.querySelector(`option[value="${targetOptions.colormap}"]`)) {
      colormapSelect.value = targetOptions.colormap;
    }

    const vectorFieldSelect = document.getElementById('streamlineVectorField');
    if (vectorFieldSelect && targetOptions.variable && vectorFieldSelect.querySelector(`option[value="${targetOptions.variable}"]`)) {
      vectorFieldSelect.value = targetOptions.variable;
    }

    setValue('streamlineMaxLength', targetOptions.maxLength ?? (fallbackDefaults?.maxLength ?? 100), (v) => Number(v).toFixed(3));
    setValue('streamlineStepSize', targetOptions.stepSize ?? (fallbackDefaults?.stepSize ?? 0.01), (v) => Number(v).toFixed(4));
    setValue('streamlinePreviewDotScale', targetOptions.previewDotScale ?? (fallbackDefaults?.previewDotScale ?? 0.004), (v) => Number(v).toFixed(4));
    setValue('streamlinePreviewSpeed', targetOptions.previewSpeed ?? (fallbackDefaults?.previewSpeed ?? 1), (v) => Number(v).toFixed(3));
    if (previewCheckbox) {
      previewCheckbox.checked = targetOptions.previewEnabled !== false;
    }

    if (targetOptions.seedType === 'point') {
      setValue('streamlinePointCenterX', targetOptions.pointCenterX ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePointCenterY', targetOptions.pointCenterY ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePointCenterZ', targetOptions.pointCenterZ ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePointRadius', targetOptions.pointRadius ?? 1, (v) => Number(v).toFixed(3));
      setValue('streamlinePointCount', targetOptions.pointCount ?? 100);
      this.updateStoredStreamlineSettings(this.viewer?.currentData || null, {
        pointRadius: targetOptions.pointRadius,
        pointCount: targetOptions.pointCount
      });
    } else if (targetOptions.seedType === 'line') {
      setValue('streamlineLineStartX', targetOptions.lineStartX ?? -1, (v) => Number(v).toFixed(3));
      setValue('streamlineLineStartY', targetOptions.lineStartY ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlineLineStartZ', targetOptions.lineStartZ ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlineLineEndX', targetOptions.lineEndX ?? 1, (v) => Number(v).toFixed(3));
      setValue('streamlineLineEndY', targetOptions.lineEndY ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlineLineEndZ', targetOptions.lineEndZ ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlineLineResolution', targetOptions.lineResolution ?? 100);
    } else if (targetOptions.seedType === 'plane') {
      setValue('streamlinePlaneOriginX', targetOptions.planeOriginX ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneOriginY', targetOptions.planeOriginY ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneOriginZ', targetOptions.planeOriginZ ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneNormalX', targetOptions.planeNormalX ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneNormalY', targetOptions.planeNormalY ?? 0, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneNormalZ', targetOptions.planeNormalZ ?? 1, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneWidth', targetOptions.planeWidth ?? 1, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneHeight', targetOptions.planeHeight ?? 1, (v) => Number(v).toFixed(3));
      setValue('streamlinePlaneResolutionU', targetOptions.planeResolutionU ?? 20);
      setValue('streamlinePlaneResolutionV', targetOptions.planeResolutionV ?? 20);
    }

    subscribePointSeedInputs();

    // Reset flag after sync is complete
    this.streamlineSyncInProgress = false;
    console.log('[syncStreamlineControls] Sync complete, flag set to false');
  }

  // ========================================================================
  // UI HELPER METHODS
  // ========================================================================

  applySliceFromUI() {
    const viewer = window.viewer;
    if (!viewer || !viewer.currentData || !viewer.currentData.grid) {
      console.warn('No mesh data available for slicing');
      return;
    }

    const origin = new THREE.Vector3(
      parseFloat(document.getElementById('sliceOriginX').value) || 0,
      parseFloat(document.getElementById('sliceOriginY').value) || 0,
      parseFloat(document.getElementById('sliceOriginZ').value) || 0
    );

    const normal = new THREE.Vector3(
      parseFloat(document.getElementById('sliceNormalX').value) || 0,
      parseFloat(document.getElementById('sliceNormalY').value) || 0,
      parseFloat(document.getElementById('sliceNormalZ').value) || 1
    ).normalize();

    // Note: mesh_parts is passed for consistency but frontend slice operates on
    // merged grid data. Mesh visibility is handled via THREE.js scene (like contour/glyph).
    this.createSliceUnstructured(viewer.currentData, {
      origin,
      normal,
      variable: viewer.colormap?.currentField || 'pressure',
      id: 'slice-filter',
      mesh_parts: this.getVisibleMeshParts()
    });
  }

  async applyContourFromUI(options = {}) {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    // Extract cache_key if provided (for collaborative session cache lookup)
    const providedCacheKey = options.cache_key || null;

    const fieldName = document.getElementById('contourField')?.value;
    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    // Validate that this is not a vector field (using actual component count from backend)
    if (viewer.fieldMetadata && viewer.fieldMetadata[fieldName]) {
      const metadata = viewer.fieldMetadata[fieldName];
      if (metadata.components !== 1) {
        Utils.notify(`Cannot create contours for ${metadata.type || 'multi-component'} field "${fieldName}" (${metadata.components} components). Please use a scalar field instead.`, 'error');
        return;
      }
    }
    // If no metadata available, let the backend validate it

    const autoSpacing = document.getElementById('contourAutoSpacing')?.checked || false;
    let values = [];
    let numContours = 10;

    if (autoSpacing) {
      numContours = parseInt(document.getElementById('contourCount')?.value) || 10;
    } else {
      const valuesInput = document.getElementById('contourValues')?.value;
      if (!valuesInput) {
        Utils.notify('Please enter iso-values', 'error');
        return;
      }
      values = valuesInput.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
      if (values.length === 0) {
        Utils.notify('Please enter valid iso-values', 'error');
        return;
      }
    }

    let progressInterval = null;
    let originalButtonHTML = null;
    try {
      Utils.notify('Generating contours...', 'info');

      // Update button state with progress bar
      const btn = document.getElementById('btnApplyContour');
      if (btn) {
        btn.disabled = true;
        originalButtonHTML = btn.innerHTML;
        btn.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="flex: 1; height: 8px; background: rgba(255,255,255,0.2); border-radius: 4px; overflow: hidden;">
              <div id="contourProgressBar" style="height: 100%; width: 0%; background: linear-gradient(90deg, #4CAF50, #8BC34A); transition: width 0.3s ease;"></div>
            </div>
            <span id="contourProgressText" style="font-size: 11px;">0%</span>
          </div>
        `;
      }

      // Start simulated progress indicator
      let progress = 0;
      progressInterval = setInterval(() => {
        if (progress < 90) {
          progress += Math.random() * 10;
          if (progress > 90) progress = 90;
          const progressBar = document.getElementById('contourProgressBar');
          const progressText = document.getElementById('contourProgressText');
          if (progressBar) progressBar.style.width = `${progress}%`;
          if (progressText) progressText.textContent = `${Math.round(progress)}%`;
        }
      }, 500);

      const requestData = {
        case_path: casePath,
        field_name: fieldName,
        auto_spacing: autoSpacing,
        num_contours: numContours,
        values: values.length > 0 ? values : []  // Backend requires values field, use empty array when auto_spacing
        // Note: mesh_parts intentionally NOT passed - contours are computed on full mesh
        // and mesh visibility is handled on the frontend for consistent caching in collaboration
      };

      // Get current time value (works for both frontend and backend rendering modes)
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;
      if (currentTime !== undefined && currentTime !== null) {
        requestData.time = currentTime;
      }

      // Include cache_key for collaborative session cache lookup (followers)
      if (providedCacheKey) {
        requestData.cache_key = providedCacheKey;
        console.log('[Contour] Using provided cache_key for cache lookup:', providedCacheKey);
      }

      console.log('[Contour] Request data:', requestData);

      const response = await fetch('/api/filters/contour', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          console.error('[Contour] Backend error:', error);
          console.error('[Contour] error.detail:', error.detail);

          // Log each validation error with full details
          if (Array.isArray(error.detail)) {
            error.detail.forEach((e, i) => {
              console.error(`[Contour] Validation error ${i}:`, {
                location: e.loc,
                message: e.msg,
                type: e.type,
                full: e
              });
            });
          }

          // Handle detail being an array (FastAPI validation errors)
          let errorMessage = 'Contour filter failed';
          if (Array.isArray(error.detail)) {
            // Include location info in error message
            errorMessage = error.detail.map(e => {
              const field = e.loc ? e.loc.join('.') : 'unknown';
              return `${field}: ${e.msg || e.message || JSON.stringify(e)}`;
            }).join(', ');
          } else if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (error.message) {
            errorMessage = error.message;
          }

          throw new Error(errorMessage);
        } else {
          const errorText = await response.text();
          console.error('[Contour] Non-JSON error response:', errorText);
          throw new Error(`Contour filter failed (HTTP ${response.status})`);
        }
      }

      // Clear progress interval and show 100%
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      const progressBar = document.getElementById('contourProgressBar');
      const progressText = document.getElementById('contourProgressText');
      if (progressBar) progressBar.style.width = '100%';
      if (progressText) progressText.textContent = '100%';

      const result = await response.json();

      console.log('[Contour] Backend response:', result);

      // Store cache_key from response for collaborative session optimization
      const responseCacheKey = result.cache_key || null;
      if (responseCacheKey) {
        console.log('[Contour] Got cache_key from response:', responseCacheKey);
      }

      // Check if this was a cache hit (instant result for followers)
      if (result.from_cache) {
        console.log('[Contour] Cache hit - using pre-computed result');
      }

      // Render contours (pass cache_key for storage)
      this.renderContours(result.data, responseCacheKey);

      Utils.notify(`Created ${result.data.num_contours} contours${result.from_cache ? ' (cached)' : ''}`, 'success');

      // Notify view lock manager of visualization change
      if (this.viewer?.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    } catch (error) {
      console.error('[Contour] Full error:', error);
      console.error('[Contour] Error details:', JSON.stringify(error, null, 2));
      Utils.notify(`Contour error: ${error.message || error.toString()}`, 'error');
    } finally {
      // Clear progress interval if still running
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Restore button state
      const btn = document.getElementById('btnApplyContour');
      if (btn) {
        btn.disabled = false;
        if (originalButtonHTML) {
          btn.innerHTML = originalButtonHTML;
        }
      }
    }
  }

  renderContours(contourData, cacheKey = null) {
    const viewer = window.viewer;
    if (!viewer || !viewer.engine) {
      console.error('[Contour] No viewer or engine');
      return;
    }

    console.log('[Contour] Rendering contours:', contourData, 'cacheKey:', cacheKey);

    // Ensure the contour filter checkbox is checked so contours are visible
    const contourCheckbox = document.getElementById('contourFilterEnabled');
    if (contourCheckbox && !contourCheckbox.checked) {
      contourCheckbox.checked = true;
      const contourControls = document.getElementById('contourControls');
      if (contourControls) {
        contourControls.style.display = 'flex';
      }
    }

    // Remove existing contours from scene
    const existingFilter = this.activeFilters.get('contour-filter');
    if (existingFilter) {
      if (existingFilter.mesh) {
        viewer.engine.scene.remove(existingFilter.mesh);
        if (existingFilter.mesh.geometry) existingFilter.mesh.geometry.dispose();
        if (existingFilter.mesh.material) existingFilter.mesh.material.dispose();
      }
      // Remove labels from DOM
      if (existingFilter.labels && Array.isArray(existingFilter.labels)) {
        existingFilter.labels.forEach(label => {
          if (label.parentElement) {
            label.parentElement.removeChild(label);
          }
        });
      }
      console.log('[Contour] Removed old contour mesh and labels from scene');
    }
    this.activeFilters.delete('contour-filter');

    const {
      vertices,
      faces,
      normals,
      scalars,
      contour_values,
      field_name,
      num_contours
    } = contourData || {};

    if (!Array.isArray(vertices) || vertices.length === 0) {
      console.warn('Contour filter returned no vertices');
      Utils.notify('Contour result contained no geometry', 'warning');
      return;
    }

    // Create geometry
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    if (normals && normals.length > 0) {
      const normalsArray = new Float32Array(normals.flat());
      geometry.setAttribute('normal', new THREE.BufferAttribute(normalsArray, 3));
    } else {
      geometry.computeVertexNormals();
    }

    // Set indices
    if (Array.isArray(faces) && faces.length > 0) {
      const indices = [];
      faces.forEach(face => {
        if (!Array.isArray(face)) return;
        if (face.length === 3) {
          indices.push(face[0], face[1], face[2]);
        } else if (face.length === 4) {
          indices.push(face[0], face[1], face[2]);
          indices.push(face[0], face[2], face[3]);
        } else if (face.length > 3) {
          for (let i = 1; i < face.length - 1; i++) {
            indices.push(face[0], face[i], face[i + 1]);
          }
        }
      });
      if (indices.length > 0) {
        const indexArray = indices.length > 65535
          ? new Uint32Array(indices)
          : new Uint16Array(indices);
        geometry.setIndex(new THREE.BufferAttribute(indexArray, 1));
      }
    }

    // Add vertex colors if scalar data provided
    const scalarInfo = scalars && scalars[field_name];
    if (scalarInfo && Array.isArray(scalarInfo.data)) {
      const data = scalarInfo.data;
      const range = Array.isArray(scalarInfo.range) ? scalarInfo.range : null;
      const min = range ? range[0] : Math.min(...data);
      const max = range ? range[1] : Math.max(...data);
      const span = max - min;
      const preset = viewer.colormap?.preset || 'viridis';
      const colorBuffer = new Float32Array(data.length * 3);

      // Get solid color for main mesh colormap dropdown if using solid colormap
      const solidColorHex = Colormap.isSolidColor(preset) ? Colormap.getSolidColor('colormapPreset') : null;

      for (let i = 0; i < data.length; i++) {
        const value = data[i];
        const t = span > 0 ? (value - min) / span : 0.5;
        const color = Colormap.getColorThree(t, preset, solidColorHex);
        colorBuffer[i * 3] = color.r;
        colorBuffer[i * 3 + 1] = color.g;
        colorBuffer[i * 3 + 2] = color.b;
      }

      geometry.setAttribute('color', new THREE.BufferAttribute(colorBuffer, 3));
    }

    // Create material with colormap
    const preset = viewer.colormap?.preset || 'viridis';
    // Get solid color for main mesh colormap dropdown if using solid colormap
    const materialSolidColor = Colormap.isSolidColor(preset) ? Colormap.getSolidColor('colormapPreset') : null;
    const baseColor = Colormap.getColorThree(0.5, preset, materialSolidColor);
    const material = new THREE.MeshPhongMaterial({
      color: baseColor,
      vertexColors: !!geometry.getAttribute('color'),
      side: THREE.DoubleSide,
      flatShading: false,
      shininess: 30
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'contour-filter';

    // Add to scene
    viewer.engine.scene.add(mesh);

    // Create contour labels if values are provided
    const labels = [];
    if (Array.isArray(contour_values) && contour_values.length > 0) {
      console.log('[Contour] Creating labels for values:', contour_values);

      // Get or create label container
      let labelContainer = document.getElementById('contour-labels');
      if (!labelContainer) {
        labelContainer = document.createElement('div');
        labelContainer.id = 'contour-labels';
        labelContainer.style.position = 'absolute';
        labelContainer.style.top = '0';
        labelContainer.style.left = '0';
        labelContainer.style.width = '100%';
        labelContainer.style.height = '100%';
        labelContainer.style.pointerEvents = 'none';
        labelContainer.style.zIndex = '1000';
        viewer.engine.renderer.domElement.parentElement.appendChild(labelContainer);
      }

      // Sample points along contours for label placement
      // We'll place labels at regular intervals through the vertex array
      const labelInterval = Math.floor(vertices.length / (contour_values.length * 2));

      for (let i = 0; i < contour_values.length; i++) {
        const value = contour_values[i];
        // Place 2 labels per contour value at different positions
        for (let j = 0; j < 2; j++) {
          const vertexIndex = (i * 2 + j) * labelInterval;
          if (vertexIndex < vertices.length) {
            const position = vertices[vertexIndex];
            const label = this.createContourLabel(value, position);
            if (label) {
              labelContainer.appendChild(label);
              labels.push(label);
            }
          }
        }
      }

      console.log('[Contour] Created', labels.length, 'labels');

      // Update label positions initially
      this.updateContourLabelPositions(viewer);
    }

    // Track in activeFilters (include cache_key for collaborative session optimization)
    const filterEntry = {
      mesh: mesh,
      labels: labels,
      type: 'contour',
      field: field_name,
      contourValues: contour_values,
      numContours: num_contours
    };

    // Store cache_key for collaborative session cache lookup
    if (cacheKey) {
      filterEntry.cache_key = cacheKey;
      console.log('[Contour] Stored cache_key for collaboration:', cacheKey);
    }

    this.activeFilters.set('contour-filter', filterEntry);

    console.log('[Contour] Added to scene:', {
      numVertices: vertices.length,
      numFaces: faces?.length,
      fieldName: field_name,
      numContours: num_contours,
      numLabels: labels.length,
      visible: mesh.visible,
      position: mesh.position,
      sceneChildrenCount: viewer.engine.scene.children.length,
      cacheKey: cacheKey
    });
  }

  createContourLabel(value, position) {
    if (!position || position.length < 3) return null;

    // Create HTML label element (like annotations)
    const labelDiv = document.createElement('div');
    labelDiv.className = 'contour-label';
    labelDiv.textContent = value.toFixed(1);  // Use 1 decimal place for cleaner labels
    labelDiv.style.position = 'absolute';
    labelDiv.style.fontSize = '11px';
    labelDiv.style.color = 'white';
    labelDiv.style.fontFamily = 'Arial, sans-serif';
    labelDiv.style.padding = '2px 5px';
    labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.75)';
    labelDiv.style.borderRadius = '3px';
    labelDiv.style.pointerEvents = 'none';
    labelDiv.style.userSelect = 'none';
    labelDiv.style.whiteSpace = 'nowrap';
    labelDiv.style.transform = 'translate(-50%, -50%)';  // Center the label on its position
    labelDiv.style.fontWeight = 'bold';

    // Store 3D position as data attribute
    labelDiv.dataset.x = position[0];
    labelDiv.dataset.y = position[1];
    labelDiv.dataset.z = position[2];

    return labelDiv;
  }

  // Update contour label positions (called every frame)
  updateContourLabelPositions(viewer) {
    const filterEntry = this.activeFilters.get('contour-filter');
    if (!filterEntry || !filterEntry.labels || !viewer.engine.camera) return;

    const camera = viewer.engine.camera;
    const renderer = viewer.engine.renderer;
    const widthHalf = renderer.domElement.clientWidth / 2;
    const heightHalf = renderer.domElement.clientHeight / 2;

    filterEntry.labels.forEach(labelDiv => {
      const position = new THREE.Vector3(
        parseFloat(labelDiv.dataset.x),
        parseFloat(labelDiv.dataset.y),
        parseFloat(labelDiv.dataset.z)
      );

      // Project 3D position to screen coordinates
      const screenPos = position.clone().project(camera);

      labelDiv.style.left = `${(screenPos.x * widthHalf) + widthHalf}px`;
      labelDiv.style.top = `${-(screenPos.y * heightHalf) + heightHalf}px`;
    });
  }

  async applyStreamlinesFromUI() {
    console.log('[applyStreamlinesFromUI] ========== STARTING ==========');
    const viewer = window.viewer;
    console.log('[applyStreamlinesFromUI] viewer:', viewer);
    console.log('[applyStreamlinesFromUI] viewer.currentData:', viewer?.currentData);
    console.log('[applyStreamlinesFromUI] viewer.renderingMode:', viewer?.renderingMode);

    if (!viewer || !viewer.currentData) {
      console.warn('[applyStreamlinesFromUI] No data available for streamlines');
      return;
    }

    const defaults = this.getStreamlineDefaultParams(viewer.currentData);
    console.log('[applyStreamlinesFromUI] defaults:', defaults);
    const parseNumber = (id, fallback) => {
      const input = document.getElementById(id);
      if (!input) return fallback;
      const value = parseFloat(input.value);
      return Number.isFinite(value) ? value : fallback;
    };
    const parseIntValue = (id, fallback) => {
      const value = parseNumber(id, fallback);
      const rounded = Math.round(value);
      return Number.isFinite(rounded) ? rounded : fallback;
    };

    const seedTypeSelect = document.getElementById('streamlineSeedType');
    const seedType = seedTypeSelect ? seedTypeSelect.value : 'line';
    const maxLength = Math.max(parseNumber('streamlineMaxLength', defaults.maxLength), 1);
    const stepSize = Math.max(parseNumber('streamlineStepSize', defaults.stepSize), 0.00005);
    const colormapSelect = document.getElementById('streamlineColormap');
    const colormap = colormapSelect?.value || viewer.colormap?.preset || 'jet';
    const vectorFieldSelect = document.getElementById('streamlineVectorField');
    const variable = vectorFieldSelect?.value || this.getDefaultVectorField(viewer.currentData);
    const colorFieldSelect = document.getElementById('streamlineColorField');
    const colorField = colorFieldSelect?.value || '';  // Empty string means use vector field
    const previewCheckbox = document.getElementById('streamlinePreviewEnabled');
    const previewEnabled = previewCheckbox ? previewCheckbox.checked : false;
    const previewDotScale = Math.max(parseNumber('streamlinePreviewDotScale', defaults.previewDotScale ?? 0.0004), 0.0001);
    const basePreviewSpeed = Math.max(parseNumber('streamlinePreviewSpeed', defaults.previewSpeed ?? 1), 0);
    // Apply current direction to preview speed
    const previewSpeed = basePreviewSpeed * (this.particleDirection || 1);

    // Get render mode and its specific parameters
    const renderModeSelect = document.getElementById('streamlineRenderMode');
    const renderMode = renderModeSelect?.value || 'lines';

    const options = {
      id: 'streamline-filter',
      seedType,
      maxLength,
      stepSize,
      colormap,
      variable,
      colorField: colorField || undefined,  // Only include if non-empty
      previewEnabled,
      previewDotScale,
      previewSpeed,
      renderMode
    };

    // Add render mode specific parameters
    if (renderMode === 'tubes') {
      options.tubeRadius = parseNumber('streamlineTubeRadius', 0.01);
      options.tubeSides = parseIntValue('streamlineTubeSides', 8);
      const varyRadiusSelect = document.getElementById('streamlineVaryRadius');
      options.varyRadius = varyRadiusSelect?.value || 'none';
    } else if (renderMode === 'ribbons') {
      options.ribbonWidth = parseNumber('streamlineRibbonWidth', 0.02);
    } else if (renderMode === 'particles') {
      options.particleCount = parseIntValue('streamlineParticleCount', 100);
      options.particleSteps = parseIntValue('streamlineParticleSteps', 50);
    }

    if (seedType === 'point') {
      options.pointCenterX = parseNumber('streamlinePointCenterX', defaults.pointCenter[0]);
      options.pointCenterY = parseNumber('streamlinePointCenterY', defaults.pointCenter[1]);
      options.pointCenterZ = parseNumber('streamlinePointCenterZ', defaults.pointCenter[2]);
      options.pointRadius = Math.max(parseNumber('streamlinePointRadius', defaults.pointRadius), 0.001);
      options.pointCount = Math.max(1, parseIntValue('streamlinePointCount', defaults.pointCount));
      this.updateStoredStreamlineSettings(viewer.currentData, {
        pointRadius: options.pointRadius,
        pointCount: options.pointCount
      });
    } else if (seedType === 'line') {
      options.lineStartX = parseNumber('streamlineLineStartX', defaults.lineStart[0]);
      options.lineStartY = parseNumber('streamlineLineStartY', defaults.lineStart[1]);
      options.lineStartZ = parseNumber('streamlineLineStartZ', defaults.lineStart[2]);
      options.lineEndX = parseNumber('streamlineLineEndX', defaults.lineEnd[0]);
      options.lineEndY = parseNumber('streamlineLineEndY', defaults.lineEnd[1]);
      options.lineEndZ = parseNumber('streamlineLineEndZ', defaults.lineEnd[2]);

      console.log('[Streamlines Apply] Line seed parameters:', {
        startX: options.lineStartX,
        startY: options.lineStartY,
        startZ: options.lineStartZ,
        endX: options.lineEndX,
        endY: options.lineEndY,
        endZ: options.lineEndZ
      });

      const lineResInput = document.getElementById('streamlineLineResolution');
      const lineResValue = lineResInput ? lineResInput.value : 'NOT FOUND';
      console.log('[Streamlines Apply] Reading lineResolution input:', lineResValue);
      options.lineResolution = Math.max(10, parseIntValue('streamlineLineResolution', defaults.lineResolution));
      console.log('[Streamlines Apply] Final lineResolution value:', options.lineResolution);
    } else if (seedType === 'plane') {
      options.planeOriginX = parseNumber('streamlinePlaneOriginX', defaults.planeOrigin[0]);
      options.planeOriginY = parseNumber('streamlinePlaneOriginY', defaults.planeOrigin[1]);
      options.planeOriginZ = parseNumber('streamlinePlaneOriginZ', defaults.planeOrigin[2]);
      options.planeNormalX = parseNumber('streamlinePlaneNormalX', defaults.planeNormal[0]);
      options.planeNormalY = parseNumber('streamlinePlaneNormalY', defaults.planeNormal[1]);
      options.planeNormalZ = parseNumber('streamlinePlaneNormalZ', defaults.planeNormal[2]);
      options.planeWidth = Math.max(parseNumber('streamlinePlaneWidth', defaults.planeWidth), 0.01);
      options.planeHeight = Math.max(parseNumber('streamlinePlaneHeight', defaults.planeHeight), 0.01);
      options.planeResolutionU = Math.max(2, parseIntValue('streamlinePlaneResolutionU', defaults.planeResolutionU));
      options.planeResolutionV = Math.max(2, parseIntValue('streamlinePlaneResolutionV', defaults.planeResolutionV));
    }

    const existing = this.activeFilters.get(options.id);
    if (existing) {
      const { id, ...updatePayload } = options;
      console.log('[Streamlines Apply] Updating existing streamlines');
      console.log('[Streamlines Apply] Update payload:', updatePayload);
      return await this.updateFilter(id, viewer.currentData, updatePayload);
    }

    console.log('[applyStreamlinesFromUI] Creating new streamlines with options:', options);
    return await this.createStreamlines(viewer.currentData, options);
  }

  // ========================================================================
  // GLYPH FILTER - VECTOR VISUALIZATION
  // ========================================================================

  async applyGlyphFromUI(options = {}) {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    // Extract cache_key if provided (for collaborative session cache lookup)
    const providedCacheKey = options.cache_key || null;

    const vectorField = document.getElementById('glyphVectorField')?.value;
    if (!vectorField) {
      Utils.notify('Please select a vector field', 'error');
      return;
    }

    // Remove existing glyph filter first to prevent overlapping
    this.removeFilter('glyph-filter');

    // Get color field - prefer options (for collaboration sync) over dropdown
    // Empty string means use vector magnitude (default)
    const colorField = options.colorField !== undefined
      ? options.colorField
      : (document.getElementById('glyphColorField')?.value || '');

    console.log('[Glyph] Selected vector field (direction):', vectorField);
    console.log('[Glyph] Selected color field:', colorField || '(Vector Magnitude)');

    const glyphType = document.getElementById('glyphType')?.value || 'arrow';
    const scaleMode = document.getElementById('glyphScaleMode')?.value || 'vector_magnitude';
    const scaleFactor = parseFloat(document.getElementById('glyphScaleFactor')?.value) || 1.0;
    const skipFactor = parseInt(document.getElementById('glyphSkipFactor')?.value) || 5;
    const colorMode = colorField ? 'field' : 'magnitude';  // Automatically set based on color field selection

    let progressInterval = null;
    let originalButtonHTML = null;
    try {
      Utils.notify('Computing glyphs...', 'info');

      // Update button state with progress bar
      const btn = document.getElementById('btnApplyGlyph');
      if (btn) {
        btn.disabled = true;
        originalButtonHTML = btn.innerHTML;
        btn.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="flex: 1; height: 8px; background: rgba(255,255,255,0.2); border-radius: 4px; overflow: hidden;">
              <div id="glyphProgressBar" style="height: 100%; width: 0%; background: linear-gradient(90deg, #4CAF50, #8BC34A); transition: width 0.3s ease;"></div>
            </div>
            <span id="glyphProgressText" style="font-size: 11px;">0%</span>
          </div>
        `;
      }

      // Start simulated progress indicator
      let progress = 0;
      progressInterval = setInterval(() => {
        if (progress < 90) {
          progress += Math.random() * 10; // Random increments for smooth animation
          if (progress > 90) progress = 90;
          const progressBar = document.getElementById('glyphProgressBar');
          const progressText = document.getElementById('glyphProgressText');
          if (progressBar) progressBar.style.width = `${progress}%`;
          if (progressText) progressText.textContent = `${Math.round(progress)}%`;
        }
      }, 500); // Update every 500ms

      // Build request data
      const requestData = {
        case_path: casePath,
        vector_field: vectorField,
        color_field: colorField || null,  // Separate field for coloring (null = use vector magnitude)
        glyph_type: glyphType,
        scale_mode: scaleMode,
        scale_factor: scaleFactor,
        skip_factor: skipFactor,
        color_mode: colorMode,
        time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue
        // Note: mesh_parts intentionally NOT passed - glyphs are computed on full mesh
        // and mesh visibility is handled on the frontend for consistent caching in collaboration
      };

      // Add cache_key if provided (for collaborative session cache lookup)
      if (providedCacheKey) {
        requestData.cache_key = providedCacheKey;
        console.log('[Glyph] Using cache_key for collaborative cache lookup:', providedCacheKey);
      }

      const response = await fetch('/api/filters/glyph', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify(requestData)
      });

      // Clear progress interval and show 100%
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      const progressBar = document.getElementById('glyphProgressBar');
      const progressText = document.getElementById('glyphProgressText');
      if (progressBar) progressBar.style.width = '100%';
      if (progressText) progressText.textContent = '100%';

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Glyph filter failed');
      }

      const result = await response.json();

      console.log('[Glyph] Backend response:', {
        numGlyphs: result.data?.num_glyphs,
        magnitudeRange: result.data?.magnitude_range,
        scaleFactor: result.data?.scale_factor,
        vectorField: vectorField,
        cache_key: result.cache_key,
        from_cache: result.from_cache
      });

      // Render glyphs using Three.js (pass cache_key and original colorField for collaboration sync)
      this.renderGlyphs(result.data, glyphType, colorMode, result.cache_key, colorField);

      Utils.notify(`Created ${result.data.num_glyphs} glyphs for field: ${vectorField}`, 'success');

      // Notify view lock manager of visualization change
      if (this.viewer?.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    } catch (error) {
      console.error('Glyph filter error:', error);
      Utils.notify(`Glyph error: ${error.message}`, 'error');
    } finally {
      // Clear progress interval if still running
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Restore button state
      const btn = document.getElementById('btnApplyGlyph');
      if (btn) {
        btn.disabled = false;
        if (originalButtonHTML) {
          btn.innerHTML = originalButtonHTML;
        }
      }
    }
  }

  renderGlyphs(glyphData, glyphType, colorMode, cacheKey = null, originalColorField = null) {
    const viewer = window.viewer;
    if (!viewer || !viewer.engine) {
      console.error('[Glyph] No viewer or engine');
      return;
    }

    // Get glyph-specific colormap from UI
    const glyphColormapSelect = document.getElementById('glyphColormap');
    const colormapPreset = glyphColormapSelect?.value || 'jet';

    // Use originalColorField (what user selected) for cache consistency, not glyphData.color_field (backend processed)
    const colorFieldForSync = originalColorField !== null
      ? originalColorField
      : (document.getElementById('glyphColorField')?.value || '');

    console.log('[Glyph] Rendering glyphs:', {
      glyphType,
      colorMode,
      colormap: colormapPreset,
      colorField: glyphData.color_field,
      colorFieldForSync: colorFieldForSync,
      dataKeys: Object.keys(glyphData)
    });

    // Remove existing glyph filter from scene and activeFilters
    const existingFilter = this.activeFilters.get('glyph-filter');
    if (existingFilter && existingFilter.mesh) {
      viewer.engine.scene.remove(existingFilter.mesh);
      if (existingFilter.mesh.geometry) existingFilter.mesh.geometry.dispose();
      if (existingFilter.mesh.material) existingFilter.mesh.material.dispose();
      console.log('[Glyph] Removed old glyph mesh from scene');
    }
    this.activeFilters.delete('glyph-filter');

    const { positions, vectors, magnitudes, magnitude_range, scale_factor, scale_mode } = glyphData;

    console.log('[Glyph] Data:', {
      numPositions: positions?.length,
      numVectors: vectors?.length,
      numMagnitudes: magnitudes?.length,
      magnitudeRange: magnitude_range,
      scaleFactor: scale_factor,
      scaleMode: scale_mode,
      colorField: glyphData.color_field,
      // Sample first few magnitude values
      sampleMagnitudes: magnitudes?.slice(0, 10)
    });

    // Create instanced mesh for performance
    const count = positions.length;
    let geometry;

    // Create glyph geometry based on type
    switch (glyphType) {
      case 'arrow':
        geometry = this.createArrowGeometry();
        break;
      case 'cone':
        geometry = new THREE.ConeGeometry(0.1, 0.3, 8);
        break;
      case 'sphere':
        geometry = new THREE.SphereGeometry(0.1, 8, 8);
        break;
      case 'cylinder':
        geometry = new THREE.CylinderGeometry(0.05, 0.05, 0.3, 8);
        break;
      case 'box':
        geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
        break;
      default:
        geometry = this.createArrowGeometry();
    }

    const material = new THREE.MeshPhongMaterial({
      vertexColors: true,
      shininess: 30
    });

    const instancedMesh = new THREE.InstancedMesh(geometry, material, count);

    // Setup colors using the glyph-specific colormap
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const pos = positions[i];
      const vec = vectors[i];
      const mag = magnitudes[i];

      // Position
      const matrix = new THREE.Matrix4();
      matrix.makeTranslation(pos[0], pos[1], pos[2]);

      // Orientation (align with vector)
      if (glyphType === 'arrow' || glyphType === 'cone' || glyphType === 'cylinder') {
        const direction = new THREE.Vector3(vec[0], vec[1], vec[2]).normalize();
        const up = new THREE.Vector3(0, 1, 0);
        const quaternion = new THREE.Quaternion().setFromUnitVectors(up, direction);
        const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(quaternion);
        matrix.multiply(rotationMatrix);
      }

      // Scale - depends on scale_mode
      let scale;
      if (scale_mode === 'off') {
        // Uniform scaling - all glyphs same size
        scale = scale_factor;
      } else {
        // Scale by magnitude (vector_magnitude or scalar mode)
        scale = mag * scale_factor;
      }
      const scaleMatrix = new THREE.Matrix4().makeScale(scale, scale, scale);
      matrix.multiply(scaleMatrix);

      instancedMesh.setMatrixAt(i, matrix);
    }

    // Get solid color for glyph colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormapPreset) ? Colormap.getSolidColor('glyphColormap') : null;

    // Apply colors in separate loop
    for (let i = 0; i < count; i++) {
      const mag = magnitudes[i] ?? 1.0;
      // Color based on magnitude/color value using glyph-specific colormap
      const normalizedMag = (mag - magnitude_range[0]) / (magnitude_range[1] - magnitude_range[0]);
      const color = Colormap.getColor(normalizedMag, colormapPreset, solidColorHex);
      // Colormap.getColor returns [r, g, b] in 0-255 range, need to normalize to 0-1
      colors[i * 3] = color[0] / 255;
      colors[i * 3 + 1] = color[1] / 255;
      colors[i * 3 + 2] = color[2] / 255;
    }

    // Set colors
    geometry.setAttribute('color', new THREE.InstancedBufferAttribute(colors, 3));

    instancedMesh.name = 'glyph-filter';
    instancedMesh.instanceMatrix.needsUpdate = true;

    viewer.engine.scene.add(instancedMesh);

    // Store glyph data for re-rendering when colormap changes
    // Use colorFieldForSync (original user selection) for consistent cache key matching in collaboration
    this.activeFilters.set('glyph-filter', {
      mesh: instancedMesh,
      type: 'glyph',
      glyphData: glyphData,  // Store for colormap updates
      options: {
        vectorField: glyphData.vector_field || document.getElementById('glyphVectorField')?.value,
        colorField: colorFieldForSync,  // Original user selection, not backend processed name
        glyphType: glyphType,
        scaleFactor: glyphData.scale_factor || parseFloat(document.getElementById('glyphScaleFactor')?.value) || 1.0,
        scaleMode: glyphData.scale_mode || document.getElementById('glyphScaleMode')?.value || 'vector_magnitude',
        skipFactor: parseInt(document.getElementById('glyphSkipFactor')?.value) || 5,
        colorMode: colorMode,
        colormap: colormapPreset,
        cache_key: cacheKey  // For collaboration sync - allows followers to use cached result
      }
    });

    console.log('[Glyph] Added to scene:', {
      count,
      glyphType,
      colormap: colormapPreset,
      colorField: glyphData.color_field,
      visible: instancedMesh.visible,
      position: instancedMesh.position,
      sceneChildrenCount: viewer.engine.scene.children.length
    });

    // Update glyph legend
    if (viewer.updateGlyphLegend) {
      viewer.updateGlyphLegend('glyph-filter', {
        colorField: colorFieldForSync || glyphData.color_field,
        scalarRange: { min: magnitude_range[0], max: magnitude_range[1] },
        colormap: colormapPreset
      });
    }
  }

  /**
   * Update glyph colors when colormap changes (without re-fetching data)
   */
  updateGlyphColors() {
    const viewer = window.viewer;
    const glyphFilter = this.activeFilters.get('glyph-filter');

    if (!viewer || !viewer.engine || !glyphFilter || !glyphFilter.glyphData) {
      return;
    }

    const glyphColormapSelect = document.getElementById('glyphColormap');
    const colormapPreset = glyphColormapSelect?.value || 'jet';
    const { magnitudes, magnitude_range } = glyphFilter.glyphData;
    const count = magnitudes.length;

    console.log('[Glyph] Updating colors with colormap:', colormapPreset);

    // Get solid color for glyph colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormapPreset) ? Colormap.getSolidColor('glyphColormap') : null;

    // Update colors
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const mag = magnitudes[i];
      const normalizedMag = (mag - magnitude_range[0]) / (magnitude_range[1] - magnitude_range[0]);
      const color = Colormap.getColor(normalizedMag, colormapPreset, solidColorHex);
      colors[i * 3] = color[0] / 255;
      colors[i * 3 + 1] = color[1] / 255;
      colors[i * 3 + 2] = color[2] / 255;
    }

    // Update the geometry color attribute
    const geometry = glyphFilter.mesh.geometry;
    const colorAttr = geometry.getAttribute('color');
    if (colorAttr) {
      colorAttr.array.set(colors);
      colorAttr.needsUpdate = true;
    } else {
      geometry.setAttribute('color', new THREE.InstancedBufferAttribute(colors, 3));
    }

    // Update stored options
    glyphFilter.options.colormap = colormapPreset;

    // Update the glyph legend with new colormap
    if (viewer && viewer.updateGlyphLegend) {
      viewer.updateGlyphLegend('glyph-filter', {
        colorField: glyphFilter.options?.colorField || glyphFilter.glyphData?.color_field,
        scalarRange: { min: magnitude_range[0], max: magnitude_range[1] },
        colormap: colormapPreset
      });
    }

    console.log('[Glyph] Colors updated');
  }

  /**
   * Update streamline colors locally without recomputing from backend.
   * Much faster than regenerating streamlines!
   */
  updateStreamlineColors() {
    const viewer = window.viewer;

    // Find all streamline filters (could be 'streamlines' or other IDs)
    const streamlineFilters = [];
    for (const [id, filter] of this.activeFilters.entries()) {
      if (filter.type === 'streamlines' && id !== 'slice-streamlines') {
        streamlineFilters.push({ id, filter });
      }
    }

    if (streamlineFilters.length === 0) {
      console.log('[Streamlines] No streamlines to recolor');
      return;
    }

    // Get the new colormap from UI
    const colormapSelect = document.getElementById('streamlineColormap');
    const newColormap = colormapSelect?.value || 'jet';

    console.log(`[Streamlines] Updating colors with colormap: ${newColormap}`);

    // Update each streamline filter
    for (const { id, filter } of streamlineFilters) {
      // Update the stored options
      if (filter.options) {
        filter.options.colormap = newColormap;
      }

      // Refresh the visuals
      this.refreshStreamlineVisuals(id, filter);

      // Update the streamline legend
      if (viewer && viewer.updateStreamlineLegend) {
        viewer.updateStreamlineLegend(id, {
          colorField: filter.options?.colorField || filter.options?.scalarField,
          scalarRange: filter.options?.scalarRange,
          colormap: newColormap
        });
      }
    }

    console.log('[Streamlines] Colors updated');
  }

  createArrowGeometry() {
    // Create simple cone arrow (points upward in +Y direction)
    const geometry = new THREE.ConeGeometry(0.05, 1.0, 8);
    // Translate so base is at origin and tip points up
    geometry.translate(0, 0.5, 0);
    return geometry;
  }

  // ========================================================================
  // SURFACE LIC FILTER - FLOW TEXTURE VISUALIZATION
  // ========================================================================

  async applySurfaceLICFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    // Use the reusable progress bar utility
    await this.applyWithProgressBar('btnApplySurfaceLIC', async () => {
      // Get UI parameters
      const vectorField = document.getElementById('surfaceLICVectorField')?.value || 'U';
      const kernelLength = parseInt(document.getElementById('surfaceLICKernelLength')?.value) || 32;
      const noiseResolution = parseInt(document.getElementById('surfaceLICNoiseResolution')?.value) || 512;
      const contrastEnhance = parseFloat(document.getElementById('surfaceLICContrast')?.value) || 1.5;
      const colorMode = document.getElementById('surfaceLICColorMode')?.value || 'magnitude';
      const colormap = document.getElementById('surfaceLICColormap')?.value || 'jet';
      const opacity = parseFloat(document.getElementById('surfaceLICOpacity')?.value) || 1.0;

      const params = {
        case_path: casePath,
        vector_field: vectorField,
        kernel_length: kernelLength,
        noise_resolution: noiseResolution,
        contrast_enhance: contrastEnhance,
        color_mode: colorMode,
        time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
        mesh_parts: this.getVisibleMeshParts()
      };

      const response = await fetch('/api/filters/surface-lic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Surface LIC filter failed');
      }

      const result = await response.json();

      // Dispose of existing renderer if any
      if (this.surfaceLICRenderer) {
        this.surfaceLICRenderer.dispose();
      }

      // Create Surface LIC renderer
      if (typeof SurfaceLICRenderer === 'undefined') {
        throw new Error('SurfaceLICRenderer not loaded. Please include surface-lic-renderer.js');
      }

      this.surfaceLICRenderer = new SurfaceLICRenderer(viewer.engine.scene, colormap);
      this.surfaceLICRenderer.createLIC(result.data, {
        opacity: opacity,
        contrastEnhance: contrastEnhance
      });

      // Check if animation should be enabled
      const animate = document.getElementById('surfaceLICAnimate')?.checked || false;
      if (animate) {
        const speed = parseFloat(document.getElementById('surfaceLICAnimationSpeed')?.value || 1.0);
        this.surfaceLICRenderer.startAnimation(speed);
      }

      // Store options for collaboration sync
      const storedOptions = {
        vectorField,
        kernelLength,
        noiseResolution,
        contrastEnhance,
        colorMode,
        colormap,
        opacity,
        animate,
        animationSpeed: animate ? parseFloat(document.getElementById('surfaceLICAnimationSpeed')?.value || 1.0) : 1.0
      };

      // Register in active filters for collaboration
      this.activeFilters.set('surface-lic-filter', {
        type: 'surface_lic',
        renderer: this.surfaceLICRenderer,
        options: storedOptions
      });

      // Notify view lock manager of visualization change
      if (window.viewLockManager) {
        window.viewLockManager.onVisualizationChange();
      }
    }, {
      startMessage: 'Computing Surface LIC...',
      successMessage: 'Surface LIC applied successfully',
      errorPrefix: 'Surface LIC error'
    });
  }

  updateSurfaceLICParameters() {
    if (!this.surfaceLICRenderer) return;

    const opacity = parseFloat(document.getElementById('surfaceLICOpacity')?.value) || 1.0;
    const colormap = document.getElementById('surfaceLICColormap')?.value || 'jet';

    this.surfaceLICRenderer.updateParameters({
      opacity: opacity,
      colormap: colormap
    });
  }

  // ========================================================================
  // VOLUME LIC FILTER
  // ========================================================================

  async applyVolumeLICFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    // Use the reusable progress bar utility
    await this.applyWithProgressBar('btnApplyVolumeLIC', async () => {
      // Get UI parameters
      const vectorField = document.getElementById('volumeLICVectorField')?.value || 'U';
      const resolution = parseInt(document.getElementById('volumeLICResolution')?.value) || 64;
      const kernelLength = parseInt(document.getElementById('volumeLICKernelLength')?.value) || 20;
      const stepSize = parseFloat(document.getElementById('volumeLICStepSize')?.value) || 0.5;
      const contrastEnhance = parseFloat(document.getElementById('volumeLICContrast')?.value) || 1.5;
      const colormap = document.getElementById('volumeLICColormap')?.value || 'jet';
      const opacity = parseFloat(document.getElementById('volumeLICOpacity')?.value) || 0.5;
      const threshold = parseFloat(document.getElementById('volumeLICThreshold')?.value) || 0.1;
      const steps = parseInt(document.getElementById('volumeLICSteps')?.value) || 128;
      const brightness = parseFloat(document.getElementById('volumeLICBrightness')?.value) || 1.0;
      const licWeight = parseFloat(document.getElementById('volumeLICWeight')?.value) || 0.5;
      const animationSpeed = parseFloat(document.getElementById('volumeLICAnimationSpeed')?.value) || 1.0;
      const animated = document.getElementById('volumeLICAnimated')?.checked ?? true;

      const params = {
        case_path: casePath,
        vector_field: vectorField,
        resolution: resolution,
        kernel_length: kernelLength,
        step_size: stepSize,
        contrast_enhance: contrastEnhance,
        time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
        mesh_parts: this.getVisibleMeshParts()
      };

      const response = await fetch('/api/filters/volume-lic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Volume LIC filter failed');
      }

      const result = await response.json();

      // Dispose of existing renderer if any
      if (this.volumeLICRenderer) {
        this.volumeLICRenderer.dispose();
      }

      // Create Volume LIC renderer
      if (typeof VolumeLICRenderer === 'undefined') {
        throw new Error('VolumeLICRenderer not loaded. Please include volume-lic-renderer.js');
      }

      this.volumeLICRenderer = new VolumeLICRenderer(viewer.engine.scene, colormap);
      this.volumeLICRenderer.animated = animated;
      this.volumeLICRenderer.animationSpeed = animationSpeed;
      this.volumeLICRenderer.createVolume(result.data, {
        opacity: opacity,
        threshold: threshold,
        steps: steps,
        brightness: brightness,
        licWeight: licWeight
      });

      // Store reference to viewer for camera updates
      this.volumeLICViewer = viewer;

      // Store options for collaboration sync
      const storedOptions = {
        vectorField,
        resolution,
        kernelLength,
        stepSize,
        contrastEnhance,
        colormap,
        opacity,
        threshold,
        steps,
        brightness,
        licWeight,
        animationSpeed,
        animated
      };

      // Register in active filters for collaboration
      this.activeFilters.set('volume-lic-filter', {
        type: 'volume_lic',
        renderer: this.volumeLICRenderer,
        options: storedOptions
      });

      // Notify view lock manager of visualization change
      if (window.viewLockManager) {
        window.viewLockManager.onVisualizationChange();
      }
    }, {
      startMessage: 'Computing Volume LIC...',
      successMessage: 'Volume LIC applied successfully',
      errorPrefix: 'Volume LIC error'
    });
  }

  updateVolumeLICCamera() {
    if (this.volumeLICRenderer && this.volumeLICViewer?.engine?.camera) {
      this.volumeLICRenderer.updateCamera(this.volumeLICViewer.engine.camera);
    }
  }

  // ========================================================================
  // CALCULATOR FILTER - CUSTOM FIELD COMPUTATION
  // ========================================================================

  async applyCalculatorFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    const expression = document.getElementById('calculatorExpression')?.value?.trim();
    const resultName = document.getElementById('calculatorResultName')?.value?.trim();

    if (!expression) {
      Utils.notify('Please enter an expression', 'error');
      return;
    }

    if (!resultName) {
      Utils.notify('Please enter a result field name', 'error');
      return;
    }

    try {
      Utils.notify('Computing field...', 'info');

      const response = await fetch('/api/filters/calculator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify({
          case_path: casePath,
          expression: expression,
          result_name: resultName,
          time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
          mesh_parts: this.getVisibleMeshParts()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Calculator failed');
      }

      const result = await response.json();

      // Add computed field to available fields
      if (result.data && result.data.result) {
        viewer.currentData.fields[resultName] = result.data.result;

        // Update field selectors
        this.updateAvailableFields(viewer.currentData.fields);

        Utils.notify(`Computed field '${resultName}' (min: ${result.data.statistics.min.toFixed(3)}, max: ${result.data.statistics.max.toFixed(3)})`, 'success');
      }
    } catch (error) {
      console.error('Calculator error:', error);
      Utils.notify(`Calculator error: ${error.message}`, 'error');
    }
  }

  // ========================================================================
  // GRADIENT FILTER - DERIVATIVES
  // ========================================================================

  async applyGradientFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    const fieldName = document.getElementById('gradientField')?.value;
    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    const computeDivergence = document.getElementById('gradientComputeDivergence')?.checked || false;
    const computeVorticity = document.getElementById('gradientComputeVorticity')?.checked || false;
    const computeQCriterion = document.getElementById('gradientComputeQCriterion')?.checked || false;

    if (!computeDivergence && !computeVorticity && !computeQCriterion) {
      Utils.notify('Please select at least one derivative to compute', 'error');
      return;
    }

    let progressInterval = null;
    let originalButtonHTML = null;
    try {
      Utils.notify('Computing derivatives...', 'info');

      // Update button state with progress bar
      const btn = document.getElementById('btnApplyGradient');
      if (btn) {
        btn.disabled = true;
        originalButtonHTML = btn.innerHTML;
        btn.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="flex: 1; height: 8px; background: rgba(255,255,255,0.2); border-radius: 4px; overflow: hidden;">
              <div id="gradientProgressBar" style="height: 100%; width: 0%; background: linear-gradient(90deg, #4CAF50, #8BC34A); transition: width 0.3s ease;"></div>
            </div>
            <span id="gradientProgressText" style="font-size: 11px;">0%</span>
          </div>
        `;
      }

      // Start simulated progress indicator
      let progress = 0;
      progressInterval = setInterval(() => {
        if (progress < 90) {
          progress += Math.random() * 10; // Random increments for smooth animation
          if (progress > 90) progress = 90;
          const progressBar = document.getElementById('gradientProgressBar');
          const progressText = document.getElementById('gradientProgressText');
          if (progressBar) progressBar.style.width = `${progress}%`;
          if (progressText) progressText.textContent = `${Math.round(progress)}%`;
        }
      }, 500); // Update every 500ms

      const response = await fetch('/api/filters/gradient', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify({
          case_path: casePath,
          field_name: fieldName,
          compute_divergence: computeDivergence,
          compute_vorticity: computeVorticity,
          compute_q_criterion: computeQCriterion,
          time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
          mesh_parts: this.getVisibleMeshParts()
        })
      });

      // Clear progress interval and show 100%
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      const progressBar = document.getElementById('gradientProgressBar');
      const progressText = document.getElementById('gradientProgressText');
      if (progressBar) progressBar.style.width = '100%';
      if (progressText) progressText.textContent = '100%';

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.detail || 'Gradient filter failed');
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error(`Gradient filter failed (HTTP ${response.status})`);
        }
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Expected JSON but got:', responseText.substring(0, 500));
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      // Add computed fields using backend-provided names
      const messages = [];
      const newFieldNames = [];
      const isBackendRendering = viewer.renderingMode === 'geometry';

      if (result.data.divergence) {
        const name = result.data.divergence.name;
        // Only add data to fields if we have it (frontend rendering mode)
        if (result.data.divergence.data && viewer.currentData && viewer.currentData.fields) {
          viewer.currentData.fields[name] = result.data.divergence.data;
        }
        newFieldNames.push(name);
        const stats = result.data.divergence.statistics;
        if (stats) {
          messages.push(`${name} (range: ${stats.min.toExponential(2)} to ${stats.max.toExponential(2)})`);
        } else {
          messages.push(name);
        }
      }
      if (result.data.vorticity) {
        const name = result.data.vorticity.name + '_magnitude';
        // Only add data to fields if we have it (frontend rendering mode)
        if (result.data.vorticity.magnitudes && viewer.currentData && viewer.currentData.fields) {
          viewer.currentData.fields[name] = result.data.vorticity.magnitudes;
        }
        newFieldNames.push(name);
        const stats = result.data.vorticity.statistics;
        if (stats) {
          messages.push(`${name} (range: ${stats.magnitude_min.toExponential(2)} to ${stats.magnitude_max.toExponential(2)})`);
        } else {
          messages.push(name);
        }
      }
      if (result.data.q_criterion) {
        const name = result.data.q_criterion.name;
        // Only add data to fields if we have it (frontend rendering mode)
        if (result.data.q_criterion.data && viewer.currentData && viewer.currentData.fields) {
          viewer.currentData.fields[name] = result.data.q_criterion.data;
        }
        newFieldNames.push(name);
        const stats = result.data.q_criterion.statistics;
        if (stats) {
          messages.push(`${name} (${(stats.positive_fraction * 100).toFixed(1)}% positive vortices)`);
        } else {
          messages.push(name);
        }
      }

      console.log('[Derivatives] Added fields:', newFieldNames);
      console.log('[Derivatives] viewer.currentData.fields keys:', Object.keys(viewer.currentData.fields));

      // Update viewer's available fields list
      if (viewer.availableFields && Array.isArray(viewer.availableFields)) {
        newFieldNames.forEach(name => {
          if (!viewer.availableFields.includes(name)) {
            viewer.availableFields.push(name);
            console.log('[Derivatives] Added to availableFields:', name);
          }
        });
      }

      // Update all field selectors (main colormap dropdown and filter dropdowns)
      this.updateAvailableFields(viewer.currentData.fields);
      viewer.updateFieldControls(viewer.currentData.fields, { force: true });

      console.log('[Derivatives] After update - availableFields:', viewer.availableFields);

      // If backend wrote fields to OpenFOAM case, reload geometry to pick them up
      if (result.needs_reload && result.written_fields && result.written_fields.length > 0) {
        const timeInfo = result.time_dir ? ` at time ${result.time_dir}` : '';
        console.log('[Derivatives] Reloading geometry to pick up written fields:', result.written_fields, 'at time:', result.time_dir);
        Utils.notify(`✓ Computed and saved${timeInfo}: ${messages.join(', ')}. Reloading geometry...`, 'info', 3000);

        // Reload geometry with current parameters
        try {
          await viewer.loadGeometry(
            viewer.geometryParams.case_path,
            viewer.geometryParams.time || null,
            viewer.geometryParams
          );
          Utils.notify(`✓ Geometry reloaded with new derivative fields: ${result.written_fields.join(', ')}`, 'success', 5000);
        } catch (reloadError) {
          console.error('[Derivatives] Failed to reload geometry:', reloadError);
          Utils.notify(`Computed fields but failed to reload geometry: ${reloadError.message}`, 'warning', 5000);
        }
      } else {
        // Show appropriate success message based on rendering mode
        if (isBackendRendering) {
          Utils.notify(`✓ Computed: ${messages.join(', ')}. Note: For OpenFOAM cases, derivatives are computed but not directly visualized. Statistics shown above.`, 'success', 8000);
        } else {
          Utils.notify(`Computed: ${messages.join(', ')}. Select from the Colormap → Variable dropdown to visualize.`, 'success');
        }
      }
    } catch (error) {
      console.error('Gradient filter error:', error);
      Utils.notify(`Gradient error: ${error.message}`, 'error');
    } finally {
      // Clear progress interval if still running
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Restore button state
      const btn = document.getElementById('btnApplyGradient');
      if (btn) {
        btn.disabled = false;
        if (originalButtonHTML) {
          btn.innerHTML = originalButtonHTML;
        }
      }
    }
  }

  // ========================================================================
  // EXTRACT SELECTION FILTER
  // ========================================================================

  updateSelectionTypeUI() {
    const selectionType = document.getElementById('selectionType')?.value;

    // Hide all type-specific controls
    document.getElementById('selectionFrustumControls').style.display = 'none';
    document.getElementById('selectionPolygonControls').style.display = 'none';
    document.getElementById('selectionThresholdControls').style.display = 'none';

    // Show the appropriate controls
    if (selectionType === 'frustum') {
      document.getElementById('selectionFrustumControls').style.display = 'block';
    } else if (selectionType === 'polygon') {
      document.getElementById('selectionPolygonControls').style.display = 'block';
    } else if (selectionType === 'threshold') {
      document.getElementById('selectionThresholdControls').style.display = 'block';
    }

    // Clear any active selection
    this.clearSelectionUI();
  }

  startRubberBandSelection() {
    const viewer = window.viewer;
    if (!viewer || !viewer.renderingEngine) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    Utils.notify('Click and drag on canvas to select region', 'info');

    this.selectionState.active = true;
    this.selectionState.type = 'frustum';
    this.selectionState.points = [];
    this.selectionState.frustumRect = null;

    const canvas = viewer.renderingEngine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();

    let startX, startY;
    let selectionDiv = null;

    const onMouseDown = (e) => {
      startX = e.clientX - rect.left;
      startY = e.clientY - rect.top;

      // Create selection rectangle overlay
      selectionDiv = document.createElement('div');
      selectionDiv.style.position = 'absolute';
      selectionDiv.style.border = '2px dashed #00ff00';
      selectionDiv.style.backgroundColor = 'rgba(0, 255, 0, 0.1)';
      selectionDiv.style.pointerEvents = 'none';
      selectionDiv.style.zIndex = '1000';
      canvas.parentElement.appendChild(selectionDiv);
    };

    const onMouseMove = (e) => {
      if (!selectionDiv) return;

      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      selectionDiv.style.left = `${left}px`;
      selectionDiv.style.top = `${top}px`;
      selectionDiv.style.width = `${width}px`;
      selectionDiv.style.height = `${height}px`;
    };

    const onMouseUp = (e) => {
      if (!selectionDiv) return;

      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;

      // Store frustum rectangle in normalized coordinates [-1, 1]
      this.selectionState.frustumRect = {
        x1: (startX / canvas.width) * 2 - 1,
        y1: 1 - (startY / canvas.height) * 2,
        x2: (endX / canvas.width) * 2 - 1,
        y2: 1 - (endY / canvas.height) * 2
      };

      // Clean up
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);

      // Show clear button
      document.getElementById('btnClearSelection').style.display = 'inline-block';

      Utils.notify('Selection captured. Click "Extract Selection" to apply.', 'success');
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);

    // Hide start button, show clear button
    document.getElementById('btnStartRubberBand').style.display = 'none';
  }

  startPolygonSelection() {
    const viewer = window.viewer;
    if (!viewer || !viewer.renderingEngine) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    Utils.notify('Click to add points. Double-click to close polygon.', 'info');

    this.selectionState.active = true;
    this.selectionState.type = 'polygon';
    this.selectionState.points = [];

    const canvas = viewer.renderingEngine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();

    // Create SVG overlay for polygon visualization
    let svg = document.getElementById('polygonSelectionSVG');
    if (!svg) {
      svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.id = 'polygonSelectionSVG';
      svg.style.position = 'absolute';
      svg.style.top = '0';
      svg.style.left = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '1000';
      canvas.parentElement.appendChild(svg);
    }

    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    polygon.setAttribute('fill', 'rgba(0, 255, 0, 0.1)');
    polygon.setAttribute('stroke', '#00ff00');
    polygon.setAttribute('stroke-width', '2');
    polygon.setAttribute('stroke-dasharray', '5,5');
    svg.appendChild(polygon);

    const onClick = (e) => {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Convert to normalized coordinates
      const normalizedX = (x / canvas.width) * 2 - 1;
      const normalizedY = 1 - (y / canvas.height) * 2;

      this.selectionState.points.push([normalizedX, normalizedY, 0]);

      // Update polygon visualization
      const points = this.selectionState.points.map(p =>
        `${(p[0] + 1) * canvas.width / 2},${(1 - p[1]) * canvas.height / 2}`
      ).join(' ');
      polygon.setAttribute('points', points);

      Utils.notify(`Point ${this.selectionState.points.length} added`, 'info');
    };

    const onDoubleClick = (e) => {
      e.preventDefault();

      if (this.selectionState.points.length < 3) {
        Utils.notify('Need at least 3 points for polygon', 'error');
        return;
      }

      // Clean up
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('dblclick', onDoubleClick);

      // Show clear button
      document.getElementById('btnClearPolygon').style.display = 'inline-block';

      Utils.notify('Polygon closed. Click "Extract Selection" to apply.', 'success');
    };

    canvas.addEventListener('click', onClick);
    canvas.addEventListener('dblclick', onDoubleClick);

    // Hide start button
    document.getElementById('btnStartPolygon').style.display = 'none';
  }

  clearSelectionUI() {
    // Reset state
    this.selectionState.active = false;
    this.selectionState.type = null;
    this.selectionState.points = [];
    this.selectionState.frustumRect = null;

    // Remove visual overlays
    const selectionDiv = document.querySelector('div[style*="dashed #00ff00"]');
    if (selectionDiv) {
      selectionDiv.remove();
    }

    const svg = document.getElementById('polygonSelectionSVG');
    if (svg) {
      svg.remove();
    }

    // Reset buttons
    document.getElementById('btnStartRubberBand').style.display = 'inline-block';
    document.getElementById('btnClearSelection').style.display = 'none';
    document.getElementById('btnStartPolygon').style.display = 'inline-block';
    document.getElementById('btnClearPolygon').style.display = 'none';
  }

  async applySelectionFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    const selectionType = document.getElementById('selectionType')?.value;

    try {
      let requestBody = {
        case_path: casePath,
        selection_type: selectionType,
        time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
        mesh_parts: this.getVisibleMeshParts()
      };

      if (selectionType === 'frustum') {
        if (!this.selectionState.frustumRect) {
          Utils.notify('Please create a rubber band selection first', 'error');
          return;
        }

        // Get camera parameters
        const camera = viewer.renderingEngine.camera;
        requestBody.frustum_points = [
          this.selectionState.frustumRect.x1,
          this.selectionState.frustumRect.y1,
          this.selectionState.frustumRect.x2,
          this.selectionState.frustumRect.y2
        ];
        requestBody.camera_params = {
          position: camera.position.toArray(),
          target: viewer.renderingEngine.controls.target.toArray(),
          up: camera.up.toArray(),
          fov: camera.fov,
          aspect: camera.aspect,
          near: camera.near,
          far: camera.far
        };

      } else if (selectionType === 'polygon') {
        if (this.selectionState.points.length < 3) {
          Utils.notify('Please create a polygon selection first', 'error');
          return;
        }
        requestBody.polygon_points = this.selectionState.points;

      } else if (selectionType === 'threshold') {
        const fieldName = document.getElementById('selectionThresholdField')?.value;
        const minValue = parseFloat(document.getElementById('selectionThresholdMin')?.value);
        const maxValue = parseFloat(document.getElementById('selectionThresholdMax')?.value);

        if (!fieldName) {
          Utils.notify('Please select a field', 'error');
          return;
        }
        if (isNaN(minValue) || isNaN(maxValue)) {
          Utils.notify('Please enter valid min/max values', 'error');
          return;
        }

        requestBody.field_name = fieldName;
        requestBody.lower_threshold = minValue;
        requestBody.upper_threshold = maxValue;
      }

      Utils.notify('Extracting selection...', 'info');

      const response = await fetch('/api/filters/selection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Selection filter failed');
      }

      const result = await response.json();
      this.renderSelection(result.data);

      Utils.notify('Selection extracted successfully', 'success');
    } catch (error) {
      console.error('Selection filter error:', error);
      Utils.notify(`Selection error: ${error.message}`, 'error');
    }
  }

  renderSelection(selectionData) {
    const { vertices, faces, normals, scalars } = selectionData;

    // Remove previous selection
    this.removeFilter('selection-filter');

    // Create geometry
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    if (normals && normals.length > 0) {
      const normalsArray = new Float32Array(normals.flat());
      geometry.setAttribute('normal', new THREE.BufferAttribute(normalsArray, 3));
    } else {
      geometry.computeVertexNormals();
    }

    // Build face indices
    const indices = [];
    faces.forEach(face => {
      if (face.length === 3) {
        indices.push(...face);
      } else if (face.length === 4) {
        // Triangulate quad
        indices.push(face[0], face[1], face[2]);
        indices.push(face[0], face[2], face[3]);
      }
    });
    geometry.setIndex(indices);

    // Create material
    const material = new THREE.MeshPhongMaterial({
      color: 0x4488ff,
      side: THREE.DoubleSide,
      flatShading: false,
      shininess: 30,
      transparent: true,
      opacity: 0.8
    });

    // Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'selection-filter';

    // Add to scene
    const viewer = window.viewer;
    viewer.renderingEngine.scene.add(mesh);

    // Track filter
    this.activeFilters.set('selection-filter', {
      mesh: mesh,
      type: 'selection',
      selectionType: this.selectionState.type
    });

    // Clear selection UI
    this.clearSelectionUI();
  }

  // ========================================================================
  // VOLUME RENDERING
  // ========================================================================

  async applyVolumeFromUI() {
    const viewer = window.viewer;
    const casePath = this.getCurrentCasePath();
    if (!viewer || !viewer.currentData || !casePath) {
      Utils.notify('Please load a case first', 'error');
      return;
    }

    const fieldName = document.getElementById('volumeField')?.value;
    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    const resolution = parseInt(document.getElementById('volumeResolution')?.value) || 128;
    const transferFunctionPreset = document.getElementById('volumeTransferFunction')?.value || 'rainbow';

    try {
      Utils.notify('Preparing volume data... This may take a few seconds.', 'info');

      // Show button states
      document.getElementById('btnApplyVolume').disabled = true;
      document.getElementById('btnApplyVolume').textContent = 'Loading...';

      const response = await fetch('/api/filters/volume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth?.getToken()}`
        },
        body: JSON.stringify({
          case_path: casePath,
          field_name: fieldName,
          resolution: [resolution, resolution, resolution],
          compute_histogram: true,
          histogram_bins: 256,
          transfer_function_preset: transferFunctionPreset,
          time: viewer.geometryParams?.time || viewer.currentData?.currentTimeValue,
          mesh_parts: this.getVisibleMeshParts()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Volume rendering failed');
      }

      const result = await response.json();

      // Store volume data
      this.currentVolumeData = result.data;

      // Initialize volume renderer if not exists
      if (!this.volumeRenderer) {
        this.volumeRenderer = new VolumeRenderer(viewer.engine);
      } else {
        // Dispose previous volume
        this.volumeRenderer.dispose();
        this.volumeRenderer = new VolumeRenderer(viewer.engine);
      }

      // Load volume
      await this.volumeRenderer.loadVolume(result.data, result.data.transfer_function);

      // Add animation callback to update camera position for volume rendering
      // Remove previous callback if it exists
      if (this.volumeAnimationCallback) {
        viewer.engine.removeAnimationCallback(this.volumeAnimationCallback);
      }

      this.volumeAnimationCallback = () => {
        if (this.volumeRenderer && this.volumeRenderer.isLoaded()) {
          this.volumeRenderer.updateCamera();
        }
      };

      viewer.engine.addAnimationCallback(this.volumeAnimationCallback);

      // Show histogram if requested
      if (document.getElementById('volumeShowHistogram')?.checked && result.data.histogram) {
        this.showVolumeHistogram(result.data.histogram, result.data.histogram_bins);
      }

      // Update UI
      document.getElementById('btnRemoveVolume').style.display = 'inline-block';
      document.getElementById('btnApplyVolume').textContent = 'Update Volume';

      Utils.notify(`Volume rendering applied for ${fieldName}`, 'success');

    } catch (error) {
      console.error('Volume rendering error:', error);
      Utils.notify(`Volume error: ${error.message}`, 'error');
    } finally {
      document.getElementById('btnApplyVolume').disabled = false;
      if (document.getElementById('btnApplyVolume').textContent === 'Loading...') {
        document.getElementById('btnApplyVolume').textContent = 'Apply Volume Rendering';
      }
    }
  }

  updateVolumeTransferFunction() {
    if (!this.volumeRenderer || !this.currentVolumeData) {
      return;
    }

    const transferFunctionPreset = document.getElementById('volumeTransferFunction')?.value || 'rainbow';

    // Create transfer function from preset
    const transferFunction = this.createTransferFunctionPreset(
      transferFunctionPreset,
      this.currentVolumeData.data_range
    );

    // Update renderer
    this.volumeRenderer.updateTransferFunction(transferFunction);

    Utils.notify(`Transfer function updated to ${transferFunctionPreset}`, 'success');
  }

  createTransferFunctionPreset(presetName, scalarRange) {
    const presets = {
      'rainbow': {
        color_points: [
          [0.0, 0.0, 0.0, 1.0],
          [0.25, 0.0, 1.0, 1.0],
          [0.5, 0.0, 1.0, 0.0],
          [0.75, 1.0, 1.0, 0.0],
          [1.0, 1.0, 0.0, 0.0]
        ],
        opacity_points: [
          [0.0, 0.0],
          [0.2, 0.1],
          [0.8, 0.8],
          [1.0, 1.0]
        ]
      },
      'grayscale': {
        color_points: [
          [0.0, 0.0, 0.0, 0.0],
          [1.0, 1.0, 1.0, 1.0]
        ],
        opacity_points: [
          [0.0, 0.0],
          [1.0, 1.0]
        ]
      },
      'hot': {
        color_points: [
          [0.0, 0.0, 0.0, 0.0],
          [0.33, 1.0, 0.0, 0.0],
          [0.66, 1.0, 1.0, 0.0],
          [1.0, 1.0, 1.0, 1.0]
        ],
        opacity_points: [
          [0.0, 0.0],
          [0.3, 0.2],
          [0.7, 0.7],
          [1.0, 1.0]
        ]
      },
      'cool': {
        color_points: [
          [0.0, 0.0, 1.0, 1.0],
          [0.5, 0.0, 0.5, 1.0],
          [1.0, 1.0, 0.0, 1.0]
        ],
        opacity_points: [
          [0.0, 0.0],
          [0.5, 0.5],
          [1.0, 1.0]
        ]
      },
      'jet': {
        color_points: [
          [0.0, 0.0, 0.0, 0.5],
          [0.125, 0.0, 0.0, 1.0],
          [0.375, 0.0, 1.0, 1.0],
          [0.625, 1.0, 1.0, 0.0],
          [0.875, 1.0, 0.0, 0.0],
          [1.0, 0.5, 0.0, 0.0]
        ],
        opacity_points: [
          [0.0, 0.0],
          [0.2, 0.1],
          [0.8, 0.8],
          [1.0, 1.0]
        ]
      }
    };

    const preset = presets[presetName] || presets['rainbow'];

    return {
      scalar_range: scalarRange,
      color_points: preset.color_points,
      opacity_points: preset.opacity_points
    };
  }

  showVolumeHistogram(histogram, bins) {
    // TODO: Create a canvas overlay to show histogram
    // For now, just log it
    console.log('Volume histogram:', {
      counts: histogram,
      bins: bins,
      numBins: histogram.length
    });
  }

  openTransferFunctionEditor() {
    // TODO: Implement interactive transfer function editor
    Utils.notify('Transfer function editor coming soon! Use presets for now.', 'info');
  }

  removeVolume() {
    if (this.volumeRenderer) {
      this.volumeRenderer.dispose();
      this.volumeRenderer = null;
      this.currentVolumeData = null;

      // Remove animation callback
      if (this.volumeAnimationCallback && window.viewer && window.viewer.engine) {
        window.viewer.engine.removeAnimationCallback(this.volumeAnimationCallback);
        this.volumeAnimationCallback = null;
      }

      document.getElementById('btnRemoveVolume').style.display = 'none';
      document.getElementById('btnApplyVolume').textContent = 'Apply Volume Rendering';
      Utils.notify('Volume rendering removed', 'success');
    }
  }

  // ========================================================================
  // SLICE FILTER FOR UNSTRUCTURED MESH
  // ========================================================================

  createSliceUnstructured(data, options = {}) {
    const {
      origin,
      normal,
      variable = 'pressure',
      colormap = 'viridis',
      id = Utils.generateId(),
      mesh_parts = null  // Accepted for consistency; frontend slice uses merged grid
    } = options;

    if (!data.grid || !data.grid.vertices || !data.grid.faces) {
      console.error('Invalid grid data for slicing');
      return null;
    }

    const scalars = data.fields[variable];
    if (!scalars) {
      console.error(`Variable ${variable} not found in data`);
      return null;
    }

    console.log(`Creating slice at origin ${origin.toArray()}, normal ${normal.toArray()}`);

    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin);
    const vertices = data.grid.vertices;
    const faces = data.grid.faces;

    const positions = [];
    const colors = [];
    let faceCount = 0;

    // Get colormap range
    const min = Math.min(...scalars);
    const max = Math.max(...scalars);

    // For each face, check if plane intersects
    for (let faceIdx = 0; faceIdx < faces.length; faceIdx++) {
      const face = faces[faceIdx];
      const faceVertices = face.map(vIdx => new THREE.Vector3(...vertices[vIdx]));
      const faceScalars = face.map(vIdx => scalars[vIdx] || 0);

      // Compute intersection of plane with face polygon
      const intersection = this.intersectPlaneWithPolygon(plane, faceVertices, faceScalars);

      if (intersection && intersection.points.length >= 3) {
        // Triangulate the intersection polygon
        for (let i = 1; i < intersection.points.length - 1; i++) {
          positions.push(
            ...intersection.points[0],
            ...intersection.points[i],
            ...intersection.points[i + 1]
          );

          // Color based on scalar value
          for (let j of [0, i, i + 1]) {
            const scalar = intersection.scalars[j];
            const t = (scalar - min) / (max - min + 1e-10);
            const color = this.getColorFromColormap(t, colormap);
            colors.push(color.r, color.g, color.b);
          }

          faceCount++;
        }
      }
    }

    if (positions.length === 0) {
      console.warn('Slice generated no geometry - plane may not intersect mesh');
      return null;
    }

    console.log(`Slice created: ${faceCount} triangles`);

    // Create THREE.js geometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshPhongMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      transparent: false
    });

    const mesh = new THREE.Mesh(geometry, material);

    this.engine.addObject(id, mesh, {
      type: 'filter',
      kind: 'slice',
      name: `Slice (${variable})`,
      icon: '✂️',
      removable: true,
      options
    });

    this.activeFilters.set(id, { type: 'slice', options, mesh });

    // Notify view lock manager of visualization change
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }

    return id;
  }

  intersectPlaneWithPolygon(plane, polygonVertices, polygonScalars) {
    const intersectionPoints = [];
    const intersectionScalars = [];

    // Check each edge of the polygon for intersection
    for (let i = 0; i < polygonVertices.length; i++) {
      const v1 = polygonVertices[i];
      const v2 = polygonVertices[(i + 1) % polygonVertices.length];
      const s1 = polygonScalars[i];
      const s2 = polygonScalars[(i + 1) % polygonVertices.length];

      const d1 = plane.distanceToPoint(v1);
      const d2 = plane.distanceToPoint(v2);

      const tolerance = 1e-6;

      // Edge crosses plane
      if ((d1 < -tolerance && d2 > tolerance) || (d1 > tolerance && d2 < -tolerance)) {
        const t = d1 / (d1 - d2);
        const intersection = new THREE.Vector3().lerpVectors(v1, v2, t);
        const scalarInterp = s1 + t * (s2 - s1);

        intersectionPoints.push([intersection.x, intersection.y, intersection.z]);
        intersectionScalars.push(scalarInterp);
      }
      // Vertex v1 on plane
      else if (Math.abs(d1) < tolerance) {
        // Only add if it's the first occurrence to avoid duplicates
        const alreadyAdded = intersectionPoints.some(p =>
          Math.abs(p[0] - v1.x) < tolerance &&
          Math.abs(p[1] - v1.y) < tolerance &&
          Math.abs(p[2] - v1.z) < tolerance
        );
        if (!alreadyAdded) {
          intersectionPoints.push([v1.x, v1.y, v1.z]);
          intersectionScalars.push(s1);
        }
      }
    }

    if (intersectionPoints.length >= 3) {
      return { points: intersectionPoints, scalars: intersectionScalars };
    }

    return null;
  }

  // ========================================================================
  // ORIGINAL METHODS (Keep for compatibility)
  // ========================================================================

  // Create contour/isosurface
  createContour(data, options = {}) {
    const {
      variable = 'pressure',
      value = 0.5,
      colormap = 'viridis',
      id = Utils.generateId()
    } = options;

    const scalars = data?.fields?.[variable];
    if (!Array.isArray(scalars) || scalars.length === 0) {
      throw new Error(`Variable ${variable} not found in data`);
    }

    const verticesSource = Array.isArray(data.vertices)
      ? data.vertices
      : (data.grid?.points || []);

    if (!Array.isArray(verticesSource) || verticesSource.length === 0) {
      throw new Error('No vertex data available for contour generation');
    }

    const range = this.getScalarRange(scalars);
    const epsilon = range.span > 0 ? Math.max(range.span * 0.01, 1e-4) : 0.1;

    // Get solid color for main mesh colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('colormapPreset') : null;

    const vertices = [];
    const colors = [];

    verticesSource.forEach((vertex, i) => {
      const scalarValue = scalars[i];
      if (scalarValue == null) return;

      if (Math.abs(scalarValue - value) <= epsilon) {
        vertices.push(vertex);
        const normalized = this.normalizeScalar(scalarValue, range);
        const color = Colormap.getColor(normalized, colormap, solidColorHex);
        colors.push([color[0] / 255, color[1] / 255, color[2] / 255]);
      }
    });

    if (vertices.length === 0) {
      Utils.notify('No contour found at specified value', 'warning');
      return null;
    }

    const storedOptions = {
      variable,
      value,
      colormap
    };

    const points = this.engine.createPointsFromData(vertices, colors, 0.1);
    this.engine.addObject(id, points, {
      type: 'filter',
      kind: 'contour',
      name: `Contour (${variable})`,
      icon: '📈',
      removable: true,
      options: storedOptions
    });
    this.activeFilters.set(id, { type: 'contour', options: storedOptions });

    return id;
  }

  // Create streamlines
  async createStreamlines(data, options = {}) {
    const isOpenFoamSource = (data.source === 'OpenFOAM') || (data.metadata?.format === 'OpenFOAM');
    const seedType = options.seedType || (isOpenFoamSource ? 'point' : 'line');
    options.seedType = seedType;
    const colormap = options.colormap || 'jet';

    const casePath = data.case_path || data.metadata?.case_path;
    if (!casePath) {
      Utils.notify('Streamline tracing requires a valid case path.', 'error');
      return null;
    }

    // Backend mode - generate seeds and use backend API
    if (this.viewer && this.viewer.renderingMode === 'geometry') {
      console.log('[Streamlines Backend] Using backend API for streamlines');

      // Generate seed points if not provided
      if (!Array.isArray(options.seedPoints) || options.seedPoints.length === 0) {
        console.log('[Streamlines Backend] Generating seed points for type:', seedType);
        const defaults = this.getStreamlineDefaultParams(data);
        const seedOptions = {
          ...options,
          seedType,
          __gridPoints: null,  // No grid points in backend mode
          __dataSource: data.source || 'OpenFOAM'
        };
        const seedsResult = this.generateStreamlineSeeds(data, seedOptions);
        if (!seedsResult || !Array.isArray(seedsResult.seeds) || seedsResult.seeds.length === 0) {
          Utils.notify('No streamline seed points generated – adjust the seed parameters.', 'warning');
          return null;
        }
        options.seedPoints = seedsResult.seeds;
        console.log('[Streamlines Backend] Generated', seedsResult.seeds.length, 'seed points');

        // Inform user about high seed counts (backend will scale computation accordingly)
        if (seedsResult.seeds.length > 200) {
          Utils.notify(`Computing ${seedsResult.seeds.length} streamlines - this may take a moment...`, 'info');
        }
      }

      return await this.createStreamlinesBackend(data, options);
    }

    let streamlineGrid = data.grid || {};
    let fieldSource = { ...(data.fields || {}) };
    const hasVolume = data?.volume && Array.isArray(data.volume.points) && data.volume.points.length > 0;

    if (hasVolume) {
      streamlineGrid = {
        ...(data.grid || {}),
        vertices: data.volume.points,
        bounds: data.volume.bounds || data.grid?.bounds
      };

      if (data.volumeFields) {
        fieldSource = { ...fieldSource, ...data.volumeFields };
      }
    }

    const effectiveData = {
      ...data,
      grid: streamlineGrid,
      fields: fieldSource
    };

    const pointCount = Array.isArray(effectiveData.grid?.points) ? effectiveData.grid.points.length : 0;
    if (Array.isArray(data.time_series)) {
      data.time_series.forEach(frame => {
        if (!frame || !frame.fields) return;
        Object.entries(frame.fields).forEach(([name, value]) => {
          if (fieldSource[name]) return;
          if (Array.isArray(value) && (!pointCount || value.length === pointCount)) {
            fieldSource[name] = value;
          }
        });
      });
    }

    const ensureVectorField = (fields, baseName) => {
      if (!fields || fields[baseName]) return fields?.[baseName];
      const x = fields[`${baseName}_x`];
      const y = fields[`${baseName}_y`];
      const z = fields[`${baseName}_z`];
      if (Array.isArray(x) && Array.isArray(y) && Array.isArray(z)) {
        const len = Math.min(x.length, y.length, z.length);
        const combined = new Array(len);
        for (let i = 0; i < len; i++) {
          combined[i] = [x[i], y[i], z[i]];
        }
        fields[baseName] = combined;
        return combined;
      }
      return null;
    };

    ensureVectorField(fieldSource, 'velocity');
    ensureVectorField(fieldSource, 'U');
    if (fieldSource.velocity && !fieldSource.U) {
      fieldSource.U = fieldSource.velocity;
      if (data.fields && !data.fields.U) {
        data.fields.U = fieldSource.velocity;
      }
    }

    const mapOpenFoamVectorField = (requested, fields) => {
      // Field name mapping for OpenFOAM (friendly name -> OpenFOAM name)
      const fieldNameMap = {
        'pressure': 'p',
        'turbulent_kinetic_energy': 'k',
        'turbulent_viscosity': 'nut',
        'specific_dissipation_rate': 'omega',
        'velocity': 'U'
        // velocity_x, velocity_y, velocity_z are scalar components and should use their actual names
      };

      // Try mapped name first
      const mappedName = fieldNameMap[requested];
      if (mappedName && fields && fields[mappedName]) {
        return mappedName;
      }

      // Try original requested name
      if (fields && fields[requested]) {
        return requested;
      }

      // Try lowercase
      const lower = (requested || '').toLowerCase();
      if (fields && fields[lower]) {
        return lower;
      }

      // For velocity-related requests, prefer 'U'
      const preferred = ['U', requested, lower, 'velocity'];
      for (const name of preferred) {
        if (!name) continue;
        if (fields && fields[name]) {
          return name;
        }
        const normalized = name.toLowerCase();
        if (fields && fields[normalized]) {
          return normalized;
        }
      }

      // Fallback: find any vector field
      const candidates = Object.keys(fields || {}).filter(name => {
        const value = fields[name];
        return Array.isArray(value) && value.length > 0 && Array.isArray(value[0]);
      });
      return candidates.length > 0 ? candidates[0] : requested;
    };

    const defaults = this.getStreamlineDefaultParams(effectiveData);
    const maxLength = Math.max(this.numberOr(options.maxLength, defaults.maxLength), 1);
    const stepSize = Math.max(this.numberOr(options.stepSize, defaults.stepSize), 0.00005);
    const id = options.id || Utils.generateId();
    let variable = options.variable || this.getDefaultVectorField(effectiveData);
    const direction = options.direction || 'both';

    let velocityField = fieldSource?.[variable] || data.fields?.[variable];
    if (!velocityField) {
      velocityField = ensureVectorField(fieldSource, variable) || ensureVectorField(fieldSource, variable.toLowerCase());
    }
    if (!velocityField && variable.toLowerCase() === 'u') {
      variable = 'velocity';
      velocityField = fieldSource?.velocity || ensureVectorField(fieldSource, 'velocity');
    }
    if (!velocityField) {
      const fallbackVector = this.getDefaultVectorField(effectiveData);
      if (fallbackVector && fallbackVector !== variable) {
        variable = fallbackVector;
        velocityField = fieldSource?.[variable] || data.fields?.[variable] || ensureVectorField(fieldSource, variable) || ensureVectorField(fieldSource, variable.toLowerCase());
      }
    }

    if (!velocityField) {
      const mapped = mapOpenFoamVectorField(variable, fieldSource);
      if (mapped && mapped !== variable) {
        variable = mapped;
        velocityField = fieldSource?.[variable] || data.fields?.[variable] || ensureVectorField(fieldSource, variable) || ensureVectorField(fieldSource, variable.toLowerCase());
      }
    }

    options.variable = variable;

    if (!velocityField) {
      throw new Error(`Vector field "${variable}" not found`);
    }

    let seedsResult;
    const seedOptions = {
      ...options,
      seedType,
      __gridPoints: Array.isArray(streamlineGrid?.points) ? streamlineGrid.points : null,
      __dataSource: effectiveData.source || data.source
    };

    if (Array.isArray(options.seedPoints) && options.seedPoints.length > 0) {
      seedsResult = { seeds: options.seedPoints.slice(), params: {} };
    } else {
      seedsResult = this.generateStreamlineSeeds(effectiveData, seedOptions);
    }

    if (!seedsResult || !Array.isArray(seedsResult.seeds) || seedsResult.seeds.length === 0) {
      Utils.notify('No streamline seed points generated – adjust the seed parameters.', 'warning');
      return null;
    }

    const seedPoints = seedsResult.seeds;
    if (!Array.isArray(seedPoints) || seedPoints.length === 0) {
      Utils.notify('No streamline seed points generated – adjust the seed parameters.', 'warning');
      return null;
    }

    let lineData = [];
    let scalarRange = null;
    let apiResult = null;
    try {
      if (!this.viewer) {
        Utils.notify('Viewer not initialized for streamline computation.', 'error');
        return null;
      }

      const apiBase = this.viewer.openfoamLoader?.apiBase || '';
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const geometryParams = this.viewer.geometryParams;
      const dataSource = geometryParams?.dataSource;
      let endpoint;
      if (dataSource === 'fluent') {
        endpoint = `${apiBase}/api/fluent/streamlines`;
      } else if (dataSource === 'vtk') {
        endpoint = `${apiBase}/api/vtk/streamlines`;
      } else if (dataSource === 'openfoam' || (data.source === 'OpenFOAM') || (data.metadata?.format === 'OpenFOAM')) {
        endpoint = `${apiBase}/api/openfoam/streamlines`;
      } else {
        endpoint = `${apiBase}/api/ensight/streamlines`;
      }
      // Send the original variable name to backend - backend will handle mapping
      const requestVectorField = variable;
      const timeValue = (typeof data.currentTimestep === 'number' && Array.isArray(data.timesteps))
        ? data.timesteps[data.currentTimestep]
        : undefined;

      // Get render mode and mode-specific parameters
      const renderMode = options.renderMode || document.getElementById('streamlineRenderMode')?.value || 'lines';
      console.log('[Streamlines] Sending request with render_mode:', renderMode);

      const requestBody = {
        case_path: casePath,
        seed_points: seedPoints,
        vector_field: requestVectorField,
        step_size: stepSize,
        max_length: maxLength,
        integration_direction: direction,
        render_mode: renderMode,
        ...(timeValue !== undefined ? { time: timeValue } : {})
      };

      if (options.colorField) {
        requestBody.color_field = options.colorField;
      }

      // Add mode-specific parameters
      if (renderMode === 'tubes') {
        requestBody.tube_radius = parseFloat(document.getElementById('streamlineTubeRadius')?.value) || 0.01;
        requestBody.tube_sides = parseInt(document.getElementById('streamlineTubeSides')?.value) || 8;
        requestBody.tube_vary_by = document.getElementById('streamlineTubeVaryBy')?.value || 'none';
        console.log('[Streamlines] Tube parameters:', {
          radius: requestBody.tube_radius,
          sides: requestBody.tube_sides,
          vary_by: requestBody.tube_vary_by
        });
      } else if (renderMode === 'ribbons') {
        requestBody.ribbon_width = parseFloat(document.getElementById('streamlineRibbonWidth')?.value) || 0.02;
        requestBody.ribbon_angle = 0.0;
        console.log('[Streamlines] Ribbon parameters:', {
          width: requestBody.ribbon_width,
          angle: requestBody.ribbon_angle
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || response.statusText;
        throw new Error(detail);
      }

      apiResult = await response.json();

      // Debug logging for render mode
      console.log('[Streamlines] API Response:', {
        render_mode: apiResult.render_mode,
        has_tubes: !!apiResult.tubes,
        has_particles: !!apiResult.particles,
        has_ribbons: !!apiResult.ribbons,
        has_streamlines: !!apiResult.streamlines
      });

      const received = Array.isArray(apiResult.streamlines) ? apiResult.streamlines : [];
      let scalarMin = Number.POSITIVE_INFINITY;
      let scalarMax = Number.NEGATIVE_INFINITY;

      const declaredRange = apiResult.scalar_range;
      if (declaredRange) {
        const rangeMin = Number(declaredRange.min);
        const rangeMax = Number(declaredRange.max);
        if (Number.isFinite(rangeMin) && Number.isFinite(rangeMax)) {
          scalarMin = rangeMin;
          scalarMax = rangeMax;
        }
      }

      lineData = received
        .map(item => {
          const points = Array.isArray(item)
            ? item
            : (Array.isArray(item?.points) ? item.points : null);
          if (!Array.isArray(points) || points.length <= 1) {
            return null;
          }
          const scalars = Array.isArray(item?.scalars) ? item.scalars.map(value => {
            const numeric = value == null ? null : Number(value);
            return Number.isFinite(numeric) ? numeric : null;
          }) : null;
          if (Array.isArray(scalars) && scalars.length === points.length) {
            for (const value of scalars) {
              if (Number.isFinite(value)) {
                scalarMin = Math.min(scalarMin, value);
                scalarMax = Math.max(scalarMax, value);
              }
            }
          }
          return {
            points,
            scalars: (Array.isArray(scalars) && scalars.length === points.length) ? scalars : null
          };
        })
        .filter(entry => entry !== null);

      if (lineData.length === 0) {
        Utils.notify('Streamline solver produced no paths – adjust seeds or parameters.', 'warning');
        return null;
      }

      if (Number.isFinite(scalarMin) && Number.isFinite(scalarMax) && scalarMax > scalarMin) {
        scalarRange = { min: scalarMin, max: scalarMax };
      } else {
        scalarRange = null;
      }
    } catch (error) {
      console.error('Failed to compute streamlines:', error);
      Utils.notify(`Streamline computation failed: ${error.message}`, 'error');
      return null;
    }

    // Use shared rendering function for consistency
    const group = this.renderStreamlinesGroup(apiResult, lineData, colormap, scalarRange, options, id);

    // Use shared options creation function for consistency
    const storedOptions = this.createStreamlineStoredOptions(
      apiResult,
      scalarRange,
      options,
      seedType,
      variable,
      maxLength,
      stepSize,
      colormap,
      seedPoints,
      direction,
      seedsResult,
      data
    );

    console.log('[Streamlines Create] Legend data from backend:', {
      scalarRange,
      scalarField: apiResult?.scalar_field,
      scalarMetric: apiResult?.scalar_metric,
      variable: storedOptions.variable
    });

    if (seedType === 'point') {
      this.updateStoredStreamlineSettings(data, {
        pointRadius: storedOptions.pointRadius,
        pointCount: storedOptions.pointCount
      });
    }

    console.log('[Streamlines Create] Adding streamlines to scene:', {
      id,
      numberOfStreamlines: lineData.length,
      groupVisible: group.visible,
      groupChildren: group.children.length
    });

    this.engine.addObject(id, group, {
      type: 'filter',
      kind: 'streamlines',
      name: 'Streamlines',
      icon: '<img src="static/assets/icons/streamlines.svg" width="16" height="16" style="filter: brightness(0) invert(1); vertical-align: middle;">',
      removable: true,
      options: storedOptions
    });
    this.activeFilters.set(id, { type: 'streamlines', options: storedOptions, streamlines: lineData });

    console.log('[Streamlines Create] Streamlines added successfully');

    // AUTO-CHECKING DISABLED: Users must manually enable streamlines filter
    // const streamlineCheckbox = document.getElementById('streamlineFilterEnabled');
    // if (streamlineCheckbox) {
    //   streamlineCheckbox.checked = true;
    // }

    if (window.viewer && typeof window.viewer.onStreamlineFilterUpdated === 'function') {
      window.viewer.onStreamlineFilterUpdated(id, storedOptions);
    }

    // Remove all seed previews after streamlines created
    if (this.engine) {
      this.engine.removeObject('streamline-sphere-preview');
      this.engine.removeObject('streamline-line-preview');
      this.engine.removeObject('streamline-plane-preview');
    }
    if (this.sphereBlinkInterval) {
      clearInterval(this.sphereBlinkInterval);
      this.sphereBlinkInterval = null;
    }
    if (this.lineBlinkInterval) {
      clearInterval(this.lineBlinkInterval);
      this.lineBlinkInterval = null;
    }
    if (this.planeBlinkInterval) {
      clearInterval(this.planeBlinkInterval);
      this.planeBlinkInterval = null;
    }

    if (window.viewer) {
      console.log('[Preview Check] Checking preview conditions:', {
        hasFunction: typeof window.viewer.attachStreamlinePreview === 'function',
        previewEnabled: storedOptions.previewEnabled,
        notSlice: id !== 'slice-streamlines',
        lineDataCount: lineData?.length
      });

      if (typeof window.viewer.attachStreamlinePreview === 'function' && storedOptions.previewEnabled !== false && id !== 'slice-streamlines') {
        console.log('[Preview Call] Calling attachStreamlinePreview from displayStreamlines');
        window.viewer.attachStreamlinePreview(id, lineData, storedOptions);
      } else if (typeof window.viewer.detachStreamlinePreview === 'function') {
        console.log('[Preview Call] Calling detachStreamlinePreview from displayStreamlines');
        window.viewer.detachStreamlinePreview(id);
      }
    }
    // Don't sync controls here - it would overwrite user input
    // The controls are already set with the values the user entered

    // Notify view lock manager of visualization change
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }

    return id;
  }
  // Trace single streamline
  traceStreamline(start, velocityField, grid, maxLength, stepSize) {
    const points = [start];
    let current = [...start];

    if (grid && !grid._vertexSearchBuilt) {
      this.buildVertexSpatialIndex(grid);
    }

    for (let i = 0; i < maxLength; i++) {
      const velocity = this.interpolateVelocity(current, velocityField, grid);
      if (!velocity || Math.hypot(...velocity) < 0.001) break;

      // Normalize and step
      const magnitude = Math.hypot(...velocity);
      const step = velocity.map(v => (v / magnitude) * stepSize);

      current = current.map((c, j) => c + step[j]);

      // Check bounds
      if (!this.isInBounds(current, grid)) break;

      points.push([...current]);
    }

    return points;
  }

  // Helper function to render streamlines (shared by frontend and backend)
  renderStreamlinesGroup(apiResult, lineData, colormap, scalarRange, options, id) {
    const group = new THREE.Group();

    // Set visibility based on checkbox state
    if (id === 'slice-streamlines') {
      const sliceStreamlineCheckbox = document.getElementById('sliceShowStreamlines');
      group.visible = sliceStreamlineCheckbox ? sliceStreamlineCheckbox.checked : true;
    } else {
      const checkbox = document.getElementById('streamlineFilterEnabled');
      group.visible = checkbox ? checkbox.checked : true;
    }

    // Handle different render modes
    console.log('[Streamlines Render] Render mode:', apiResult.render_mode);

    if (apiResult.render_mode === 'tubes' && apiResult.tubes) {
      console.log('[Streamlines Render] Rendering TUBES');
      this.renderStreamTubes(apiResult.tubes, colormap, scalarRange, group);
    } else if (apiResult.render_mode === 'ribbons' && apiResult.ribbons) {
      console.log('[Streamlines Render] Rendering RIBBONS');
      this.renderRibbonStreamlines(apiResult.ribbons, colormap, scalarRange, group);
    } else {
      console.log('[Streamlines Render] Rendering LINES');
      // Default: render lines
      lineData.forEach((entry, i) => {
        const lineObj = this.buildStreamlineLine(
          entry.points,
          entry.scalars,
          i,
          lineData.length,
          colormap,
          scalarRange
        );
        group.add(lineObj);
      });
    }

    return group;
  }

  // Helper function to create stored options (shared by frontend and backend)
  createStreamlineStoredOptions(apiResult, scalarRange, options, seedType, variable, maxLength, stepSize, colormap, seedPoints, direction, seedsResult, data) {
    const storedOptions = {
      seedType,
      variable,
      maxLength,
      stepSize,
      colormap,
      seedCount: seedPoints.length,
      seedPoints: seedPoints,
      direction,
      previewEnabled: options.previewEnabled !== false,
      previewDotScale: Number.isFinite(options.previewDotScale) ? Math.max(options.previewDotScale, 0.0001) : 0.004,
      previewSpeed: Number.isFinite(options.previewSpeed) ? Math.max(options.previewSpeed, 0) : undefined,
      ...(seedsResult?.params || {})
    };

    if (options.colorField) {
      storedOptions.colorField = options.colorField;
      storedOptions.colorBy = options.colorField;
    }

    if (!Number.isFinite(storedOptions.previewSpeed)) {
      const bounds = this.extractBounds(data) || { xmin: 0, xmax: 0, ymin: 0, ymax: 0, zmin: 0, zmax: 0 };
      const span = Math.max(
        bounds.xmax - bounds.xmin,
        bounds.ymax - bounds.ymin,
        bounds.zmax - bounds.zmin,
        1
      );
      storedOptions.previewSpeed = span * 0.2;
    }

    if (scalarRange) {
      storedOptions.scalarRange = scalarRange;
    }
    if (typeof apiResult?.scalar_field === 'string') {
      storedOptions.scalarField = apiResult.scalar_field;
      if (!storedOptions.colorField) {
        storedOptions.colorField = apiResult.scalar_field;
      }
    }
    if (typeof apiResult?.scalar_metric === 'string') {
      storedOptions.scalarMetric = apiResult.scalar_metric;
    }

    if (!storedOptions.colorBy) {
      storedOptions.colorBy = storedOptions.colorField || storedOptions.scalarField || storedOptions.variable;
    }

    // Store render mode and its specific parameters
    if (options.renderMode) {
      storedOptions.renderMode = options.renderMode;
    }
    if (options.tubeRadius !== undefined) {
      storedOptions.tubeRadius = options.tubeRadius;
    }
    if (options.tubeSides !== undefined) {
      storedOptions.tubeSides = options.tubeSides;
    }
    if (options.varyRadius !== undefined) {
      storedOptions.varyRadius = options.varyRadius;
    }
    if (options.ribbonWidth !== undefined) {
      storedOptions.ribbonWidth = options.ribbonWidth;
    }
    if (options.particleCount !== undefined) {
      storedOptions.particleCount = options.particleCount;
    }
    if (options.particleSteps !== undefined) {
      storedOptions.particleSteps = options.particleSteps;
    }

    return storedOptions;
  }

  // Interpolate velocity at point
  async createStreamlinesBackend(data, options = {}) {
    console.log('[Streamlines Backend] Creating streamlines using backend API', options);

    const casePath = data.case_path || data.metadata?.case_path;
    const id = options.id || 'streamlines';
    const seedPoints = options.seedPoints || [];

    // Get vector field from options or UI
    const vectorFieldSelect = document.getElementById('streamlineVectorField');
    const vectorField = options.variable || vectorFieldSelect?.value || 'U';

    // Get color field from options or UI dropdown
    const colorFieldSelect = document.getElementById('streamlineColorField');
    const colorField = options.colorField || colorFieldSelect?.value || vectorField;

    // Get render mode from UI
    const renderModeSelect = document.getElementById('streamlineRenderMode');
    const renderMode = options.renderMode || renderModeSelect?.value || 'lines';

    // Get step size and max length from UI or options
    const stepSizeInput = document.getElementById('streamlineStepSize');
    const maxLengthInput = document.getElementById('streamlineMaxLength');
    let stepSize = options.stepSize;
    if (stepSize === undefined || stepSize === null) {
      stepSize = stepSizeInput ? parseFloat(stepSizeInput.value) : 0.01;
    }
    if (!stepSize || stepSize <= 0) stepSize = 0.01;

    let maxLength = options.maxLength;
    if (maxLength === undefined || maxLength === null) {
      maxLength = maxLengthInput ? parseFloat(maxLengthInput.value) : 100.0;
    }
    if (!maxLength || maxLength <= 0) maxLength = 100.0;

    const direction = options.direction || 'both';

    if (seedPoints.length === 0) {
      Utils.notify('No seed points specified for streamlines', 'error');
      return null;
    }

    try {
      // Get authentication token
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');

      // Build request body
      const requestBody = {
        case_path: casePath,
        time: this.viewer.geometryParams?.time,
        seed_points: seedPoints,
        color_field: colorField,
        vector_field: vectorField,
        step_size: stepSize,
        max_length: maxLength,
        integration_direction: direction,
        render_mode: renderMode
      };

      // Include cache_key if provided (collaborative session optimization)
      // This allows followers to fetch cached results instead of recomputing
      if (options.cache_key) {
        requestBody.cache_key = options.cache_key;
        console.log('[Streamlines Backend] Using cache_key from sync:', options.cache_key);
      }

      // Add render mode specific parameters
      if (renderMode === 'tubes') {
        const tubeRadiusInput = document.getElementById('streamlineTubeRadius');
        const tubeSidesInput = document.getElementById('streamlineTubeSides');
        requestBody.tube_radius = tubeRadiusInput ? parseFloat(tubeRadiusInput.value) : 0.01;
        requestBody.tube_sides = tubeSidesInput ? parseInt(tubeSidesInput.value) : 8;
      } else if (renderMode === 'particles') {
        const particleCountInput = document.getElementById('streamlineParticleCount');
        const particleStepsInput = document.getElementById('streamlineParticleSteps');
        requestBody.particle_count = particleCountInput ? parseInt(particleCountInput.value) : 100;
        requestBody.particle_steps = particleStepsInput ? parseInt(particleStepsInput.value) : 50;
      } else if (renderMode === 'ribbons') {
        const ribbonWidthInput = document.getElementById('streamlineRibbonWidth');
        requestBody.ribbon_width = ribbonWidthInput ? parseFloat(ribbonWidthInput.value) : 0.02;
      }

      console.log('[Streamlines Backend] Request:', requestBody);

      // Determine API base URL
      const apiBase = this.viewer.openfoamLoader?.apiBase ||
        (window.location.origin.includes('localhost') ? 'http://localhost:8082' : '');

      // Determine data source - check geometryParams first, then data.source
      const geometryDataSource = this.viewer.geometryParams?.dataSource;
      let dataSource;
      if (geometryDataSource === 'fluent') {
        dataSource = 'fluent';
      } else if (geometryDataSource === 'ensight' || data.source === 'EnSight' || data.metadata?.format === 'EnSight') {
        dataSource = 'ensight';
      } else {
        dataSource = 'openfoam';
      }
      const endpoint = `${apiBase}/api/${dataSource}/streamlines`;
      console.log('[Streamlines Backend] Using endpoint:', endpoint, 'for dataSource:', dataSource, 'geometryDataSource:', geometryDataSource);

      // Call backend streamlines API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Backend streamline computation failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Streamlines Backend] Received streamline data:', result);

      // Parse streamlines from backend response
      const streamlines = result.streamlines || [];
      if (streamlines.length === 0) {
        Utils.notify('No streamlines generated - check seed points and flow field', 'warning');
        return null;
      }

      // Prepare line data structure
      const lineData = streamlines.map(s => ({
        points: s.points || s,
        scalars: s.scalars
      }));

      const scalarRange = result.scalar_range || { min: 0, max: 1 };
      const colormapPreset = options.colormap || this.viewer?.colormap?.preset || 'jet';

      // Use the shared rendering function (same as frontend)
      const group = this.renderStreamlinesGroup(result, lineData, colormapPreset, scalarRange, options, id);

      // Capture UI parameters for sync (so follower can display same UI values)
      const seedType = options.seedType || 'point';
      const uiParams = {};

      // Capture seed type specific parameters
      // Helper to get value from options or UI element
      const getVal = (optVal, elId, defaultVal) => {
        if (optVal !== undefined && optVal !== null) return optVal;
        const el = document.getElementById(elId);
        const parsed = el ? parseFloat(el.value) : NaN;
        return isNaN(parsed) ? defaultVal : parsed;
      };
      const getIntVal = (optVal, elId, defaultVal) => {
        if (optVal !== undefined && optVal !== null) return optVal;
        const el = document.getElementById(elId);
        const parsed = el ? parseInt(el.value) : NaN;
        return isNaN(parsed) ? defaultVal : parsed;
      };

      if (seedType === 'point') {
        uiParams.pointCenterX = getVal(options.pointCenterX, 'streamlinePointCenterX', 0);
        uiParams.pointCenterY = getVal(options.pointCenterY, 'streamlinePointCenterY', 0);
        uiParams.pointCenterZ = getVal(options.pointCenterZ, 'streamlinePointCenterZ', 0);
        uiParams.pointRadius = getVal(options.pointRadius, 'streamlinePointRadius', 1);
        uiParams.pointCount = getIntVal(options.pointCount, 'streamlinePointCount', 100);
      } else if (seedType === 'line') {
        uiParams.lineStartX = getVal(options.lineStartX, 'streamlineLineStartX', 0);
        uiParams.lineStartY = getVal(options.lineStartY, 'streamlineLineStartY', 0);
        uiParams.lineStartZ = getVal(options.lineStartZ, 'streamlineLineStartZ', 0);
        uiParams.lineEndX = getVal(options.lineEndX, 'streamlineLineEndX', 0);
        uiParams.lineEndY = getVal(options.lineEndY, 'streamlineLineEndY', 0);
        uiParams.lineEndZ = getVal(options.lineEndZ, 'streamlineLineEndZ', 0);
        uiParams.lineResolution = getIntVal(options.lineResolution, 'streamlineLineResolution', 50);
      } else if (seedType === 'plane') {
        uiParams.planeOriginX = getVal(options.planeOriginX, 'streamlinePlaneOriginX', 0);
        uiParams.planeOriginY = getVal(options.planeOriginY, 'streamlinePlaneOriginY', 0);
        uiParams.planeOriginZ = getVal(options.planeOriginZ, 'streamlinePlaneOriginZ', 0);
        uiParams.planeNormalX = getVal(options.planeNormalX, 'streamlinePlaneNormalX', 0);
        uiParams.planeNormalY = getVal(options.planeNormalY, 'streamlinePlaneNormalY', 0);
        uiParams.planeNormalZ = getVal(options.planeNormalZ, 'streamlinePlaneNormalZ', 1);
        uiParams.planeWidth = getVal(options.planeWidth, 'streamlinePlaneWidth', 1);
        uiParams.planeHeight = getVal(options.planeHeight, 'streamlinePlaneHeight', 1);
        uiParams.planeResolutionU = getIntVal(options.planeResolutionU, 'streamlinePlaneResolutionU', 10);
        uiParams.planeResolutionV = getIntVal(options.planeResolutionV, 'streamlinePlaneResolutionV', 10);
      }

      // Capture render mode specific parameters (tubes, ribbons, particles)
      uiParams.renderMode = renderMode;
      if (renderMode === 'tubes') {
        uiParams.tubeRadius = getVal(options.tubeRadius, 'streamlineTubeRadius', 0.01);
        uiParams.tubeSides = getIntVal(options.tubeSides, 'streamlineTubeSides', 8);
        const varyRadiusSelect = document.getElementById('streamlineVaryRadius');
        uiParams.varyRadius = options.varyRadius || (varyRadiusSelect ? varyRadiusSelect.value : 'none');
      } else if (renderMode === 'ribbons') {
        uiParams.ribbonWidth = getVal(options.ribbonWidth, 'streamlineRibbonWidth', 0.02);
      } else if (renderMode === 'particles') {
        uiParams.particleCount = getIntVal(options.particleCount, 'streamlineParticleCount', 100);
        uiParams.particleSteps = getIntVal(options.particleSteps, 'streamlineParticleSteps', 50);
      }

      console.log('[Streamlines Backend] Captured UI params for sync:', uiParams);

      // Create stored options for the filter
      const storedOptions = this.createStreamlineStoredOptions(
        result,
        scalarRange,
        options,
        seedType,  // Use seedType captured earlier
        vectorField,
        maxLength,
        stepSize,
        colormapPreset,
        seedPoints,
        direction,
        { params: uiParams },  // Include UI params for sync
        data
      );

      // Store cache_key from backend for collaborative session optimization
      // Followers can use this to fetch cached results instead of recomputing
      if (result.cache_key) {
        storedOptions.cache_key = result.cache_key;
        console.log('[Streamlines Backend] Stored cache_key for sync:', result.cache_key);
      }

      // Remove old streamlines if exists
      this.removeFilter(id);

      // Add to scene (use this.engine, not this.renderingEngine)
      this.engine.addObject(id, group, {
        type: 'filter',
        kind: 'streamlines',
        name: 'Streamlines',
        icon: '<img src="static/assets/icons/streamlines.svg" width="16" height="16" style="filter: brightness(0) invert(1); vertical-align: middle;">',
        removable: true,
        options: storedOptions
      });

      // Extract flat array of scalars for recoloring
      // Note: Using Array.prototype.push.apply or concat instead of spread operator
      // to avoid stack overflow with large arrays
      let flatScalars = [];
      lineData.forEach(line => {
        if (line.scalars && Array.isArray(line.scalars)) {
          // Use concat for large arrays to avoid stack overflow
          flatScalars = flatScalars.concat(line.scalars);
        }
      });

      // Store in active filters
      this.activeFilters.set(id, {
        type: 'streamlines',
        mesh: group,
        options: storedOptions,
        streamlines: lineData,
        scalars: flatScalars,  // Store flat array for easy recoloring
        scalarRange: scalarRange  // Store scalar range
      });

      // AUTO-CHECKING DISABLED: Users must manually enable streamlines filter
      // const streamlineCheckbox = document.getElementById('streamlineFilterEnabled');
      // if (streamlineCheckbox && id !== 'slice-streamlines') {
      //   streamlineCheckbox.checked = true;
      // }

      // Update legend with color field information
      if (window.viewer && typeof window.viewer.onStreamlineFilterUpdated === 'function') {
        console.log('[Streamlines Backend] Updating legend with options:', storedOptions);
        window.viewer.onStreamlineFilterUpdated(id, storedOptions);
      }

      // Attach particle preview if enabled
      if (window.viewer) {
        console.log('[Preview Check Backend] Checking preview conditions:', {
          hasFunction: typeof window.viewer.attachStreamlinePreview === 'function',
          previewEnabled: storedOptions.previewEnabled,
          notSlice: id !== 'slice-streamlines',
          lineDataCount: lineData?.length
        });

        if (typeof window.viewer.attachStreamlinePreview === 'function' && storedOptions.previewEnabled !== false && id !== 'slice-streamlines') {
          console.log('[Preview Call Backend] Calling attachStreamlinePreview from backend flow');
          window.viewer.attachStreamlinePreview(id, lineData, storedOptions);
        } else if (typeof window.viewer.detachStreamlinePreview === 'function') {
          console.log('[Preview Call Backend] Calling detachStreamlinePreview from backend flow');
          window.viewer.detachStreamlinePreview(id);
        }
      }

      // Remove all seed previews after streamlines loaded
      if (this.engine) {
        this.engine.removeObject('streamline-sphere-preview');
        this.engine.removeObject('streamline-line-preview');
        this.engine.removeObject('streamline-plane-preview');
      }
      if (this.sphereBlinkInterval) {
        clearInterval(this.sphereBlinkInterval);
        this.sphereBlinkInterval = null;
      }
      if (this.lineBlinkInterval) {
        clearInterval(this.lineBlinkInterval);
        this.lineBlinkInterval = null;
      }
      if (this.planeBlinkInterval) {
        clearInterval(this.planeBlinkInterval);
        this.planeBlinkInterval = null;
      }

      console.log(`[Streamlines Backend] Created ${streamlines.length} streamlines using shared rendering`);

      // Notify view lock manager of visualization change
      if (this.viewer?.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }

      return id;

    } catch (error) {
      console.error('[Streamlines Backend] Error:', error);
      Utils.notify('Failed to compute streamlines: ' + error.message, 'error');
      return null;
    }
  }

  interpolateVelocity(point, velocityField, grid) {
    // Simple trilinear interpolation
    const [x, y, z] = point;
    const { nx, ny, nz, bounds } = grid;

    if (!grid || !bounds || !Number.isFinite(nx) || !Number.isFinite(ny) || !Number.isFinite(nz)) {
      return this.interpolateUnstructuredVelocity(point, velocityField, grid);
    }

    // Convert to grid coordinates
    const fx = ((x - bounds.xmin) / (bounds.xmax - bounds.xmin)) * (nx - 1);
    const fy = ((y - bounds.ymin) / (bounds.ymax - bounds.ymin)) * (ny - 1);
    const fz = ((z - bounds.zmin) / (bounds.zmax - bounds.zmin)) * (nz - 1);

    const i0 = Math.floor(fx), i1 = Math.min(i0 + 1, nx - 1);
    const j0 = Math.floor(fy), j1 = Math.min(j0 + 1, ny - 1);
    const k0 = Math.floor(fz), k1 = Math.min(k0 + 1, nz - 1);

    if (i0 < 0 || j0 < 0 || k0 < 0) return null;

    // Get velocities at corners
    const v000 = velocityField[i0 + j0 * nx + k0 * nx * ny];
    const v100 = velocityField[i1 + j0 * nx + k0 * nx * ny];
    const v010 = velocityField[i0 + j1 * nx + k0 * nx * ny];
    const v110 = velocityField[i1 + j1 * nx + k0 * nx * ny];
    const v001 = velocityField[i0 + j0 * nx + k1 * nx * ny];
    const v101 = velocityField[i1 + j0 * nx + k1 * nx * ny];
    const v011 = velocityField[i0 + j1 * nx + k1 * nx * ny];
    const v111 = velocityField[i1 + j1 * nx + k1 * nx * ny];

    // Interpolation weights
    const tx = fx - i0;
    const ty = fy - j0;
    const tz = fz - k0;

    // Trilinear interpolation
    const vx =
      v000[0] * (1 - tx) * (1 - ty) * (1 - tz) +
      v100[0] * tx * (1 - ty) * (1 - tz) +
      v010[0] * (1 - tx) * ty * (1 - tz) +
      v110[0] * tx * ty * (1 - tz) +
      v001[0] * (1 - tx) * (1 - ty) * tz +
      v101[0] * tx * (1 - ty) * tz +
      v011[0] * (1 - tx) * ty * tz +
      v111[0] * tx * ty * tz;

    const vy =
      v000[1] * (1 - tx) * (1 - ty) * (1 - tz) +
      v100[1] * tx * (1 - ty) * (1 - tz) +
      v010[1] * (1 - tx) * ty * (1 - tz) +
      v110[1] * tx * ty * (1 - tz) +
      v001[1] * (1 - tx) * (1 - ty) * tz +
      v101[1] * tx * (1 - ty) * tz +
      v011[1] * (1 - tx) * ty * tz +
      v111[1] * tx * ty * tz;

    const vz =
      v000[2] * (1 - tx) * (1 - ty) * (1 - tz) +
      v100[2] * tx * (1 - ty) * (1 - tz) +
      v010[2] * (1 - tx) * ty * (1 - tz) +
      v110[2] * tx * ty * (1 - tz) +
      v001[2] * (1 - tx) * (1 - ty) * tz +
      v101[2] * tx * (1 - ty) * tz +
      v011[2] * (1 - tx) * ty * tz +
      v111[2] * tx * ty * tz;

    return [vx, vy, vz];
  }

  // Check if point is in bounds
  isInBounds(point, grid) {
    const [x, y, z] = point;
    const { bounds } = grid;
    return x >= bounds.xmin && x <= bounds.xmax &&
      y >= bounds.ymin && y <= bounds.ymax &&
      z >= bounds.zmin && z <= bounds.zmax;
  }

  // Generate seed points for streamlines
  generateSeedPoints(data, count) {
    const { bounds } = data.grid;
    const points = [];

    for (let i = 0; i < count; i++) {
      points.push([
        bounds.xmin + Math.random() * (bounds.xmax - bounds.xmin),
        bounds.ymin + Math.random() * (bounds.ymax - bounds.ymin),
        bounds.zmin + Math.random() * (bounds.zmax - bounds.zmin)
      ]);
    }

    return points;
  }

  // Create slice
  // Note: mesh_parts accepted for consistency; frontend slice uses merged grid data
  createSlice(data, options = {}) {
    const {
      plane = 'xy',
      position = 0,
      variable = 'pressure',
      colormap = 'viridis',
      id = Utils.generateId(),
      mesh_parts = null  // Accepted for consistency; frontend slice uses merged grid
    } = options;

    const scalars = data?.fields?.[variable];
    if (!Array.isArray(scalars) || scalars.length === 0) {
      throw new Error(`Variable ${variable} not found`);
    }

    // Create slice geometry
    const vertices = [];
    const colors = [];

    const verticesSource = Array.isArray(data.vertices)
      ? data.vertices
      : (data.grid?.points || []);

    if (!Array.isArray(verticesSource) || verticesSource.length === 0) {
      throw new Error('No vertex data available for slicing');
    }

    const bounds = data.grid?.bounds || {
      xmin: -1, xmax: 1,
      ymin: -1, ymax: 1,
      zmin: -1, zmax: 1
    };

    const range = this.getScalarRange(scalars);

    // Get solid color for main mesh colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('colormapPreset') : null;

    verticesSource.forEach((vertex, i) => {
      if (!vertex) return;
      const scalarValue = scalars[i];
      if (scalarValue == null) return;

      let check = false;
      const tolerance = this.getSliceTolerance(bounds, plane);

      if (plane === 'xy') {
        check = Math.abs(vertex[2] - position) <= tolerance;
      } else if (plane === 'xz') {
        check = Math.abs(vertex[1] - position) <= tolerance;
      } else if (plane === 'yz') {
        check = Math.abs(vertex[0] - position) <= tolerance;
      }

      if (check) {
        vertices.push(vertex);
        const normalized = this.normalizeScalar(scalarValue, range);
        const color = Colormap.getColor(normalized, colormap, solidColorHex);
        colors.push([color[0] / 255, color[1] / 255, color[2] / 255]);
      }
    });

    if (vertices.length === 0) {
      Utils.notify('No data found at slice position', 'warning');
      return null;
    }

    const storedOptions = {
      plane,
      position,
      variable,
      colormap
    };

    const points = this.engine.createPointsFromData(vertices, colors, 0.05);
    this.engine.addObject(id, points, {
      type: 'filter',
      kind: 'slice',
      name: `Slice (${plane.toUpperCase()})`,
      icon: '✂️',
      removable: true,
      options: storedOptions
    });
    this.activeFilters.set(id, { type: 'slice', options: storedOptions });

    return id;
  }

  // Create vector field
  createVectorField(data, options = {}) {
    const {
      variable = 'velocity',
      scale = 1.0,
      skipFactor = 2,
      colormap = 'viridis',
      id = Utils.generateId()
    } = options;

    const vectors = data?.fields?.[variable];
    if (!Array.isArray(vectors) || vectors.length === 0) {
      throw new Error(`Variable ${variable} not found`);
    }

    const verticesSource = Array.isArray(data?.vertices)
      ? data.vertices
      : (Array.isArray(data?.grid?.vertices) ? data.grid.vertices : null);

    if (!Array.isArray(verticesSource) || verticesSource.length === 0) {
      throw new Error('No vertex data available for vector field');
    }

    const group = new THREE.Group();
    let maxMagnitude = 0;

    vectors.forEach(vector => {
      if (!Array.isArray(vector)) return;
      const m = Math.hypot(...vector);
      if (Number.isFinite(m)) {
        maxMagnitude = Math.max(maxMagnitude, m);
      }
    });

    if (maxMagnitude <= 0) {
      maxMagnitude = 1;
    }

    // Get solid color for main mesh colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('colormapPreset') : null;

    // Create arrows
    verticesSource.forEach((vertex, i) => {
      if (i % skipFactor !== 0) return;

      const vector = vectors[i];
      const magnitude = Math.hypot(...vector);
      if (magnitude < 0.001) return;

      const dir = new THREE.Vector3(...vector).normalize();
      const origin = new THREE.Vector3(...vertex);
      const length = magnitude * scale;
      const normalized = Math.min(1, Math.max(0, magnitude / maxMagnitude));
      const color = Colormap.getColorThree(normalized, colormap, solidColorHex);

      const arrow = new THREE.ArrowHelper(dir, origin, length, color);
      group.add(arrow);
    });

    const storedOptions = {
      variable,
      scale,
      skipFactor,
      colormap
    };

    this.engine.addObject(id, group, {
      type: 'filter',
      kind: 'vectors',
      name: 'Vector Field',
      icon: '➡️',
      removable: true,
      options: storedOptions
    });
    this.activeFilters.set(id, { type: 'vectors', options: storedOptions });

    return id;
  }

  // Create threshold filter
  createThreshold(data, options = {}) {
    const {
      variable = 'pressure',
      min = -Infinity,
      max = Infinity,
      colormap = 'viridis',
      id = Utils.generateId()
    } = options;

    const scalars = data?.fields?.[variable];
    if (!Array.isArray(scalars) || scalars.length === 0) {
      throw new Error(`Variable ${variable} not found`);
    }

    const vertices = [];
    const colors = [];

    const verticesSource = Array.isArray(data.vertices)
      ? data.vertices
      : (data.grid?.points || []);

    if (!Array.isArray(verticesSource) || verticesSource.length === 0) {
      throw new Error('No vertex data available for threshold filter');
    }

    const range = this.getScalarRange(scalars);

    // Get solid color for main mesh colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('colormapPreset') : null;

    verticesSource.forEach((vertex, i) => {
      const value = scalars[i];
      if (value == null) return;
      if (value >= min && value <= max) {
        vertices.push(vertex);
        const normalized = this.normalizeScalar(value, range);
        const color = Colormap.getColor(normalized, colormap, solidColorHex);
        colors.push([color[0] / 255, color[1] / 255, color[2] / 255]);
      }
    });

    if (vertices.length === 0) {
      Utils.notify('No data within threshold range', 'warning');
      return null;
    }

    const storedOptions = {
      variable,
      min,
      max,
      colormap
    };

    const points = this.engine.createPointsFromData(vertices, colors, 0.05);
    this.engine.addObject(id, points, {
      type: 'filter',
      kind: 'threshold',
      name: 'Threshold',
      icon: '📊',
      removable: true,
      options: storedOptions
    });
    this.activeFilters.set(id, { type: 'threshold', options: storedOptions });

    return id;
  }

  // ========================================================================
  // UTILITY METHODS
  // ========================================================================

  getColorFromColormap(t, colormapName) {
    // Use viewer's colormap or fallback
    if (window.viewer && window.viewer.getColorFromNormalizedValue) {
      const color = window.viewer.getColorFromNormalizedValue(t);
      return new THREE.Color(color[0], color[1], color[2]);
    }

    // Fallback: simple viridis-like colormap
    const r = Math.max(0, Math.min(1, 1.5 - Math.abs(2 * t - 1)));
    const g = Math.max(0, Math.min(1, 1.5 - Math.abs(2 * t - 0.5)));
    const b = Math.max(0, Math.min(1, 1.5 - Math.abs(2 * t)));
    return new THREE.Color(r, g, b);
  }

  getScalarRange(scalars) {
    let min = Infinity;
    let max = -Infinity;
    scalars.forEach(value => {
      if (!Number.isFinite(value)) {
        return;
      }
      if (value < min) min = value;
      if (value > max) max = value;
    });

    if (min === Infinity || max === -Infinity) {
      min = 0;
      max = 1;
    }

    return {
      min,
      max,
      span: max - min
    };
  }

  normalizeScalar(value, range) {
    if (!range) return 0;
    const span = range.span || (range.max - range.min);
    if (!Number.isFinite(value) || !Number.isFinite(span) || span === 0) {
      return 0.5;
    }
    return Math.min(1, Math.max(0, (value - range.min) / span));
  }

  getSliceTolerance(bounds, plane) {
    let span = 1;
    if (plane === 'xy') {
      span = (bounds?.zmax ?? 1) - (bounds?.zmin ?? 0);
    } else if (plane === 'xz') {
      span = (bounds?.ymax ?? 1) - (bounds?.ymin ?? 0);
    } else {
      span = (bounds?.xmax ?? 1) - (bounds?.xmin ?? 0);
    }
    return Math.max(span * 0.01, 1e-3);
  }

  numberOr(value, fallback) {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  }

  getDefaultVectorField(data) {
    const fields = data?.fields;
    if (!fields) {
      return 'velocity';
    }

    if (fields.U) {
      return 'U';
    }

    if (fields.velocity) {
      return 'velocity';
    }

    for (const [name, value] of Object.entries(fields)) {
      if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
        return name;
      }
    }

    const keys = Object.keys(fields);
    return keys.length > 0 ? keys[0] : 'velocity';
  }

  extractBounds(data) {
    const fallback = { xmin: -1, xmax: 1, ymin: -1, ymax: 1, zmin: -1, zmax: 1 };

    // Try multiple sources for bounds:
    // 1. data.grid.bounds (OpenFOAM/Fluent/EnSight style object)
    // 2. data.bounds (array format: [xmin, xmax, ymin, ymax, zmin, zmax])
    // 3. data.volume.bounds
    // 4. viewer's scene bounds from rendering engine (fallback)
    let bounds = data?.grid?.bounds;

    // Handle array format: [xmin, xmax, ymin, ymax, zmin, zmax]
    if (!bounds && Array.isArray(data?.bounds) && data.bounds.length >= 6) {
      bounds = {
        xmin: data.bounds[0],
        xmax: data.bounds[1],
        ymin: data.bounds[2],
        ymax: data.bounds[3],
        zmin: data.bounds[4],
        zmax: data.bounds[5]
      };
    }

    // Try volume bounds
    if (!bounds && data?.volume?.bounds) {
      bounds = data.volume.bounds;
    }

    // Try to get bounds from the rendering engine's scene
    if (!bounds && this.engine) {
      const sceneBounds = this.engine.getSceneBounds?.();
      if (sceneBounds) {
        bounds = {
          xmin: sceneBounds.min?.x,
          xmax: sceneBounds.max?.x,
          ymin: sceneBounds.min?.y,
          ymax: sceneBounds.max?.y,
          zmin: sceneBounds.min?.z,
          zmax: sceneBounds.max?.z
        };
      }
    }

    if (!bounds) return fallback;

    const sanitize = (val) => {
      return Number.isFinite(val) ? val : null;
    };

    const xmin = sanitize(bounds.xmin);
    const xmax = sanitize(bounds.xmax);
    const ymin = sanitize(bounds.ymin);
    const ymax = sanitize(bounds.ymax);
    const zmin = sanitize(bounds.zmin);
    const zmax = sanitize(bounds.zmax);

    return {
      xmin: xmin ?? fallback.xmin,
      xmax: xmax ?? fallback.xmax,
      ymin: ymin ?? fallback.ymin,
      ymax: ymax ?? fallback.ymax,
      zmin: zmin ?? fallback.zmin,
      zmax: zmax ?? fallback.zmax
    };
  }

  getStreamlineDefaultParams(data) {
    const bounds = this.extractBounds(data);
    const center = [
      (bounds.xmin + bounds.xmax) / 2,
      (bounds.ymin + bounds.ymax) / 2,
      (bounds.zmin + bounds.zmax) / 2
    ];

    const spanX = Math.max(bounds.xmax - bounds.xmin, 0.01);
    const spanY = Math.max(bounds.ymax - bounds.ymin, 0.01);
    const spanZ = Math.max(bounds.zmax - bounds.zmin, 0.01);
    const longest = Math.max(spanX, spanY, spanZ, 0.01);
    const defaultStep = Math.min(Math.max(longest / 200, 0.00005), Math.max(longest / 10, 0.00005));
    const defaultLength = Math.max(50, Math.round((longest / defaultStep) * 2));

    let lineStart;
    let lineEnd;
    if (spanX >= spanY && spanX >= spanZ) {
      lineStart = [bounds.xmin, center[1], center[2]];
      lineEnd = [bounds.xmax, center[1], center[2]];
    } else if (spanY >= spanX && spanY >= spanZ) {
      lineStart = [center[0], bounds.ymin, center[2]];
      lineEnd = [center[0], bounds.ymax, center[2]];
    } else {
      lineStart = [center[0], center[1], bounds.zmin];
      lineEnd = [center[0], center[1], bounds.zmax];
    }

    let planeNormal;
    if (spanZ <= spanX && spanZ <= spanY) {
      planeNormal = [0, 0, 1];
    } else if (spanY <= spanX && spanY <= spanZ) {
      planeNormal = [0, 1, 0];
    } else {
      planeNormal = [1, 0, 0];
    }

    const isOpenFoamSource = data?.source === 'OpenFOAM' || data?.metadata?.format === 'OpenFOAM';
    const sampleGridPoints = Array.isArray(data?.grid?.points) ? data.grid.points.length : 0;
    const openFoamPointCount = sampleGridPoints
      ? Math.min(800, Math.max(300, Math.round(sampleGridPoints / 800)))
      : 400;

    const defaults = {
      pointCenter: center,
      pointRadius: longest / 2,  // Half of the longest mesh dimension
      pointCount: isOpenFoamSource ? openFoamPointCount : 200,
      lineStart,
      lineEnd,
      lineResolution: 100,
      planeOrigin: center,
      planeNormal,
      planeWidth: spanX || longest,
      planeHeight: spanY || longest,
      planeResolutionU: 20,
      planeResolutionV: 20,
      stepSize: defaultStep,
      maxLength: defaultLength,
      direction: 'both',
      previewDotScale: 0.004,
      previewSpeed: longest * 0.2,
      previewEnabled: false,
      useGridSampling: isOpenFoamSource
    };

    const key = this.getStreamlineSettingsKey(data);
    if (key && this.streamlineUserSettings.has(key)) {
      const stored = this.streamlineUserSettings.get(key);
      if (stored?.pointRadius != null && Number.isFinite(stored.pointRadius)) {
        defaults.pointRadius = stored.pointRadius;
      }
      if (stored?.pointCount != null && Number.isFinite(stored.pointCount)) {
        defaults.pointCount = Math.max(1, Math.round(stored.pointCount));
      }
    }

    return defaults;
  }

  generateStreamlineSeeds(data, options = {}) {
    const defaults = this.getStreamlineDefaultParams(data);
    const seedType = options.seedType || 'line';

    if (data?.grid && !data.grid._vertexSearchBuilt) {
      this.buildVertexSpatialIndex(data.grid);
    }

    switch (seedType) {
      case 'line':
        return this.generateLineSeedData(options, defaults);
      case 'plane':
        return this.generatePlaneSeedData(options, defaults);
      case 'point':
      default:
        return this.generatePointSeedData(options, defaults);
    }
  }

  generatePointSeedData(options, defaults) {
    const center = [
      this.numberOr(options.pointCenterX, defaults.pointCenter[0]),
      this.numberOr(options.pointCenterY, defaults.pointCenter[1]),
      this.numberOr(options.pointCenterZ, defaults.pointCenter[2])
    ];
    const radius = Math.max(this.numberOr(options.pointRadius, defaults.pointRadius), 0.001);
    const count = Math.max(1, Math.round(this.numberOr(options.pointCount ?? options.seedCount, defaults.pointCount)));
    const radiusSq = radius * radius;
    const gridPoints = Array.isArray(options.__gridPoints) ? options.__gridPoints : null;
    const hasGridPoints = Array.isArray(gridPoints) && gridPoints.length > 0;
    const useGridSampling = options.useGridSampling !== undefined
      ? options.useGridSampling
      : (Array.isArray(options.__gridPoints) && options.__gridPoints.length > 0)
      || (options.__dataSource === 'OpenFOAM');

    const seeds = [];
    if (useGridSampling && hasGridPoints) {
      const filtered = radius > 0
        ? gridPoints.filter((pt) => {
          if (!Array.isArray(pt) || pt.length < 3) return false;
          const dx = pt[0] - center[0];
          const dy = pt[1] - center[1];
          const dz = pt[2] - center[2];
          return dx * dx + dy * dy + dz * dz <= radiusSq;
        })
        : gridPoints;
      const source = filtered.length >= count ? filtered : gridPoints;
      const used = new Set();
      let attempts = 0;
      while (seeds.length < count && attempts < count * 10) {
        const idx = Math.floor(Math.random() * source.length);
        const key = source === gridPoints ? idx : `${idx}:${attempts}`;
        if (used.has(key)) {
          attempts++;
          continue;
        }
        used.add(key);
        const pt = source[idx];
        if (!Array.isArray(pt) || pt.length < 3) {
          attempts++;
          continue;
        }
        seeds.push([pt[0], pt[1], pt[2]]);
      }
    }

    if (seeds.length === 0) {
      let attempts = 0;
      while (seeds.length < count && attempts < count * 20) {
        const point = this.randomPointInSphere(radius);
        if (point) {
          seeds.push([
            center[0] + point[0],
            center[1] + point[1],
            center[2] + point[2]
          ]);
        }
        attempts++;
      }
    }

    if (seeds.length === 0) {
      seeds.push(center.slice());
    }

    return {
      seeds,
      params: {
        pointCenterX: center[0],
        pointCenterY: center[1],
        pointCenterZ: center[2],
        pointRadius: radius,
        pointCount: count,
        useGridSampling: useGridSampling
      }
    };
  }

  generateLineSeedData(options, defaults) {
    const start = [
      this.numberOr(options.lineStartX, defaults.lineStart[0]),
      this.numberOr(options.lineStartY, defaults.lineStart[1]),
      this.numberOr(options.lineStartZ, defaults.lineStart[2])
    ];
    const end = [
      this.numberOr(options.lineEndX, defaults.lineEnd[0]),
      this.numberOr(options.lineEndY, defaults.lineEnd[1]),
      this.numberOr(options.lineEndZ, defaults.lineEnd[2])
    ];
    const resolution = Math.max(2, Math.round(this.numberOr(options.lineResolution, defaults.lineResolution)));

    const seeds = [];
    if (resolution === 1) {
      seeds.push(start.slice());
    } else {
      for (let i = 0; i < resolution; i++) {
        const t = i / (resolution - 1);
        seeds.push([
          start[0] + (end[0] - start[0]) * t,
          start[1] + (end[1] - start[1]) * t,
          start[2] + (end[2] - start[2]) * t
        ]);
      }
    }

    return {
      seeds,
      params: {
        lineStartX: start[0],
        lineStartY: start[1],
        lineStartZ: start[2],
        lineEndX: end[0],
        lineEndY: end[1],
        lineEndZ: end[2],
        lineResolution: resolution
      }
    };
  }

  generatePlaneSeedData(options, defaults) {
    const origin = [
      this.numberOr(options.planeOriginX, defaults.planeOrigin[0]),
      this.numberOr(options.planeOriginY, defaults.planeOrigin[1]),
      this.numberOr(options.planeOriginZ, defaults.planeOrigin[2])
    ];
    const normalVec = new THREE.Vector3(
      this.numberOr(options.planeNormalX, defaults.planeNormal[0]),
      this.numberOr(options.planeNormalY, defaults.planeNormal[1]),
      this.numberOr(options.planeNormalZ, defaults.planeNormal[2])
    );
    if (normalVec.lengthSq() < 1e-6) {
      normalVec.set(0, 0, 1);
    }
    normalVec.normalize();

    const width = Math.max(this.numberOr(options.planeWidth, defaults.planeWidth), 0.01);
    const height = Math.max(this.numberOr(options.planeHeight, defaults.planeHeight), 0.01);
    const resU = Math.max(2, Math.round(this.numberOr(options.planeResolutionU, defaults.planeResolutionU)));
    const resV = Math.max(2, Math.round(this.numberOr(options.planeResolutionV, defaults.planeResolutionV)));

    const { axisU, axisV } = this.computePlaneAxes(normalVec);
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    const seeds = [];
    for (let i = 0; i < resU; i++) {
      const u = resU === 1 ? 0 : ((i / (resU - 1)) * width - halfWidth);
      for (let j = 0; j < resV; j++) {
        const v = resV === 1 ? 0 : ((j / (resV - 1)) * height - halfHeight);
        const offset = axisU.clone().multiplyScalar(u).add(axisV.clone().multiplyScalar(v));
        seeds.push([
          origin[0] + offset.x,
          origin[1] + offset.y,
          origin[2] + offset.z
        ]);
      }
    }

    return {
      seeds,
      params: {
        planeOriginX: origin[0],
        planeOriginY: origin[1],
        planeOriginZ: origin[2],
        planeNormalX: normalVec.x,
        planeNormalY: normalVec.y,
        planeNormalZ: normalVec.z,
        planeWidth: width,
        planeHeight: height,
        planeResolutionU: resU,
        planeResolutionV: resV
      }
    };
  }

  randomPointInSphere(radius) {
    for (let i = 0; i < 10; i++) {
      const x = Math.random() * 2 - 1;
      const y = Math.random() * 2 - 1;
      const z = Math.random() * 2 - 1;
      const lenSq = x * x + y * y + z * z;
      if (lenSq > 1 || lenSq === 0) continue;
      const scale = radius * Math.cbrt(Math.random());
      const invLen = scale / Math.sqrt(lenSq);
      return [x * invLen, y * invLen, z * invLen];
    }
    return null;
  }

  updateStoredStreamlineSettings(data, updates = {}) {
    if (!updates || Object.keys(updates).length === 0) return;
    const key = this.getStreamlineSettingsKey(data);
    if (!key) return;
    const existing = this.streamlineUserSettings.get(key) || {};
    const sanitized = { ...existing };
    if (updates.pointRadius != null && Number.isFinite(updates.pointRadius)) {
      sanitized.pointRadius = Math.max(0.001, updates.pointRadius);
    }
    if (updates.pointCount != null && Number.isFinite(updates.pointCount)) {
      sanitized.pointCount = Math.max(1, Math.round(updates.pointCount));
    }
    this.streamlineUserSettings.set(key, sanitized);
  }

  getStreamlineSettingsKey(data) {
    if (!data) return 'default';
    return data.case_path
      || data.metadata?.case_path
      || data.metadata?.fileName
      || data.metadata?.case
      || 'default';
  }

  getFirstStreamlineFilterId() {
    for (const [id, entry] of this.activeFilters.entries()) {
      if (entry?.type === 'streamlines') {
        return id;
      }
    }
    return null;
  }

  hasActiveStreamlines() {
    for (const entry of this.activeFilters.values()) {
      if (entry?.type === 'streamlines') {
        return true;
      }
    }
    return false;
  }

  async recomputeStreamlinesForData(data) {
    if (!data) return;
    const entries = [];
    this.activeFilters.forEach((entry, id) => {
      if (entry?.type === 'streamlines') {
        entries.push([id, entry]);
      }
    });

    for (const [id, entry] of entries) {
      try {
        const baseOptions = typeof structuredClone === 'function'
          ? structuredClone(entry.options || {})
          : JSON.parse(JSON.stringify(entry.options || {}));
        delete baseOptions.seedPoints;
        await this.createStreamlines(data, {
          ...baseOptions,
          id
        });
      } catch (error) {
        console.error(`Streamline recompute failed for ${id}:`, error);
      }
    }
  }

  updateStreamlineVisualization(updates = {}) {
    if (!updates || Object.keys(updates).length === 0) {
      return;
    }

    const targetId = this.getFirstStreamlineFilterId();
    if (!targetId) {
      return;
    }

    const entry = this.activeFilters.get(targetId);
    if (!entry) {
      return;
    }

    const sanitized = { ...updates };

    if (sanitized.previewEnabled !== undefined) {
      sanitized.previewEnabled = !!sanitized.previewEnabled;
    }

    if (sanitized.previewDotScale !== undefined) {
      let scale = parseFloat(sanitized.previewDotScale);
      if (!Number.isFinite(scale) || scale <= 0) {
        scale = entry.options.previewDotScale ?? 0.004;
      }
      sanitized.previewDotScale = Math.max(scale, 0.0001);
    }

    if (sanitized.previewSpeed !== undefined) {
      let speed = parseFloat(sanitized.previewSpeed);
      if (!Number.isFinite(speed) || speed < 0) {
        speed = entry.options.previewSpeed ?? 0;
      }
      sanitized.previewSpeed = Math.max(speed, 0);
    }

    if (sanitized.colormap !== undefined && typeof sanitized.colormap !== 'string') {
      delete sanitized.colormap;
    }

    const keys = Object.keys(sanitized);
    if (keys.length === 0) {
      return;
    }

    const changedKeys = keys.filter(key => {
      const previous = entry.options[key];
      const next = sanitized[key];
      if (typeof previous === 'number' && typeof next === 'number') {
        return Math.abs(previous - next) > 1e-9;
      }
      return previous !== next;
    });

    if (changedKeys.length === 0) {
      return;
    }

    changedKeys.forEach(key => {
      entry.options[key] = sanitized[key];
    });

    this.rebuildStreamlineGeometry(targetId, entry);

    if (this.viewer) {
      if (typeof this.viewer.onStreamlineFilterUpdated === 'function') {
        this.viewer.onStreamlineFilterUpdated(targetId, entry.options);
      }

      if (typeof this.viewer.attachStreamlinePreview === 'function' && targetId !== 'slice-streamlines' && Array.isArray(entry.streamlines) && entry.streamlines.length > 0 && entry.options.previewEnabled !== false) {
        this.viewer.attachStreamlinePreview(targetId, entry.streamlines, entry.options);
      } else if (typeof this.viewer.detachStreamlinePreview === 'function') {
        this.viewer.detachStreamlinePreview(targetId);
      }

      if (typeof this.viewer.selectPipelineItem === 'function') {
        this.viewer.selectPipelineItem(targetId);
      }
    }

    // Don't sync controls - visualization updates shouldn't change input values
  }

  buildStreamlineLine(points, scalars, index, total, colormap, scalarRange) {
    const positionCount = Array.isArray(points) ? points.length : 0;

    // Get solid color for this dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('streamlineColormap') : null;

    if (positionCount < 2) {
      return this.engine.createLineFromData(points || [], Colormap.getColorThree(0, colormap, solidColorHex));
    }

    // If colormap is 'none', use a single solid color
    if (colormap === 'none') {
      const solidColor = new THREE.Color(0x4a9eff); // Blue color for solid streamlines
      return this.engine.createLineFromData(points, solidColor, undefined);
    }

    const baseColor = Colormap.getColorThree(total > 1 ? index / Math.max(total - 1, 1) : 0, colormap, solidColorHex);
    const hasScalarRange = scalarRange && Number.isFinite(scalarRange.min) && Number.isFinite(scalarRange.max) && scalarRange.max > scalarRange.min;
    const hasScalars = Array.isArray(scalars) && scalars.length === positionCount && hasScalarRange;

    let vertexColors = null;
    if (hasScalars) {
      const range = scalarRange.max - scalarRange.min;
      vertexColors = new Float32Array(positionCount * 3);
      for (let i = 0; i < scalars.length; i++) {
        const value = scalars[i];
        let normalized = 0.5;
        if (Number.isFinite(value)) {
          normalized = (value - scalarRange.min) / range;
        }
        normalized = Math.max(0, Math.min(1, normalized));
        const color = Colormap.getColorThree(normalized, colormap, solidColorHex);
        vertexColors[i * 3] = color.r;
        vertexColors[i * 3 + 1] = color.g;
        vertexColors[i * 3 + 2] = color.b;
      }
    }

    const lineObj = this.engine.createLineFromData(points, baseColor, vertexColors ? { vertexColors } : undefined);

    // Store scalar data in userData for later recoloring
    if (hasScalars && lineObj) {
      lineObj.userData.scalars = scalars;
      lineObj.userData.scalarRange = scalarRange;
    }

    return lineObj;
  }

  computeScalarRangeFromStreamlines(streamlines) {
    if (!Array.isArray(streamlines) || streamlines.length === 0) {
      return null;
    }
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    streamlines.forEach(entry => {
      if (!entry || !Array.isArray(entry.scalars)) return;
      entry.scalars.forEach(value => {
        if (Number.isFinite(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      });
    });
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return null;
    }
    return { min, max };
  }

  rebuildStreamlineGeometry(id, entry) {
    if (!entry) return;
    let streamlines = Array.isArray(entry.streamlines) ? entry.streamlines : [];
    if (!Array.isArray(streamlines) || streamlines.length === 0) {
      const engineEntry = this.engine.objects.get(id);
      if (engineEntry?.object) {
        const lineChildren = (engineEntry.object.children || []).filter(child => child?.isLine);
        const reconstructed = lineChildren.map(line => {
          const attr = line.geometry?.attributes?.position;
          if (!attr || !attr.array) return null;
          const dataArray = attr.array;
          const points = [];
          for (let i = 0; i < dataArray.length; i += 3) {
            points.push([dataArray[i], dataArray[i + 1], dataArray[i + 2]]);
          }
          const scalars = Array.isArray(line.userData?.scalars)
            ? line.userData.scalars.slice()
            : null;
          return points.length > 1 ? { points, scalars } : null;
        }).filter(item => item && Array.isArray(item.points));
        if (reconstructed.length > 0) {
          streamlines = reconstructed;
          entry.streamlines = reconstructed;
        }
      }
    }

    if (streamlines.length === 0) {
      return;
    }

    const options = entry.options || {};
    const colormap = options.colormap || 'jet';

    let scalarRange = options.scalarRange;
    if (!scalarRange || !Number.isFinite(scalarRange.min) || !Number.isFinite(scalarRange.max) || scalarRange.max <= scalarRange.min) {
      scalarRange = this.computeScalarRangeFromStreamlines(streamlines);
      if (scalarRange) {
        options.scalarRange = scalarRange;
      }
    }

    this.engine.removeObject(id);

    const group = new THREE.Group();

    // Set visibility based on checkbox state
    // Special case: slice streamlines use their own checkbox
    if (id === 'slice-streamlines') {
      const sliceStreamlineCheckbox = document.getElementById('sliceShowStreamlines');
      group.visible = sliceStreamlineCheckbox ? sliceStreamlineCheckbox.checked : true;
    } else {
      const checkbox = document.getElementById('streamlineFilterEnabled');
      group.visible = checkbox ? checkbox.checked : true;
    }

    streamlines.forEach((item, index) => {
      if (!item || !Array.isArray(item.points) || item.points.length < 2) return;
      const lineObj = this.buildStreamlineLine(
        item.points,
        Array.isArray(item.scalars) ? item.scalars : null,
        index,
        streamlines.length,
        colormap,
        scalarRange
      );
      group.add(lineObj);
    });

    const metadata = {
      type: 'filter',
      kind: 'streamlines',
      name: 'Streamlines',
      icon: '<img src="static/assets/icons/streamlines.svg" width="16" height="16" style="filter: brightness(0) invert(1); vertical-align: middle;">',
      removable: true,
      options
    };

    this.engine.addObject(id, group, metadata);
  }

  refreshStreamlineVisuals(id, entry) {
    if (!entry) return;
    const engineEntry = this.engine.objects.get(id);
    if (!engineEntry?.object) return;

    const data = Array.isArray(entry.streamlines) ? entry.streamlines : [];
    const total = data.length;
    const options = entry.options || {};
    const colormap = options.colormap || 'jet';

    console.log(`[Streamlines] refreshStreamlineVisuals: id=${id}, colormap=${colormap}, dataLength=${data.length}, hasScalarRange=${!!options.scalarRange}`);

    let scalarRange = options.scalarRange;
    if (!scalarRange || !Number.isFinite(scalarRange.min) || !Number.isFinite(scalarRange.max) || scalarRange.max <= scalarRange.min) {
      scalarRange = this.computeScalarRangeFromStreamlines(data);
      if (scalarRange) {
        options.scalarRange = scalarRange;
      }
    }

    console.log(`[Streamlines] scalarRange:`, scalarRange);

    // Get solid color for streamline colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('streamlineColormap') : null;

    const lineObjects = (engineEntry.object.children || []).filter(child => child?.isLine);
    console.log(`[Streamlines] lineObjects count: ${lineObjects.length}, data items: ${data.length}`);

    if (data.length > 0 && data[0]) {
      console.log(`[Streamlines] First data item has scalars: ${Array.isArray(data[0].scalars)}, scalars length: ${data[0].scalars?.length}`);
    }

    lineObjects.forEach((line, index) => {
      const info = data[index] || data[data.length - 1] || null;
      const fallbackColor = Colormap.getColorThree(total > 1 ? index / Math.max(total - 1, 1) : 0, colormap, solidColorHex);
      this.applyColorsToLine(line, info, fallbackColor, colormap, options.scalarRange, solidColorHex);
    });
  }

  applyColorsToLine(line, info, fallbackColor, colormap, scalarRange, solidColorHex = null) {
    if (!line || !line.geometry?.attributes?.position) {
      return;
    }

    const geometry = line.geometry;
    const vertexCount = geometry.attributes.position.count;
    const scalars = info?.scalars;
    const hasRange = scalarRange && Number.isFinite(scalarRange.min) && Number.isFinite(scalarRange.max) && scalarRange.max > scalarRange.min;

    // Debug: log condition check for first line only
    if (!this._applyColorsDebugLogged) {
      console.log(`[applyColorsToLine] vertexCount=${vertexCount}, scalarsLength=${scalars?.length}, hasRange=${hasRange}, colormap=${colormap}`);
      console.log(`[applyColorsToLine] Condition: isArray=${Array.isArray(scalars)}, lengthMatch=${scalars?.length === vertexCount}`);
      this._applyColorsDebugLogged = true;
      setTimeout(() => { this._applyColorsDebugLogged = false; }, 1000);
    }

    if (Array.isArray(scalars) && scalars.length === vertexCount && hasRange) {
      const range = scalarRange.max - scalarRange.min;
      let colorAttr = geometry.getAttribute('color');
      if (!colorAttr || !colorAttr.array || colorAttr.array.length !== vertexCount * 3) {
        const array = new Float32Array(vertexCount * 3);
        geometry.setAttribute('color', new THREE.BufferAttribute(array, 3));
        colorAttr = geometry.getAttribute('color');
      }

      if (!colorAttr) {
        return;
      }

      const colorArray = colorAttr.array;
      let sampleColor = null;
      for (let i = 0; i < scalars.length; i++) {
        const value = scalars[i];
        let normalized = 0.5;
        if (Number.isFinite(value)) {
          normalized = (value - scalarRange.min) / range;
        }
        normalized = Math.max(0, Math.min(1, normalized));
        const color = Colormap.getColorThree(normalized, colormap, solidColorHex);
        if (i === 0) sampleColor = { r: color.r, g: color.g, b: color.b, normalized };
        colorArray[i * 3] = color.r;
        colorArray[i * 3 + 1] = color.g;
        colorArray[i * 3 + 2] = color.b;
      }
      colorAttr.needsUpdate = true;

      // Debug: log sample color for first call
      if (sampleColor && !this._sampleColorLogged) {
        console.log(`[applyColorsToLine] Sample color for ${colormap}: r=${sampleColor.r.toFixed(3)}, g=${sampleColor.g.toFixed(3)}, b=${sampleColor.b.toFixed(3)}, normalized=${sampleColor.normalized.toFixed(3)}`);
        this._sampleColorLogged = true;
        setTimeout(() => { this._sampleColorLogged = false; }, 1000);
      }

      // Always create fresh material to ensure shader compiles with vertexColors
      const oldMaterial = line.material;
      line.material = new THREE.LineBasicMaterial({ color: new THREE.Color(1, 1, 1), vertexColors: true });
      if (oldMaterial && typeof oldMaterial.dispose === 'function') {
        oldMaterial.dispose();
      }
    } else {
      if (geometry.getAttribute('color')) {
        geometry.deleteAttribute('color');
      }
      if (!line.material || !line.material.isLineBasicMaterial) {
        line.material = new THREE.LineBasicMaterial({ color: fallbackColor });
      } else {
        line.material.vertexColors = false;
        line.material.color.copy(fallbackColor);
        line.material.needsUpdate = true;
      }
    }
  }

  computePlaneAxes(normal) {
    const candidate = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);

    const axisU = new THREE.Vector3().crossVectors(normal, candidate).normalize();
    if (axisU.lengthSq() < 1e-6) {
      axisU.set(1, 0, 0);
    }
    const axisV = new THREE.Vector3().crossVectors(normal, axisU).normalize();
    return { axisU, axisV };
  }

  interpolateUnstructuredVelocity(point, velocityField, grid) {
    if (!grid || !Array.isArray(grid.vertices) || grid.vertices.length === 0) {
      return null;
    }

    if (!grid._vertexSearchBuilt) {
      this.buildVertexSpatialIndex(grid);
    }

    const search = grid._vertexSearch;
    if (!search) return null;

    const { cellSize, bounds, cells } = search;
    const [x, y, z] = point;

    if (
      x < bounds.xmin || x > bounds.xmax ||
      y < bounds.ymin || y > bounds.ymax ||
      z < bounds.zmin || z > bounds.zmax
    ) {
      return null;
    }

    const ixBase = Math.floor((x - bounds.xmin) / cellSize);
    const iyBase = Math.floor((y - bounds.ymin) / cellSize);
    const izBase = Math.floor((z - bounds.zmin) / cellSize);

    const candidates = [];
    let radius = 0;
    const maxRadius = 4;

    while (candidates.length < 16 && radius <= maxRadius) {
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dz = -radius; dz <= radius; dz++) {
            const key = `${ixBase + dx}|${iyBase + dy}|${izBase + dz}`;
            const list = cells.get(key);
            if (list) {
              candidates.push(...list);
            }
          }
        }
      }
      if (candidates.length >= 16) break;
      radius++;
    }

    if (candidates.length === 0) {
      return null;
    }

    const vertices = grid.vertices;
    let weighted = [0, 0, 0];
    let totalWeight = 0;

    for (let i = 0; i < candidates.length; i++) {
      const idx = candidates[i];
      const vertex = vertices[idx];
      if (!Array.isArray(vertex) || vertex.length < 3) continue;
      const velocity = velocityField[idx];
      if (!velocity) continue;
      const dx = vertex[0] - x;
      const dy = vertex[1] - y;
      const dz = vertex[2] - z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq < 1e-12) {
        return Array.isArray(velocity)
          ? velocity.slice()
          : [velocity.x || 0, velocity.y || 0, velocity.z || 0];
      }
      const weight = 1 / distSq;
      weighted[0] += weight * velocity[0];
      weighted[1] += weight * velocity[1];
      weighted[2] += weight * velocity[2];
      totalWeight += weight;
    }

    if (totalWeight === 0) {
      return null;
    }

    return [
      weighted[0] / totalWeight,
      weighted[1] / totalWeight,
      weighted[2] / totalWeight
    ];
  }

  buildVertexSpatialIndex(grid) {
    if (grid._vertexSearchBuilt) return;
    if (!grid || !Array.isArray(grid.vertices) || grid.vertices.length === 0) {
      return;
    }

    const bounds = grid.bounds || this.computeBoundsFromVertices(grid.vertices);
    const volume = Math.max(
      (bounds.xmax - bounds.xmin) * (bounds.ymax - bounds.ymin) * (bounds.zmax - bounds.zmin),
      1e-9
    );
    const avgSpacing = Math.cbrt(volume / Math.max(grid.vertices.length, 1));
    const cellSize = Math.max(avgSpacing * 2, 1e-4);

    const cells = new Map();

    const getKey = (vx, vy, vz) => {
      const ix = Math.floor((vx - bounds.xmin) / cellSize);
      const iy = Math.floor((vy - bounds.ymin) / cellSize);
      const iz = Math.floor((vz - bounds.zmin) / cellSize);
      return `${ix}|${iy}|${iz}`;
    };

    for (let i = 0; i < grid.vertices.length; i++) {
      const vertex = grid.vertices[i];
      if (!Array.isArray(vertex) || vertex.length < 3) continue;
      const key = getKey(vertex[0], vertex[1], vertex[2]);
      if (!cells.has(key)) {
        cells.set(key, []);
      }
      cells.get(key).push(i);
    }

    grid._vertexSearch = {
      cellSize,
      bounds,
      cells
    };
    grid._vertexSearchBuilt = true;
  }

  computeBoundsFromVertices(vertices) {
    const bounds = {
      xmin: Infinity,
      xmax: -Infinity,
      ymin: Infinity,
      ymax: -Infinity,
      zmin: Infinity,
      zmax: -Infinity
    };

    vertices.forEach(vertex => {
      if (!Array.isArray(vertex) || vertex.length < 3) return;
      bounds.xmin = Math.min(bounds.xmin, vertex[0]);
      bounds.xmax = Math.max(bounds.xmax, vertex[0]);
      bounds.ymin = Math.min(bounds.ymin, vertex[1]);
      bounds.ymax = Math.max(bounds.ymax, vertex[1]);
      bounds.zmin = Math.min(bounds.zmin, vertex[2]);
      bounds.zmax = Math.max(bounds.zmax, vertex[2]);
    });

    return bounds;
  }

  // Remove filter
  async updateFilter(id, data, updates = {}) {
    const entry = this.activeFilters.get(id);
    if (!entry) {
      return null;
    }

    const rawKeys = Object.keys(updates || {});
    if (rawKeys.length === 0) {
      return id;
    }

    const sanitized = { ...updates };

    if (sanitized.previewEnabled !== undefined) {
      if (typeof sanitized.previewEnabled === 'string') {
        sanitized.previewEnabled = sanitized.previewEnabled !== 'false';
      } else {
        sanitized.previewEnabled = !!sanitized.previewEnabled;
      }
    }

    if (sanitized.previewDotScale !== undefined) {
      let scale = parseFloat(sanitized.previewDotScale);
      if (!Number.isFinite(scale) || scale <= 0) {
        scale = entry.options.previewDotScale ?? 0.004;
      }
      sanitized.previewDotScale = Math.max(scale, 0.0001);
    }

    if (sanitized.previewSpeed !== undefined) {
      let speed = parseFloat(sanitized.previewSpeed);
      if (!Number.isFinite(speed) || speed < 0) {
        speed = entry.options.previewSpeed ?? 0;
      }
      sanitized.previewSpeed = Math.max(speed, 0);
    }

    const keys = Object.keys(sanitized);
    if (keys.length === 0) {
      return id;
    }

    const changedKeys = keys.filter(key => {
      const previous = entry.options[key];
      const next = sanitized[key];
      const changed = (typeof previous === 'number' && typeof next === 'number')
        ? Math.abs(previous - next) > 1e-9
        : previous !== next;

      if (key.includes('line') || key.includes('Start') || key.includes('End') || key.includes('Resolution') || key === 'variable' || key === 'colormap') {
        console.log(`[Streamlines Update] Comparing ${key}: ${previous} -> ${next} (changed: ${changed})`);
      }

      return changed;
    });

    if (changedKeys.length === 0) {
      console.log('[Streamlines Update] No changes detected - skipping update');
      return id;
    }

    const effectiveUpdates = {};
    changedKeys.forEach(key => {
      effectiveUpdates[key] = sanitized[key];
    });

    console.log('[Streamlines Update] Changed keys:', changedKeys);
    console.log('[Streamlines Update] Effective updates:', effectiveUpdates);

    // Only preview settings are visualization-only (don't require recreation)
    // Colormap changes require full recreation to sync properly with followers
    const visualizationKeys = new Set(['previewEnabled', 'previewDotScale', 'previewSpeed']);
    const isVisualizationOnly = entry.type === 'streamlines' && changedKeys.every(key => visualizationKeys.has(key));

    console.log('[Streamlines Update] isVisualizationOnly:', isVisualizationOnly, 'changedKeys:', changedKeys);

    if (isVisualizationOnly) {
      Object.assign(entry.options, effectiveUpdates);

      this.rebuildStreamlineGeometry(id, entry);

      if (window.viewer) {
        if (typeof window.viewer.onStreamlineFilterUpdated === 'function') {
          window.viewer.onStreamlineFilterUpdated(id, entry.options);
        }

        if (typeof window.viewer.attachStreamlinePreview === 'function' && id !== 'slice-streamlines' && Array.isArray(entry.streamlines) && entry.streamlines.length > 0 && entry.options.previewEnabled !== false) {
          window.viewer.attachStreamlinePreview(id, entry.streamlines, entry.options);
        } else if (typeof window.viewer.detachStreamlinePreview === 'function') {
          window.viewer.detachStreamlinePreview(id);
        }

        if (typeof window.viewer.selectPipelineItem === 'function') {
          window.viewer.selectPipelineItem(id);
        }

        // Notify view lock manager of visualization change for sync
        if (window.viewer.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      }

      return id;
    }

    const merged = { ...entry.options, ...effectiveUpdates };
    console.log('[Streamlines Update] Merged options for recreation:', merged);

    // For streamlines, remove old seedPoints so they get regenerated with new parameters
    if (entry.type === 'streamlines') {
      delete merged.seedPoints;
      console.log('[Streamlines Update] Removed old seedPoints to force regeneration');
    }

    try {
      switch (entry.type) {
        case 'contour':
          return this.createContour(data, { ...merged, id });
        case 'slice':
          return this.createSlice(data, { ...merged, id });
        case 'streamlines':
          console.log('[Streamlines Update] Recreating streamlines with new parameters');
          return await this.createStreamlines(data, { ...merged, id });
        case 'threshold':
          return this.createThreshold(data, { ...merged, id });
        case 'vectors':
          return this.createVectorField(data, { ...merged, id });
        default:
          console.warn(`Update for filter type "${entry.type}" is not implemented`);
          return null;
      }
    } catch (error) {
      console.error('Failed to update filter:', error);
      return null;
    }
  }

  removeFilter(id) {
    if (this.activeFilters.has(id)) {
      const filterEntry = this.activeFilters.get(id);

      // Special handling for contour filter - it's added directly to scene, not via engine.addObject
      if (id === 'contour-filter' && filterEntry) {
        if (filterEntry.mesh && window.viewer?.engine?.scene) {
          window.viewer.engine.scene.remove(filterEntry.mesh);
          if (filterEntry.mesh.geometry) filterEntry.mesh.geometry.dispose();
          if (filterEntry.mesh.material) filterEntry.mesh.material.dispose();
          console.log('[removeFilter] Removed contour mesh from scene');
        }
        // Remove labels
        if (filterEntry.labels && Array.isArray(filterEntry.labels)) {
          filterEntry.labels.forEach(label => {
            if (label.parentNode) {
              label.parentNode.removeChild(label);
            }
          });
          console.log('[removeFilter] Removed contour labels');
        }
      } else if (id === 'glyph-filter' && filterEntry) {
        // Special handling for glyph filter - it's added directly to scene as InstancedMesh
        if (filterEntry.mesh && window.viewer?.engine?.scene) {
          window.viewer.engine.scene.remove(filterEntry.mesh);
          if (filterEntry.mesh.geometry) filterEntry.mesh.geometry.dispose();
          if (filterEntry.mesh.material) filterEntry.mesh.material.dispose();
          console.log('[removeFilter] Removed glyph mesh from scene');
        }
        // Hide glyph legend
        if (window.viewer && typeof window.viewer.hideGlyphLegend === 'function') {
          window.viewer.hideGlyphLegend();
          console.log('[removeFilter] Hidden glyph legend');
        }
      } else if (id === 'surface-lic-filter' && filterEntry) {
        // Special handling for Surface LIC filter
        if (filterEntry.renderer && filterEntry.renderer.dispose) {
          filterEntry.renderer.dispose();
          console.log('[removeFilter] Disposed Surface LIC renderer');
        }
        // Clear the reference on filters class as well
        if (this.surfaceLICRenderer === filterEntry.renderer) {
          this.surfaceLICRenderer = null;
        }
      } else if (id === 'volume-lic-filter' && filterEntry) {
        // Special handling for Volume LIC filter
        if (filterEntry.renderer && filterEntry.renderer.dispose) {
          filterEntry.renderer.dispose();
          console.log('[removeFilter] Disposed Volume LIC renderer');
        }
        // Clear the reference on filters class as well
        if (this.volumeLICRenderer === filterEntry.renderer) {
          this.volumeLICRenderer = null;
        }
      } else if (id === 'slice-contours' && filterEntry) {
        // Special handling for slice contours - remove via renderer using the slice widget's object name
        const sliceWidget = this.sliceWidget;
        if (sliceWidget && sliceWidget.renderer) {
          const prefix = sliceWidget.config?.inputPrefix || 'slice';
          const slicePlaneObjectName = `${prefix}-plane`;
          sliceWidget.renderer.removeObject(slicePlaneObjectName);
          console.log('[removeFilter] Removed slice contours mesh:', slicePlaneObjectName);
        } else if (filterEntry.mesh && window.viewer?.engine?.scene) {
          window.viewer.engine.scene.remove(filterEntry.mesh);
          if (filterEntry.mesh.geometry) filterEntry.mesh.geometry.dispose();
          if (filterEntry.mesh.material) filterEntry.mesh.material.dispose();
          console.log('[removeFilter] Removed slice contours mesh from scene directly');
        }
      } else {
        // Standard removal via engine
        this.engine.removeObject(id);
      }

      if (window.viewer && typeof window.viewer.onStreamlineFilterRemoved === 'function') {
        window.viewer.onStreamlineFilterRemoved(id);
      }

      // Hide streamline legend if removing a streamline filter and it's showing this filter's legend
      if (filterEntry?.type === 'streamlines' || id === 'slice-streamlines' || id === 'streamline-filter') {
        const legend = document.getElementById('streamlineLegend');
        if (legend && (legend.dataset.filterId === id || !legend.dataset.filterId)) {
          if (window.viewer && typeof window.viewer.hideStreamlineLegend === 'function') {
            window.viewer.hideStreamlineLegend();
            console.log('[removeFilter] Hidden streamline legend for:', id);
          } else {
            legend.style.display = 'none';
          }
        }
      }

      // Hide isolines legend if removing slice-isolines filter
      if (id === 'slice-isolines') {
        if (window.viewer && typeof window.viewer.hideIsolinesLegend === 'function') {
          window.viewer.hideIsolinesLegend();
        }
      }

      this.activeFilters.delete(id);

      // Only sync controls if there are still other streamline filters remaining
      // Don't reset user selections when removing the last streamline before creating a new one
      const hasOtherStreamlines = Array.from(this.activeFilters.values()).some(entry => entry?.type === 'streamlines');
      if (hasOtherStreamlines) {
        this.syncStreamlineControls();
      }

      // Notify view lock manager of visualization change
      if (this.viewer?.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }

      return true;
    }
    return false;
  }

  // Clear all filters
  clearAll() {
    this.activeFilters.forEach((entry, id) => {
      this.engine.removeObject(id);
      if (window.viewer && typeof window.viewer.onStreamlineFilterRemoved === 'function' && entry?.type === 'streamlines') {
        window.viewer.onStreamlineFilterRemoved(id);
      }
    });
    this.activeFilters.clear();

    this.syncStreamlineControls();

    // Notify view lock manager of visualization change
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }
  }

  // Check if filter exists
  hasFilter(id) {
    return this.activeFilters.has(id);
  }

  // Get filter metadata
  getFilter(id) {
    return this.activeFilters.get(id) || null;
  }

  // List filters
  listFilters() {
    return Array.from(this.activeFilters.entries());
  }

  // Get active filters
  getActiveFilters() {
    return Array.from(this.activeFilters.entries());
  }

  /**
   * ====================================================================
   * XY PLOT CHARTS
   * ====================================================================
   */

  /**
   * Extract plot data along a line or at points
   * @param {Object} params - Plot parameters
   * @returns {Promise<Object>} Plot data result
   */
  async extractPlotData(params) {
    const {
      casePath,
      fieldName,
      probeType = 'line',
      startPoint = null,
      endPoint = null,
      numPoints = 100,
      points = null,
      origin = null,
      normal = null,
      resolution = 200,
      width = null,
      height = null,
      smooth = false,
      time = null,
      meshParts = null,
      useNearest = false
    } = params;

    if (!casePath || !fieldName) {
      throw new Error('Case path and field name are required');
    }

    if (probeType === 'line' && (!startPoint || !endPoint)) {
      throw new Error('Start and end points are required for line probe');
    }

    if (probeType === 'points' && !points) {
      throw new Error('Points array is required for point probe');
    }

    if (probeType === 'slice' && (!origin || !normal)) {
      throw new Error('Origin and normal are required for slice extraction');
    }

    try {
      const token = window.Auth?.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/plot/data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          case_path: casePath,
          field_name: fieldName,
          probe_type: probeType,
          start_point: startPoint,
          end_point: endPoint,
          num_points: numPoints,
          use_nearest: useNearest,
          points: points,
          origin: origin,
          normal: normal,
          resolution: resolution,
          width: width,
          height: height,
          smooth: smooth,
          time: time,
          mesh_parts: meshParts
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to extract plot data');
      }

      const result = await response.json();
      console.log('Plot data extracted:', result);
      return result;

    } catch (error) {
      console.error('Error extracting plot data:', error);
      throw error;
    }
  }

  /**
   * Get list of currently visible mesh parts from the viewer
   */
  getVisibleMeshParts() {
    if (!window.viewer || !window.viewer.meshParts || !window.viewer.meshParts.visibleParts) {
      console.log('[Filters] No mesh parts info available, using all parts');
      return null; // Return null to include all parts (backward compatibility)
    }
    const visibleParts = Array.from(window.viewer.meshParts.visibleParts);

    // If no parts are visible, use all parts instead of sending empty array
    if (visibleParts.length === 0) {
      console.log('[Filters] No mesh parts visible, using all parts for plot extraction');
      return null;
    }

    console.log('[Filters] Visible mesh parts for plot extraction:', visibleParts);
    return visibleParts;
  }

  /**
   * Apply XY plot from UI controls
   */
  async applyXYPlotFromUI() {
    const fieldName = document.getElementById('xyPlotField')?.value;
    const probeType = document.getElementById('xyPlotProbeType')?.value || 'line';
    const numPoints = parseInt(document.getElementById('xyPlotNumPoints')?.value) || 100;

    // Check if slice plane mode is selected
    if (probeType === 'slice') {
      // Use slice plane extraction instead
      return this.applySlicePlaneFromUI();
    }

    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    // Get visible mesh parts
    const visibleParts = this.getVisibleMeshParts();

    // Validate based on probe type
    let extractParams = {
      casePath: casePath,
      fieldName: fieldName,
      probeType: probeType,
      time: window.viewer.geometryParams?.time || window.viewer.currentData?.currentTimeValue,
      meshParts: visibleParts
    };

    if (probeType === 'line') {
      // Get line probe points from viewer state
      const startPoint = window.viewer.lineProbeStart;
      const endPoint = window.viewer.lineProbeEnd;

      if (!startPoint || !endPoint) {
        Utils.notify('Please pick start and end points for the line probe', 'error');
        return;
      }

      // Check if user wants nearest mesh points instead of interpolation
      const useNearest = document.getElementById('lineProbeUseNearest')?.checked || false;

      extractParams.startPoint = startPoint;
      extractParams.endPoint = endPoint;
      extractParams.numPoints = numPoints;
      extractParams.useNearest = useNearest;

    } else if (probeType === 'points') {
      // Get probe points from viewer state
      const probePoints = window.viewer.probePoints;

      if (!probePoints || probePoints.length === 0) {
        Utils.notify('Please add at least one probe point', 'error');
        return;
      }

      extractParams.points = probePoints;
    }

    try {
      // Show loading state
      const btnExtract = document.getElementById('btnExtractPlotData');
      const originalText = btnExtract.textContent;
      btnExtract.textContent = 'Extracting...';
      btnExtract.disabled = true;

      // Extract data
      const result = await this.extractPlotData(extractParams);

      if (result.status === 'success' && result.data) {
        // Store data for modal view and export
        window.currentPlotData = result.data;

        console.log('XY plot data extracted successfully');

        // Auto-open the modal to show the plot, then notify sync
        setTimeout(() => {
          openChartModal();
          // Notify view lock manager of visualization change AFTER modal opens
          if (window.viewer?.viewLockManager) {
            window.viewer.viewLockManager.onVisualizationChange();
          }
        }, 100);
      }

      // Restore button
      btnExtract.textContent = originalText;
      btnExtract.disabled = false;

    } catch (error) {
      console.error('Error applying XY plot:', error);
      Utils.notify(`Failed to create XY plot: ${error.message}`, 'error');

      // Restore button
      const btnExtract = document.getElementById('btnExtractPlotData');
      btnExtract.textContent = 'Extract Data';
      btnExtract.disabled = false;
    }
  }

  /**
   * Display XY plot chart using Chart.js
   * @param {Object} data - Plot data from backend
   * @param {string} fieldName - Field name for labels
   */
  displayXYPlotChart(data, fieldName) {
    const { distances, values, field_name, value_range } = data;

    // Show chart container
    const chartContainer = document.getElementById('chartContainer');
    chartContainer.style.display = 'block';

    // Create or update chart
    window.chartManager.createXYChart('xyPlotCanvas', distances, values, {
      xLabel: 'Distance along line (m)',
      yLabel: `${field_name || fieldName}`,
      title: `${field_name || fieldName} along probe line`,
      lineColor: 'rgb(75, 192, 192)',
      showPoints: distances.length <= 200  // Show points only if not too many
    });

    console.log(`Chart created with ${distances.length} points`);
    console.log(`Value range: [${value_range[0]}, ${value_range[1]}]`);
  }

  /**
   * Export plot data to CSV
   */
  exportPlotToCSV() {
    if (!window.currentPlotData) {
      Utils.notify('No plot data available', 'error');
      return;
    }

    const fieldName = window.currentPlotData.field_name || 'field';
    const probeType = window.currentPlotData.probe_type || document.getElementById('xyPlotProbeType')?.value || 'line';

    if (probeType === 'slice') {
      // Export slice data
      const { points_2d, points_3d, values } = window.currentPlotData;
      const filename = `${fieldName}_slice.csv`;

      let csv = 'Point,2D_U,2D_V,3D_X,3D_Y,3D_Z,Value\n';
      values.forEach((val, i) => {
        const p2d = points_2d[i];
        const p3d = points_3d[i];
        csv += `${i + 1},${p2d[0]},${p2d[1]},${p3d[0]},${p3d[1]},${p3d[2]},${val}\n`;
      });

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } else if (probeType === 'points') {
      // Export point cloud data
      const { points, values, vectors } = window.currentPlotData;
      const filename = `${fieldName}_pointcloud.csv`;

      let csv = '';
      if (vectors && vectors.length > 0) {
        // Vector field
        csv = 'Point,X,Y,Z,Vector_X,Vector_Y,Vector_Z,Magnitude\n';
        vectors.forEach((vec, i) => {
          const mag = Math.sqrt(vec[0] ** 2 + vec[1] ** 2 + vec[2] ** 2);
          csv += `${i + 1},${points[i][0]},${points[i][1]},${points[i][2]},${vec[0]},${vec[1]},${vec[2]},${mag}\n`;
        });
      } else {
        // Scalar field
        csv = 'Point,X,Y,Z,Value\n';
        values.forEach((val, i) => {
          csv += `${i + 1},${points[i][0]},${points[i][1]},${points[i][2]},${val}\n`;
        });
      }

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // Export line probe data
      const filename = `${fieldName}_lineprobe.csv`;
      window.chartManager.exportChartToCSV('xyPlotCanvasModal', filename);
    }
  }

  /**
   * Export plot chart to PNG
   */
  exportPlotToPNG() {
    if (!window.currentPlotData) {
      Utils.notify('No plot data available', 'error');
      return;
    }

    const fieldName = window.currentPlotData.field_name || 'field';
    const probeType = window.currentPlotData.probe_type || document.getElementById('xyPlotProbeType')?.value || 'line';

    if (probeType === 'slice' || probeType === 'points') {
      // Export Plotly plot (either 3D point cloud or 2D slice heatmap)
      const filename = probeType === 'slice' ? `${fieldName}_slice.png` : `${fieldName}_pointcloud.png`;
      const plotDiv = document.getElementById('xyPlotly3DModal');

      if (plotDiv && typeof Plotly !== 'undefined') {
        Plotly.downloadImage(plotDiv, {
          format: 'png',
          width: 1920,
          height: 1080,
          filename: filename.replace('.png', '')
        });
      } else {
        Utils.notify('Unable to export: plot not found', 'error');
      }
    } else {
      // Export 2D Chart.js plot
      const filename = `${fieldName}_plot.png`;
      window.chartManager.exportChartToPNG('xyPlotCanvasModal', filename);
    }
  }

  /**
   * Export plot chart to SVG
   */
  exportPlotToSVG() {
    if (!window.currentPlotData) {
      Utils.notify('No plot data available', 'error');
      return;
    }

    const fieldName = window.currentPlotData.field_name || 'field';
    const probeType = window.currentPlotData.probe_type || document.getElementById('xyPlotProbeType')?.value || 'line';

    if (probeType === 'slice' || probeType === 'points') {
      // Export Plotly plot to SVG
      const filename = probeType === 'slice' ? `${fieldName}_slice.svg` : `${fieldName}_pointcloud.svg`;
      const plotDiv = document.getElementById('xyPlotly3DModal');

      if (plotDiv && typeof Plotly !== 'undefined') {
        Plotly.downloadImage(plotDiv, {
          format: 'svg',
          width: 1920,
          height: 1080,
          filename: filename.replace('.svg', '')
        });
      } else {
        Utils.notify('Unable to export: plot not found', 'error');
      }
    } else {
      // Export 2D Chart.js plot to SVG
      const filename = `${fieldName}_plot.svg`;
      window.chartManager.exportChartToSVG('xyPlotCanvasModal', filename);
    }
  }

  /**
   * Export plot chart to PDF
   */
  exportPlotToPDF() {
    if (!window.currentPlotData) {
      Utils.notify('No plot data available', 'error');
      return;
    }

    const fieldName = window.currentPlotData.field_name || 'field';
    const probeType = window.currentPlotData.probe_type || document.getElementById('xyPlotProbeType')?.value || 'line';

    if (probeType === 'slice' || probeType === 'points') {
      // Export Plotly plot to PDF
      const filename = probeType === 'slice' ? `${fieldName}_slice.pdf` : `${fieldName}_pointcloud.pdf`;
      const plotDiv = document.getElementById('xyPlotly3DModal');

      if (plotDiv && typeof Plotly !== 'undefined') {
        // Plotly doesn't directly support PDF, so we'll convert to PNG and embed in PDF
        Plotly.toImage(plotDiv, {
          format: 'png',
          width: 1920,
          height: 1080
        }).then(dataUrl => {
          const { jsPDF } = window.jspdf;
          const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'px',
            format: [1920, 1080]
          });

          pdf.addImage(dataUrl, 'PNG', 0, 0, 1920, 1080);
          pdf.save(filename);
        }).catch(err => {
          console.error('Error exporting to PDF:', err);
          Utils.notify('Error exporting to PDF', 'error');
        });
      } else {
        Utils.notify('Unable to export: plot not found', 'error');
      }
    } else {
      // Export 2D Chart.js plot to PDF
      const filename = `${fieldName}_plot.pdf`;
      window.chartManager.exportChartToPDF('xyPlotCanvasModal', filename);
    }
  }

  /**
   * ====================================================================
   * SLICE PLANE HEATMAP
   * ====================================================================
   */

  /**
   * Apply slice plane extraction from UI
   */
  async applySlicePlaneFromUI() {
    const fieldSelect = document.getElementById('xyPlotField');
    const fieldName = fieldSelect?.value;

    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    // Get plane normal
    const nx = parseFloat(document.getElementById('slicePlaneNormalX')?.value || 0);
    const ny = parseFloat(document.getElementById('slicePlaneNormalY')?.value || 0);
    const nz = parseFloat(document.getElementById('slicePlaneNormalZ')?.value || 1);
    const normal = [nx, ny, nz];

    // Validate normal vector
    const mag = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (mag < 0.01) {
      Utils.notify('Normal vector is too small. Please provide a valid normal direction.', 'error');
      return;
    }

    // Get plane origin
    const ox = parseFloat(document.getElementById('slicePlaneOriginX')?.value || 0);
    const oy = parseFloat(document.getElementById('slicePlaneOriginY')?.value || 0);
    const oz = parseFloat(document.getElementById('slicePlaneOriginZ')?.value || 0);
    const origin = [ox, oy, oz];

    // Get resolution
    const resolution = parseInt(document.getElementById('slicePlaneResolution')?.value || 200);

    // Get plane dimensions (0 means auto)
    const width = parseFloat(document.getElementById('slicePlaneWidth')?.value || 0);
    const height = parseFloat(document.getElementById('slicePlaneHeight')?.value || 0);

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    // Get smooth interpolation setting from checkbox
    const smoothEnabled = document.getElementById('sliceHeatmapSmooth')?.checked || false;

    // Get visible mesh parts
    const visibleParts = this.getVisibleMeshParts();

    const extractParams = {
      casePath: casePath,
      fieldName: fieldName,
      probeType: 'slice',
      origin: origin,
      normal: normal,
      resolution: resolution,
      width: width > 0 ? width : null,
      height: height > 0 ? height : null,
      smooth: smoothEnabled,
      time: window.viewer?.geometryParams?.time || window.viewer?.currentData?.currentTimeValue,
      meshParts: visibleParts
    };

    // Show loading state
    const btnExtract = document.getElementById('btnExtractPlotData');
    if (!btnExtract) {
      console.error('Extract button not found');
      return;
    }

    const originalText = btnExtract.textContent;

    try {
      btnExtract.textContent = 'Extracting...';
      btnExtract.disabled = true;

      // Extract data
      const result = await this.extractPlotData(extractParams);

      if (result.status === 'success' && result.data) {
        // Store data for modal view
        window.currentPlotData = result.data;
        window.currentPlotData.probe_type = 'slice';

        console.log('Slice data extracted successfully');
        console.log('Points:', result.data.num_points);

        // The interactive slice widget is already showing the plane,
        // so we don't need to call visualizeSlicePlane() here

        // Auto-open the modal to show the heatmap, then notify sync
        setTimeout(() => {
          openChartModal();
          // Notify view lock manager of visualization change AFTER modal opens
          if (window.viewer?.viewLockManager) {
            window.viewer.viewLockManager.onVisualizationChange();
          }
        }, 100);
      }

      // Restore button
      btnExtract.textContent = originalText;
      btnExtract.disabled = false;

    } catch (error) {
      console.error('Error extracting slice:', error);
      Utils.notify(`Failed to extract slice: ${error.message}`, 'error');

      // Restore button
      btnExtract.textContent = originalText;
      btnExtract.disabled = false;
    }
  }

  /**
   * ====================================================================
   * SURFACE/VOLUME INTEGRATION
   * ====================================================================
   */

  /**
   * Compute surface or volume integral
   * @param {Object} params - Integration parameters
   * @returns {Promise<Object>} Integration result
   */
  async computeIntegration(params) {
    const {
      casePath,
      fieldName,
      integrationType = 'surface',
      surfaceIds = null,
      mesh_parts = null,
      time = null
    } = params;

    if (!casePath || !fieldName) {
      throw new Error('Case path and field name are required');
    }

    try {
      const token = window.Auth?.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/analysis/integrate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          case_path: casePath,
          field_name: fieldName,
          integration_type: integrationType,
          surface_ids: surfaceIds,
          mesh_parts: mesh_parts,
          time: time
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to compute integration');
      }

      const result = await response.json();
      console.log('Integration computed:', result);
      return result;

    } catch (error) {
      console.error('Error computing integration:', error);
      throw error;
    }
  }

  /**
   * Apply integration from UI controls
   */
  async applyIntegrationFromUI() {
    const fieldName = document.getElementById('integrationField')?.value;
    const integrationType = document.getElementById('integrationType')?.value || 'surface';
    const surfaceSelection = document.getElementById('integrationSurface')?.value || 'all';

    if (!fieldName) {
      Utils.notify('Please select a field', 'error');
      return;
    }

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    try {
      // Show loading state
      const btnCompute = document.getElementById('btnComputeIntegration');
      const originalText = btnCompute.textContent;
      btnCompute.textContent = 'Computing...';
      btnCompute.disabled = true;

      // Get visible mesh parts (same as plots)
      const viewer = window.viewer;
      const meshParts = this.getVisibleMeshParts();

      // Get current time
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;
      const currentTimestep = viewer.currentTimestep;
      console.log(`[Integration] Computing at timestep ${currentTimestep}, time=${currentTime}, mesh parts=${meshParts?.join(', ') || 'all'}`);

      // Compute integration on visible mesh parts only
      const result = await this.computeIntegration({
        casePath: casePath,
        fieldName: fieldName,
        integrationType: integrationType,
        surfaceIds: null,  // Deprecated, using mesh_parts instead
        mesh_parts: meshParts,
        time: currentTime
      });

      if (result.status === 'success' && result.data) {
        // Display results
        this.displayIntegrationResults(result.data);

        // Show export button
        document.getElementById('btnExportIntegrationCSV').style.display = 'inline-block';

        // Store data for export
        window.currentIntegrationData = result.data;

        console.log('Integration results displayed');

        // Notify sync for collaboration
        if (window.viewer?.viewLockManager) {
          window.viewer.viewLockManager.onVisualizationChange();
        }
      }

      // Restore button
      btnCompute.textContent = originalText;
      btnCompute.disabled = false;

    } catch (error) {
      console.error('Error computing integration:', error);
      Utils.notify(`Failed to compute integration: ${error.message}`, 'error');

      // Restore button
      const btnCompute = document.getElementById('btnComputeIntegration');
      btnCompute.textContent = 'Compute Integral';
      btnCompute.disabled = false;
    }
  }

  /**
   * Display integration results in UI
   * @param {Object} data - Integration result data
   */
  displayIntegrationResults(data) {
    const { field_name, integral, area, volume, average, num_cells, integration_type_actual, fallback_reason, time, mesh_parts } = data;

    // Show results container
    const resultsDiv = document.getElementById('integrationResults');
    resultsDiv.style.display = 'block';

    // Update result fields
    document.getElementById('resultFieldName').textContent = field_name || '-';
    document.getElementById('resultTime').textContent = time !== null && time !== undefined ? `${time.toFixed(3)} s` : '-';
    document.getElementById('resultIntegral').textContent = integral?.toExponential(6) || '-';
    document.getElementById('resultAreaVolume').textContent =
      (area?.toExponential(4) || volume?.toExponential(4) || '-') +
      (area ? ' m²' : volume ? ' m³' : '');
    document.getElementById('resultAverage').textContent = average?.toExponential(6) || '-';
    document.getElementById('resultNumCells').textContent = num_cells || '-';

    console.log('Integration results:', data);

    // Show info about mesh parts if filtered
    if (mesh_parts && mesh_parts.length > 0) {
      console.log(`[Integration] Computed on mesh parts: ${mesh_parts.join(', ')}`);
    }

    // Show warning if volume integral fell back to surface integral
    if (integration_type_actual === 'surface' && fallback_reason) {
      Utils.notify(`Note: ${fallback_reason}. Computed surface integral instead.`, 'warning');
    }
  }

  /**
   * Export integration results to CSV
   */
  exportIntegrationToCSV() {
    if (!window.currentIntegrationData) {
      Utils.notify('No integration data available', 'error');
      return;
    }

    const data = window.currentIntegrationData;
    const fieldName = data.field_name || 'field';

    // Create CSV content
    let csv = 'Property,Value\n';
    csv += `Field,${data.field_name}\n`;
    if (data.time !== null && data.time !== undefined) {
      csv += `Time (s),${data.time}\n`;
    }
    if (data.mesh_parts && data.mesh_parts.length > 0) {
      csv += `Mesh Parts,"${data.mesh_parts.join(', ')}"\n`;
    }
    csv += `Integral,${data.integral}\n`;
    if (data.area) {
      csv += `Area (m²),${data.area}\n`;
    }
    if (data.volume) {
      csv += `Volume (m³),${data.volume}\n`;
    }
    csv += `Average,${data.average}\n`;
    csv += `Number of Cells,${data.num_cells}\n`;
    if (data.integration_type_actual) {
      csv += `Integration Type,${data.integration_type_actual}\n`;
    }
    if (data.fallback_reason) {
      csv += `Note,"${data.fallback_reason}"\n`;
    }

    // Create Blob and download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fieldName}_integration.csv`;
    link.click();
    URL.revokeObjectURL(url);

    console.log('Integration results exported to CSV');
  }

  // ============================================================================
  // ENGINEERING CALCULATIONS - Mass Flow, Force Coefficients, Heat Transfer
  // ============================================================================

  /**
   * Compute mass flow rate through surfaces
   */
  async computeMassFlowRate(params) {
    const { casePath, velocityField, densityField, densityValue, meshParts, time, cacheKey } = params;

    if (!casePath || !velocityField) {
      throw new Error('Case path and velocity field are required');
    }

    const token = window.Auth?.getToken();
    if (!token) throw new Error('Not authenticated');

    const requestBody = {
      case_path: casePath,
      velocity_field: velocityField,
      density_field: densityField || null,
      density_value: densityValue || 1.0,
      mesh_parts: meshParts,
      time: time
    };

    // Include cache_key for collaborative session cache lookup
    if (cacheKey) {
      requestBody.cache_key = cacheKey;
      console.log('[MassFlowRate] Using cache_key for lookup:', cacheKey);
    }

    const response = await fetch('/api/analysis/mass-flow-rate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to compute mass flow rate');
    }

    return await response.json();
  }

  /**
   * Apply mass flow rate calculation from UI
   */
  async applyMassFlowFromUI() {
    const velocityField = document.getElementById('massFlowVelocityField')?.value;
    const densityMode = document.getElementById('massFlowDensityMode')?.value;
    const densityValue = parseFloat(document.getElementById('massFlowDensityValue')?.value) || 1.225;
    const densityField = densityMode === 'field' ? document.getElementById('massFlowDensityFieldSelect')?.value : null;
    const surfaceSelection = document.getElementById('massFlowSurface')?.value;

    if (!velocityField) {
      Utils.notify('Please select a velocity field', 'error');
      return;
    }

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    try {
      const btn = document.getElementById('btnComputeMassFlow');
      btn.textContent = 'Computing...';
      btn.disabled = true;

      const viewer = window.viewer;
      const meshParts = surfaceSelection === 'selected' && viewer.meshParts?.visibleParts
        ? Array.from(viewer.meshParts.visibleParts) : null;
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;

      const result = await this.computeMassFlowRate({
        casePath,
        velocityField,
        densityField,
        densityValue,
        meshParts,
        time: currentTime
      });

      if (result.status === 'success' && result.data) {
        // Store cache_key from response for collaborative session optimization
        if (result.cache_key) {
          result.data.cache_key = result.cache_key;
          console.log('[MassFlowRate] Stored cache_key:', result.cache_key);
        }
        this.displayMassFlowResults(result.data);
        window.currentMassFlowData = result.data;

        // Notify if this was a cache hit
        if (result.from_cache) {
          Utils.notify('Mass flow rate computed (cached)', 'success');
        } else {
          Utils.notify('Mass flow rate computed', 'success');
        }
      }

      btn.textContent = 'Compute Mass Flow';
      btn.disabled = false;
    } catch (error) {
      console.error('Error computing mass flow rate:', error);
      Utils.notify(`Failed to compute mass flow rate: ${error.message}`, 'error');
      document.getElementById('btnComputeMassFlow').textContent = 'Compute Mass Flow';
      document.getElementById('btnComputeMassFlow').disabled = false;
    }
  }

  /**
   * Display mass flow results
   */
  displayMassFlowResults(data) {
    const resultsDiv = document.getElementById('massFlowResults');
    resultsDiv.style.display = 'block';

    document.getElementById('resultMassFlow').textContent =
      `${data.mass_flow_rate?.toExponential(4)} kg/s`;
    document.getElementById('resultVolumetricFlow').textContent =
      `${data.volumetric_flow_rate?.toExponential(4)} m³/s`;
    document.getElementById('resultAvgVelocity').textContent =
      `${data.average_velocity?.toFixed(4)} m/s`;
    document.getElementById('resultMassFlowArea').textContent =
      `${data.area?.toExponential(4)} m²`;
  }

  /**
   * Compute force coefficients from pressure integration
   */
  async computeForceCoefficients(params) {
    const {
      casePath, pressureField, velocityField, referenceVelocity, referenceArea,
      referenceDensity, referencePressure, flowDirection, liftDirection, flipNormals,
      meshParts, time, cacheKey
    } = params;

    if (!casePath || !pressureField) {
      throw new Error('Case path and pressure field are required');
    }

    const token = window.Auth?.getToken();
    if (!token) throw new Error('Not authenticated');

    const requestBody = {
      case_path: casePath,
      pressure_field: pressureField,
      velocity_field: velocityField || 'velocity',
      reference_velocity: referenceVelocity || null,
      reference_area: referenceArea || null,
      reference_density: referenceDensity || 1.225,
      reference_pressure: referencePressure || 0,
      flow_direction: flowDirection || [1, 0, 0],
      lift_direction: liftDirection || [0, 1, 0],
      flip_normals: flipNormals || false,
      mesh_parts: meshParts,
      time: time
    };

    // Include cache_key for collaborative session cache lookup
    if (cacheKey) {
      requestBody.cache_key = cacheKey;
      console.log('[ForceCoefficients] Using cache_key for lookup:', cacheKey);
    }

    const response = await fetch('/api/analysis/force-coefficients', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to compute force coefficients');
    }

    return await response.json();
  }

  /**
   * Apply force coefficients calculation from UI
   */
  async applyForceCoeffFromUI() {
    const pressureField = document.getElementById('forceCoeffPressureField')?.value;
    const velocityField = document.getElementById('forceCoeffVelocityField')?.value;
    const refVelocity = parseFloat(document.getElementById('forceCoeffRefVelocity')?.value) || null;
    const refArea = parseFloat(document.getElementById('forceCoeffRefArea')?.value) || null;
    const refDensity = parseFloat(document.getElementById('forceCoeffRefDensity')?.value) || 1.225;
    const refPressure = parseFloat(document.getElementById('forceCoeffRefPressure')?.value) || 0;
    const flowDirection = [
      parseFloat(document.getElementById('forceCoeffFlowX')?.value) || 1,
      parseFloat(document.getElementById('forceCoeffFlowY')?.value) || 0,
      parseFloat(document.getElementById('forceCoeffFlowZ')?.value) || 0
    ];
    const liftDirection = [
      parseFloat(document.getElementById('forceCoeffLiftX')?.value) || 0,
      parseFloat(document.getElementById('forceCoeffLiftY')?.value) || 1,
      parseFloat(document.getElementById('forceCoeffLiftZ')?.value) || 0
    ];
    const surfaceSelection = document.getElementById('forceCoeffSurface')?.value;
    const flipNormals = document.getElementById('forceCoeffFlipNormals')?.checked || false;

    if (!pressureField) {
      Utils.notify('Please select a pressure field', 'error');
      return;
    }

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    try {
      const btn = document.getElementById('btnComputeForceCoeff');
      btn.textContent = 'Computing...';
      btn.disabled = true;

      const viewer = window.viewer;
      const meshParts = surfaceSelection === 'selected' && viewer.meshParts?.visibleParts
        ? Array.from(viewer.meshParts.visibleParts) : null;
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;

      const result = await this.computeForceCoefficients({
        casePath,
        pressureField,
        velocityField,
        referenceVelocity: refVelocity,
        referenceArea: refArea,
        referenceDensity: refDensity,
        referencePressure: refPressure,
        flowDirection,
        liftDirection,
        flipNormals,
        meshParts,
        time: currentTime
      });

      if (result.status === 'success' && result.data) {
        // Store cache_key from response for collaborative session optimization
        if (result.cache_key) {
          result.data.cache_key = result.cache_key;
          console.log('[ForceCoefficients] Stored cache_key:', result.cache_key);
        }
        this.displayForceCoeffResults(result.data);
        window.currentForceCoeffData = result.data;

        // Notify if this was a cache hit
        if (result.from_cache) {
          Utils.notify('Force coefficients computed (cached)', 'success');
        } else {
          Utils.notify('Force coefficients computed', 'success');
        }
      }

      btn.textContent = 'Compute Coefficients';
      btn.disabled = false;
    } catch (error) {
      console.error('Error computing force coefficients:', error);
      Utils.notify(`Failed to compute force coefficients: ${error.message}`, 'error');
      document.getElementById('btnComputeForceCoeff').textContent = 'Compute Coefficients';
      document.getElementById('btnComputeForceCoeff').disabled = false;
    }
  }

  /**
   * Display force coefficient results
   */
  displayForceCoeffResults(data) {
    const resultsDiv = document.getElementById('forceCoeffResults');
    resultsDiv.style.display = 'block';

    // Highlight negative Cd in red as a warning
    const cdElement = document.getElementById('resultCd');
    cdElement.textContent = data.drag_coefficient?.toFixed(6);
    if (data.drag_coefficient < 0) {
      cdElement.style.color = '#ff6b6b';
      cdElement.title = 'Negative Cd - check flow direction or surface normals';
    } else {
      cdElement.style.color = '';
      cdElement.title = '';
    }

    document.getElementById('resultCl').textContent = data.lift_coefficient?.toFixed(6);
    document.getElementById('resultCs').textContent = data.side_coefficient?.toFixed(6);
    document.getElementById('resultCm').textContent = data.moment_coefficient?.toFixed(6);
    document.getElementById('resultDragForce').textContent = `${data.drag_force?.toFixed(4)} N`;
    document.getElementById('resultLiftForce').textContent = `${data.lift_force?.toFixed(4)} N`;
    document.getElementById('resultDynPressure').textContent = `${data.dynamic_pressure?.toFixed(2)} Pa`;

    // Show warnings if any
    if (data.warnings && data.warnings.length > 0) {
      data.warnings.forEach(warning => {
        Utils.notify(warning, 'warning', 8000);  // Show for 8 seconds
      });
    }
  }

  /**
   * Compute heat transfer coefficient
   */
  async computeHeatTransfer(params) {
    const { casePath, heatFluxField, temperatureField, referenceTemp, wallTemp, meshParts, time, cacheKey } = params;

    if (!casePath) {
      throw new Error('Case path is required');
    }

    const token = window.Auth?.getToken();
    if (!token) throw new Error('Not authenticated');

    const requestBody = {
      case_path: casePath,
      wall_heat_flux_field: heatFluxField || null,
      temperature_field: temperatureField || 'T',
      reference_temperature: referenceTemp || 300,
      wall_temperature: wallTemp || null,
      mesh_parts: meshParts,
      time: time
    };

    // Include cache_key for collaborative session cache lookup
    if (cacheKey) {
      requestBody.cache_key = cacheKey;
      console.log('[HeatTransfer] Using cache_key for lookup:', cacheKey);
    }

    const response = await fetch('/api/analysis/heat-transfer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to compute heat transfer');
    }

    return await response.json();
  }

  /**
   * Apply heat transfer calculation from UI
   */
  async applyHeatTransferFromUI() {
    const heatFluxField = document.getElementById('heatTransferFluxField')?.value || null;
    const temperatureField = document.getElementById('heatTransferTempField')?.value;
    const refTemp = parseFloat(document.getElementById('heatTransferRefTemp')?.value) || 300;
    const wallTemp = parseFloat(document.getElementById('heatTransferWallTemp')?.value) || null;
    const surfaceSelection = document.getElementById('heatTransferSurface')?.value;

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    try {
      const btn = document.getElementById('btnComputeHeatTransfer');
      btn.textContent = 'Computing...';
      btn.disabled = true;

      const viewer = window.viewer;
      const meshParts = surfaceSelection === 'selected' && viewer.meshParts?.visibleParts
        ? Array.from(viewer.meshParts.visibleParts) : null;
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;

      const result = await this.computeHeatTransfer({
        casePath,
        heatFluxField,
        temperatureField,
        referenceTemp: refTemp,
        wallTemp,
        meshParts,
        time: currentTime
      });

      if (result.status === 'success' && result.data) {
        // Store cache_key from response for collaborative session optimization
        if (result.cache_key) {
          result.data.cache_key = result.cache_key;
          console.log('[HeatTransfer] Stored cache_key:', result.cache_key);
        }
        this.displayHeatTransferResults(result.data);
        window.currentHeatTransferData = result.data;

        // Notify if this was a cache hit
        if (result.from_cache) {
          Utils.notify('Heat transfer computed (cached)', 'success');
        } else {
          Utils.notify('Heat transfer computed', 'success');
        }
      }

      btn.textContent = 'Compute Heat Transfer';
      btn.disabled = false;
    } catch (error) {
      console.error('Error computing heat transfer:', error);
      Utils.notify(`Failed to compute heat transfer: ${error.message}`, 'error');
      document.getElementById('btnComputeHeatTransfer').textContent = 'Compute Heat Transfer';
      document.getElementById('btnComputeHeatTransfer').disabled = false;
    }
  }

  /**
   * Display heat transfer results
   */
  displayHeatTransferResults(data) {
    const resultsDiv = document.getElementById('heatTransferResults');
    resultsDiv.style.display = 'block';

    document.getElementById('resultHAvg').textContent =
      `${data.heat_transfer_coefficient?.toFixed(2)} W/(m²·K)`;
    document.getElementById('resultHMin').textContent =
      `${data.heat_transfer_coefficient_min?.toFixed(2)} W/(m²·K)`;
    document.getElementById('resultHMax').textContent =
      `${data.heat_transfer_coefficient_max?.toFixed(2)} W/(m²·K)`;
    document.getElementById('resultHeatRate').textContent =
      `${data.total_heat_rate?.toExponential(4)} W`;
    document.getElementById('resultAvgHeatFlux').textContent =
      `${data.average_heat_flux?.toExponential(4)} W/m²`;
    document.getElementById('resultHeatArea').textContent =
      `${data.area?.toExponential(4)} m²`;
    document.getElementById('resultDeltaT').textContent =
      `${data.temperature_difference?.toFixed(2)} K`;

    // Show warning if no heat flux field found
    if (!data.heat_flux_field_found) {
      Utils.notify('No heat flux field found - results may be limited', 'warning');
    }
  }

  /**
   * Refresh engineering calculation dropdowns with available fields
   */
  refreshEngineeringDropdowns(fieldNames, vectorFields) {
    // Mass flow - velocity fields (vectors only)
    const massFlowVelocity = document.getElementById('massFlowVelocityField');
    if (massFlowVelocity) {
      const prevVal = massFlowVelocity.value;
      massFlowVelocity.innerHTML = '<option value="">Select velocity field...</option>';
      vectorFields.forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        massFlowVelocity.appendChild(opt);
      });
      if (prevVal && vectorFields.includes(prevVal)) massFlowVelocity.value = prevVal;
    }

    // Mass flow - density fields (scalars)
    const massFlowDensity = document.getElementById('massFlowDensityFieldSelect');
    if (massFlowDensity) {
      massFlowDensity.innerHTML = '<option value="">Select density field...</option>';
      fieldNames.filter(f => !vectorFields.includes(f)).forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        massFlowDensity.appendChild(opt);
      });
    }

    // Force coefficients - pressure fields (scalars)
    const forceCoeffPressure = document.getElementById('forceCoeffPressureField');
    if (forceCoeffPressure) {
      const prevVal = forceCoeffPressure.value;
      forceCoeffPressure.innerHTML = '<option value="">Select pressure field...</option>';
      fieldNames.filter(f => !vectorFields.includes(f)).forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        forceCoeffPressure.appendChild(opt);
      });
      if (prevVal) forceCoeffPressure.value = prevVal;
    }

    // Force coefficients - velocity fields (vectors)
    const forceCoeffVelocity = document.getElementById('forceCoeffVelocityField');
    if (forceCoeffVelocity) {
      const prevVal = forceCoeffVelocity.value;
      forceCoeffVelocity.innerHTML = '<option value="">Select velocity field...</option>';
      vectorFields.forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        forceCoeffVelocity.appendChild(opt);
      });
      if (prevVal && vectorFields.includes(prevVal)) forceCoeffVelocity.value = prevVal;
    }

    // Heat transfer - heat flux fields (scalars)
    const heatFluxField = document.getElementById('heatTransferFluxField');
    if (heatFluxField) {
      heatFluxField.innerHTML = '<option value="">Auto-detect or none</option>';
      fieldNames.filter(f => !vectorFields.includes(f)).forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        heatFluxField.appendChild(opt);
      });
    }

    // Heat transfer - temperature fields (scalars)
    const tempField = document.getElementById('heatTransferTempField');
    if (tempField) {
      const prevVal = tempField.value;
      tempField.innerHTML = '<option value="">Select temperature field...</option>';
      fieldNames.filter(f => !vectorFields.includes(f)).forEach(f => {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        tempField.appendChild(opt);
      });
      if (prevVal) tempField.value = prevVal;
    }
  }

  // ============================================================================
  // MESH CUTTER (Multi-Plane Cutting)
  // ============================================================================

  // Predefined colors for cutting planes (distinct, visible colors)
  static CUTTING_PLANE_COLORS = [
    '#E24A4A', // Red
    '#4A90E2', // Blue
    '#4AE24A', // Green
    '#E2E24A', // Yellow
    '#E24AE2', // Magenta
    '#4AE2E2', // Cyan
    '#E2904A', // Orange
    '#904AE2', // Purple
  ];

  /**
   * Initialize mesh cutter state
   */
  initMeshCutter() {
    if (!this.meshCutterPlanes) {
      this.meshCutterPlanes = [];
    }
    this.renderCuttingPlanesList();
  }

  /**
   * Get mesh center and size from scene bounds
   */
  getMeshCenterAndSize() {
    const viewer = window.viewer || this.viewer;
    let center = [0, 0, 0];
    let size = 1;

    // Try to get bounds from rendering engine
    const sceneBounds = this.engine?.getSceneBounds?.();
    if (sceneBounds) {
      center = [
        (sceneBounds.min.x + sceneBounds.max.x) / 2,
        (sceneBounds.min.y + sceneBounds.max.y) / 2,
        (sceneBounds.min.z + sceneBounds.max.z) / 2
      ];
      size = Math.max(
        sceneBounds.max.x - sceneBounds.min.x,
        sceneBounds.max.y - sceneBounds.min.y,
        sceneBounds.max.z - sceneBounds.min.z
      );
    }

    return { center, size };
  }

  /**
   * Get color for a plane based on its index
   */
  getPlaneColor(index) {
    const colors = Filters.CUTTING_PLANE_COLORS;
    return colors[index % colors.length];
  }

  /**
   * Add a cutting plane (auto-centered on mesh)
   */
  addCuttingPlane(config = {}) {
    // Get mesh center and size for auto-positioning
    const { center, size } = this.getMeshCenterAndSize();

    // Default normal cycles through X, Y, Z based on plane count
    const planeCount = this.meshCutterPlanes?.length || 0;
    const defaultNormals = [
      [1, 0, 0], // X
      [0, 1, 0], // Y
      [0, 0, 1], // Z
    ];
    const defaultNormal = defaultNormals[planeCount % 3];

    const origin = config.origin || center;
    const normal = config.normal || defaultNormal;
    const width = config.width !== undefined ? config.width : null;
    const height = config.height !== undefined ? config.height : null;
    const invert = config.invert !== undefined ? config.invert : false;

    // Validate normal is not zero
    const normalMag = Math.sqrt(normal[0]**2 + normal[1]**2 + normal[2]**2);
    if (normalMag < 0.001) {
      Utils.notify('Normal vector cannot be zero', 'error');
      return null;
    }

    const planeId = config.id || `plane_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    // Assign color based on plane index
    const colorIndex = planeCount;
    const color = config.color || this.getPlaneColor(colorIndex);

    const plane = {
      id: planeId,
      origin: origin,
      normal: normal,
      width: width,
      height: height,
      scaleU: config.scaleU !== undefined ? config.scaleU : 1.0,  // Per-plane U scale
      scaleV: config.scaleV !== undefined ? config.scaleV : 1.0,  // Per-plane V scale
      depth: config.depth !== undefined ? config.depth : 0,       // Depth for volume/slab cut (0 = plane, >0 = volume)
      invert: invert,
      enabled: config.enabled !== undefined ? config.enabled : true,
      color: color
    };

    if (!this.meshCutterPlanes) {
      this.meshCutterPlanes = [];
    }
    this.meshCutterPlanes.push(plane);
    this.renderCuttingPlanesList();

    console.log('[MeshCutter] Added plane:', plane);

    // Notify widget
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.onPlaneAdded(planeId);
    }

    return planeId;
  }

  /**
   * Remove a cutting plane
   */
  removeCuttingPlane(planeId) {
    if (!this.meshCutterPlanes) return;
    const idx = this.meshCutterPlanes.findIndex(p => p.id === planeId);
    if (idx >= 0) {
      this.meshCutterPlanes.splice(idx, 1);
      this.renderCuttingPlanesList();
      console.log('[MeshCutter] Removed plane:', planeId);

      // Notify widget
      if (this.cuttingPlaneWidget) {
        this.cuttingPlaneWidget.onPlaneRemoved(planeId);
      }
    }
  }

  /**
   * Toggle cutting plane enabled state
   */
  toggleCuttingPlane(planeId) {
    if (!this.meshCutterPlanes) return;
    const plane = this.meshCutterPlanes.find(p => p.id === planeId);
    if (plane) {
      plane.enabled = !plane.enabled;
      this.renderCuttingPlanesList();

      // Notify widget
      if (this.cuttingPlaneWidget) {
        this.cuttingPlaneWidget.onPlaneToggled(planeId);
      }
    }
  }

  /**
   * Clear all cutting planes
   */
  clearCuttingPlanes() {
    this.meshCutterPlanes = [];
    this.renderCuttingPlanesList();

    // Notify widget - clear planes and restore original mesh
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.onPlanesCleared();
      this.cuttingPlaneWidget.onCutCleared();
    }

    // Hide results
    const resultsDiv = document.getElementById('meshCutterResults');
    if (resultsDiv) resultsDiv.style.display = 'none';

    // Clear the cut mesh from scene
    window.currentMeshCutterData = null;
    this.removeFilter('mesh-cutter');
  }

  /**
   * Render the list of cutting planes in the UI as cards
   */
  renderCuttingPlanesList() {
    const listDiv = document.getElementById('cuttingPlanesList');
    if (!listDiv) return;

    if (!this.meshCutterPlanes || this.meshCutterPlanes.length === 0) {
      listDiv.innerHTML = '';
      return;
    }

    const activePlaneId = this.cuttingPlaneWidget?.activePlaneId;

    let html = '';
    this.meshCutterPlanes.forEach((plane, idx) => {
      const isSelected = plane.id === activePlaneId;
      const isWidgetVisible = this.cuttingPlaneWidget?.isPlaneWidgetVisible(plane.id) ?? true;
      const isClippingActive = this.cuttingPlaneWidget?.isPlaneClippingActive(plane.id) ?? true;
      const visibilityTitle = isWidgetVisible ? 'Hide plane preview' : 'Show plane preview';
      const visibilityIconSrc = isWidgetVisible ? 'static/assets/icons/eye.svg' : 'static/assets/icons/eye-off.svg';
      const clippingTitle = isClippingActive ? 'Disable clipping' : 'Enable clipping';
      const clippingIconSrc = isClippingActive ? 'static/assets/icons/scissors.svg' : 'static/assets/icons/scissors-off.svg';
      const planeColor = plane.color || this.getPlaneColor(idx);

      html += `
        <div class="cutting-plane-card" data-plane-id="${plane.id}"
             style="display: flex; align-items: center; gap: 8px; padding: 6px 10px; margin-bottom: 6px;
                    background: ${isSelected ? '#2a2a35' : '#1e1e1e'};
                    border: 1px solid ${isSelected ? planeColor : '#333'};
                    border-radius: 6px; cursor: pointer; transition: all 0.15s ease;"
             onclick="window.viewer.filters.selectCuttingPlane('${plane.id}')"
             onmouseenter="this.style.background='#2a2a2a'"
             onmouseleave="this.style.background='${isSelected ? '#2a2a35' : '#1e1e1e'}'">

          <!-- Color indicator circle -->
          <div style="width: 12px; height: 12px; border-radius: 50%; background: ${planeColor};
                      flex-shrink: 0; box-shadow: 0 0 4px ${planeColor}80;"></div>

          <!-- Plane info -->
          <div style="flex: 1; min-width: 0;">
            <div style="font-size: 12px; font-weight: 500; color: #e0e0e0;">Plane ${idx + 1}</div>
          </div>

          <!-- Action buttons -->
          <div style="display: flex; gap: 4px; align-items: center;">
            <button class="btn-icon" onclick="event.stopPropagation(); window.viewer.filters.togglePlaneClipping('${plane.id}')"
                    title="${clippingTitle}"
                    style="padding: 4px; opacity: ${isClippingActive ? 1 : 0.5}; border-radius: 4px; background: transparent; border: none; cursor: pointer;">
              <img src="${clippingIconSrc}" alt="" width="16" height="16" style="display: block; filter: invert(1);">
            </button>
            <button class="btn-icon" onclick="event.stopPropagation(); window.viewer.filters.togglePlaneVisibility('${plane.id}')"
                    title="${visibilityTitle}"
                    style="padding: 4px; opacity: ${isWidgetVisible ? 1 : 0.5}; border-radius: 4px; background: transparent; border: none; cursor: pointer;">
              <img src="${visibilityIconSrc}" alt="" width="16" height="16" style="display: block; filter: invert(1);">
            </button>
            <button class="btn-icon" onclick="event.stopPropagation(); window.viewer.filters.removeCuttingPlane('${plane.id}')"
                    title="Remove plane"
                    style="padding: 4px; opacity: 0.9; border-radius: 4px; background: transparent; border: none; cursor: pointer;">
              <img src="static/assets/icons/trash.svg" alt="" width="16" height="16" style="display: block; filter: invert(27%) sepia(94%) saturate(2255%) hue-rotate(343deg) brightness(88%) contrast(97%);">
            </button>
          </div>
        </div>
      `;
    });

    listDiv.innerHTML = html;
  }

  /**
   * Render the list of eraser sequences in the UI as cards
   */
  renderEraserSequencesList() {
    const listDiv = document.getElementById('eraserSequencesList');
    if (!listDiv) return;

    const eraser = this.meshEraser;
    if (!eraser || eraser.sequences.length === 0) {
      listDiv.innerHTML = '<span style="font-size: 11px; color: #666;">No sequences yet</span>';
      return;
    }

    let html = '';
    eraser.sequences.forEach((seq) => {
      const isActive = seq.id === eraser.activeSequenceId;
      const isVisible = seq.visible;
      const visibilityIconSrc = isVisible ? 'static/assets/icons/scissors.svg' : 'static/assets/icons/scissors-off.svg';
      const visibilityTitle = isVisible ? 'Restore erased triangles' : 'Erase triangles again';
      const triCount = eraser._getErasedTriCount(seq);

      html += `
        <div class="eraser-sequence-card" data-seq-id="${seq.id}"
             style="display: flex; align-items: center; gap: 8px; padding: 6px 10px; margin-bottom: 6px;
                    background: ${isActive ? '#2a2a35' : '#1e1e1e'};
                    border: 1px solid ${isActive ? seq.color : '#333'};
                    border-radius: 6px; transition: all 0.15s ease;"
             onmouseenter="this.style.background='#2a2a2a'"
             onmouseleave="this.style.background='${isActive ? '#2a2a35' : '#1e1e1e'}'">

          <!-- Color indicator circle -->
          <div style="width: 12px; height: 12px; border-radius: 50%; background: ${seq.color};
                      flex-shrink: 0; box-shadow: 0 0 4px ${seq.color}80;"></div>

          <!-- Sequence info -->
          <div style="flex: 1; min-width: 0;">
            <span class="eraser-seq-name" data-seq-id="${seq.id}"
                  style="font-size: 12px; font-weight: 500; color: #e0e0e0; cursor: text; display: block;"
                  ondblclick="this.contentEditable=true; this.focus();"
                  onblur="window.viewer.filters.meshEraser.renameSequence('${seq.id}', this.textContent); this.contentEditable=false;"
                  onkeydown="if(event.key==='Enter'){this.blur(); event.preventDefault();}">${seq.name}</span>
            <div style="font-size: 10px; color: #666;">${triCount.toLocaleString()} triangle${triCount !== 1 ? 's' : ''}</div>
          </div>

          <!-- Action buttons -->
          <div style="display: flex; gap: 4px; align-items: center;">
            <button class="btn-icon" onclick="event.stopPropagation(); window.viewer.filters.meshEraser.toggleSequence('${seq.id}')"
                    title="${visibilityTitle}"
                    style="padding: 4px; opacity: ${isVisible ? 1 : 0.5}; border-radius: 4px; background: transparent; border: none; cursor: pointer;">
              <img src="${visibilityIconSrc}" alt="" width="16" height="16" style="display: block; filter: invert(1);">
            </button>
            <button class="btn-icon" onclick="event.stopPropagation(); window.viewer.filters.meshEraser.deleteSequence('${seq.id}')"
                    title="Delete sequence"
                    style="padding: 4px; opacity: 0.9; border-radius: 4px; background: transparent; border: none; cursor: pointer;">
              <img src="static/assets/icons/trash.svg" alt="" width="16" height="16"
                   style="display: block; filter: invert(27%) sepia(94%) saturate(2255%) hue-rotate(343deg) brightness(88%) contrast(97%);">
            </button>
          </div>
        </div>
      `;
    });

    listDiv.innerHTML = html;
  }

  /**
   * Toggle plane preview visibility (the visual plane widget)
   */
  togglePlaneVisibility(planeId) {
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.togglePlaneWidgetVisibility(planeId);
    }
  }

  /**
   * Toggle plane clipping effect (whether the mesh is clipped by this plane)
   */
  togglePlaneClipping(planeId) {
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.togglePlaneClipping(planeId);
    }
  }

  /**
   * Select a cutting plane for editing
   */
  selectCuttingPlane(planeId) {
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.selectPlane(planeId);
      this.renderCuttingPlanesList(); // Re-render to update selection highlight
    }
  }

  /**
   * Apply mesh cutter (call API)
   */
  async applyMeshCutter(options = {}) {
    if (!this.meshCutterPlanes || this.meshCutterPlanes.length === 0) {
      Utils.notify('Add at least one cutting plane', 'error');
      return;
    }

    const casePath = this.getCurrentCasePath();
    if (!casePath) {
      Utils.notify('No case loaded', 'error');
      return;
    }

    const token = window.Auth?.getToken();
    if (!token) {
      Utils.notify('Not authenticated', 'error');
      return;
    }

    try {
      const btn = document.getElementById('btnApplyMeshCutter');
      if (btn) {
        btn.textContent = 'Cutting...';
        btn.disabled = true;
      }

      const viewer = window.viewer;
      const currentTime = viewer.geometryParams?.time || viewer.currentData?.currentTimeValue;
      const meshParts = viewer.meshParts?.visibleParts ?
        Array.from(viewer.meshParts.visibleParts) : null;

      // Get actual scaled dimensions from the widget
      const widget = this.cuttingPlaneWidget;

      const requestBody = {
        case_path: casePath,
        planes: this.meshCutterPlanes.map(p => {
          // Get the widget's actual dimensions (with U/V scaling applied)
          const widgetData = widget?.planeWidgets?.get(p.id);
          return {
            id: p.id,
            origin: p.origin,
            normal: p.normal,
            width: widgetData?.width || p.width,
            height: widgetData?.height || p.height,
            invert: p.invert,
            enabled: p.enabled,
            bounded: true  // Always use bounded cutting
          };
        }),
        time: currentTime,
        mesh_parts: meshParts
      };

      // Include cache_key if provided (for collaborative sync)
      if (options.cache_key) {
        requestBody.cache_key = options.cache_key;
      }

      const response = await fetch('/api/filters/mesh-cutter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to apply mesh cutter');
      }

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        // Store cache_key and data
        if (result.cache_key) {
          result.data.cache_key = result.cache_key;
        }
        window.currentMeshCutterData = result.data;

        // Render the cut mesh
        this.renderCutMesh(result.data);

        // Hide original mesh, show only cut result
        if (this.cuttingPlaneWidget) {
          this.cuttingPlaneWidget.onCutApplied();
        }

        // Show results
        this.displayMeshCutterResults(result.data);

        const enabledCount = this.meshCutterPlanes.filter(p => p.enabled).length;
        Utils.notify(`Mesh cut with ${enabledCount} plane(s): ${result.data.num_points} points`, 'success');
      }

      if (btn) {
        btn.textContent = 'Apply Cut';
        btn.disabled = false;
      }

    } catch (error) {
      console.error('Mesh cutter error:', error);
      Utils.notify(`Mesh cutter failed: ${error.message}`, 'error');
      const btn = document.getElementById('btnApplyMeshCutter');
      if (btn) {
        btn.textContent = 'Apply Cut';
        btn.disabled = false;
      }
    }
  }

  /**
   * Display mesh cutter results
   */
  displayMeshCutterResults(data) {
    const resultsDiv = document.getElementById('meshCutterResults');
    if (!resultsDiv) return;

    resultsDiv.style.display = 'block';
    document.getElementById('cutResultPoints').textContent = data.num_points?.toLocaleString() || '-';
    document.getElementById('cutResultCells').textContent = data.num_cells?.toLocaleString() || '-';
  }

  /**
   * Render the cut mesh in the 3D scene
   */
  renderCutMesh(data) {
    const viewer = window.viewer;
    if (!viewer || !viewer.engine) {
      console.error('[MeshCutter] No viewer or engine');
      return;
    }

    // Remove existing cut mesh
    this.removeFilter('mesh-cutter');

    if (!data.vertices || data.vertices.length === 0) {
      console.warn('[MeshCutter] No vertices in cut result');
      return;
    }

    // Create Three.js geometry
    const geometry = new THREE.BufferGeometry();
    const vertices = new Float32Array(data.vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    // Add normals if available
    if (data.normals && data.normals.length > 0) {
      const normals = new Float32Array(data.normals.flat());
      geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    }

    // Create faces from cells
    const indices = [];
    for (const cell of data.cells) {
      if (cell.length === 3) {
        // Triangle
        indices.push(cell[0], cell[1], cell[2]);
      } else if (cell.length === 4) {
        // Quad - split into two triangles
        indices.push(cell[0], cell[1], cell[2]);
        indices.push(cell[0], cell[2], cell[3]);
      } else if (cell.length > 4) {
        // Polygon - fan triangulation
        for (let i = 1; i < cell.length - 1; i++) {
          indices.push(cell[0], cell[i], cell[i + 1]);
        }
      }
    }
    geometry.setIndex(indices);

    if (!data.normals || data.normals.length === 0) {
      geometry.computeVertexNormals();
    }

    // Create material - use semi-transparent to show cutting
    const material = new THREE.MeshPhongMaterial({
      color: 0x66aaff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.85,
      flatShading: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'mesh-cutter-result';

    viewer.engine.scene.add(mesh);

    // Store in active filters
    this.activeFilters.set('mesh-cutter', {
      mesh: mesh,
      type: 'mesh-cutter',
      data: data
    });

    viewer.engine.renderer.render(viewer.engine.scene, viewer.engine.camera);
    console.log('[MeshCutter] Rendered cut mesh:', data.num_points, 'points');
  }

  /**
   * Get mesh cutter configuration for sync
   */
  getMeshCutterConfig() {
    // Include widget states (visible, clippingActive) for each plane
    const widgetStates = {};
    if (this.cuttingPlaneWidget) {
      this.cuttingPlaneWidget.planeWidgets.forEach((widget, planeId) => {
        widgetStates[planeId] = {
          visible: widget.visible,
          clippingActive: widget.clippingActive
        };
      });
    }

    return {
      planes: this.meshCutterPlanes || [],
      widgetStates: widgetStates,
      data: window.currentMeshCutterData || null
    };
  }

  /**
   * Load mesh cutter configuration (for sync)
   */
  loadMeshCutterConfig(config) {
    if (!config) return;

    this.meshCutterPlanes = config.planes || [];
    this.renderCuttingPlanesList();

    // Sync the CuttingPlaneWidget with the loaded planes
    if (this.cuttingPlaneWidget) {
      // If mesh cutter is enabled, rebuild all plane widgets
      const meshCutterEnabled = document.getElementById('meshCutterEnabled')?.checked;
      if (meshCutterEnabled && this.cuttingPlaneWidget.enabled) {
        this.cuttingPlaneWidget.rebuildAllPlaneWidgets();

        // Apply widget states (visible, clippingActive) after rebuild
        if (config.widgetStates) {
          Object.keys(config.widgetStates).forEach(planeId => {
            const state = config.widgetStates[planeId];
            const widget = this.cuttingPlaneWidget.planeWidgets.get(planeId);
            if (widget) {
              // Apply visibility state
              if (state.visible !== undefined && widget.visible !== state.visible) {
                this.cuttingPlaneWidget.setPlaneWidgetVisible(planeId, state.visible);
              }
              // Apply clipping active state
              if (state.clippingActive !== undefined && widget.clippingActive !== state.clippingActive) {
                widget.clippingActive = state.clippingActive;
              }
            }
          });
          // Update clipping after applying all states
          this.cuttingPlaneWidget.updateClippingPlanes();
        }
      }
    }

    if (config.data) {
      window.currentMeshCutterData = config.data;
      this.renderCutMesh(config.data);
      this.displayMeshCutterResults(config.data);
    }
  }

  /**
   * Get mesh eraser configuration for sync
   */
  getMeshEraserConfig() {
    const eraser = this.meshEraser;
    if (!eraser) return null;

    return {
      enabled: eraser.enabled,
      brushRadiusPercent: eraser.brushRadiusPercent,
      brushShape: eraser.brushShape,
      sequences: eraser.sequences.map(seq => ({
        id: seq.id,
        name: seq.name,
        erasedTriangles: Object.fromEntries(
          Array.from(seq.erasedTriangles.entries()).map(([k, v]) => [k, Array.from(v)])
        ),
        visible: seq.visible,
        color: seq.color
      }))
    };
  }

  /**
   * Load mesh eraser configuration (for sync)
   */
  loadMeshEraserConfig(config) {
    if (!config) return;

    const eraser = this.meshEraser;
    if (!eraser) return;

    // Restore brush radius
    if (config.brushRadiusPercent !== undefined) {
      eraser.brushRadiusPercent = config.brushRadiusPercent;
      const slider = document.getElementById('eraserBrushRadius');
      const input = document.getElementById('eraserBrushRadiusValue');
      if (slider) slider.value = config.brushRadiusPercent;
      if (input) input.value = config.brushRadiusPercent;
    }

    // Restore brush shape
    if (config.brushShape) {
      eraser.brushShape = config.brushShape;
      eraser._recreateBrushCursor();
      const shapeSelect = document.getElementById('eraserBrushShape');
      if (shapeSelect) shapeSelect.value = config.brushShape;
      const sizeLabel = document.getElementById('eraserBrushSizeLabel');
      if (sizeLabel) sizeLabel.textContent = config.brushShape === 'cube' ? 'Brush Side' : 'Brush Radius';
    }

    // Enable if needed (this captures target mesh, builds spatial grid, etc.)
    if (config.enabled && !eraser.enabled) {
      eraser.enable();
    }

    // Restore sequences
    eraser.sequences = (config.sequences || []).map(s => {
      // Handle both old format (flat array) and new format (object with mesh keys)
      let erasedTriangles;
      if (Array.isArray(s.erasedTriangles)) {
        // Old format: flat array of triangle indices (single mesh)
        const singleMesh = eraser._getSingleMesh();
        const meshKey = singleMesh ? eraser._getMeshKey(singleMesh) : '_legacy';
        erasedTriangles = new Map([[meshKey, new Set(s.erasedTriangles)]]);
      } else if (s.erasedTriangles && typeof s.erasedTriangles === 'object') {
        // New format: object with mesh keys → triangle index arrays
        erasedTriangles = new Map(
          Object.entries(s.erasedTriangles).map(([k, v]) => [k, new Set(v)])
        );
      } else {
        erasedTriangles = new Map();
      }
      return {
        id: s.id,
        name: s.name,
        erasedTriangles,
        visible: s.visible,
        color: s.color
      };
    });
    eraser.activeSequenceId = null;

    // Rebuild index buffer to reflect the synced sequences
    if (eraser.enabled && (eraser.originalIndices || eraser.originalIndicesMap.size > 0)) {
      eraser._rebuildIndexBuffer();
    }

    this.renderEraserSequencesList();
    eraser._updateResultsDisplay();
  }

  // ============================================================================
  // ADVANCED STREAMLINES
  // ============================================================================

  /**
   * Apply advanced streamlines (tubes, particles, ribbons) from UI
   */
  async applyAdvancedStreamlinesFromUI() {
    const viewer = window.viewer;
    if (!viewer || !viewer.currentData) {
      console.warn('No data available for advanced streamlines');
      return;
    }

    // Get streamline type
    const typeSelect = document.getElementById('advancedStreamlineType');
    const type = typeSelect ? typeSelect.value : 'tubes';

    // Get common parameters
    const seedConfig = this.getAdvancedStreamlineSeedConfig();
    const vectorFieldSelect = document.getElementById('advancedStreamlineVectorField');
    const vectorField = vectorFieldSelect?.value || 'U';
    const stepSize = parseFloat(document.getElementById('advancedStreamlineStepSize')?.value) || 0.01;
    const maxLength = parseFloat(document.getElementById('advancedStreamlineMaxLength')?.value) || 100;
    const direction = document.getElementById('advancedStreamlineDirection')?.value || 'both';
    const colormap = document.getElementById('advancedStreamlineColormap')?.value || 'jet';

    // Call appropriate method based on type
    if (type === 'tubes') {
      const radius = parseFloat(document.getElementById('tubeRadius')?.value) || 0.01;
      const varyBy = document.getElementById('tubeVaryBy')?.value || 'speed';
      const sides = parseInt(document.getElementById('tubeSides')?.value) || 8;

      await this.applyStreamTubes({
        seed: seedConfig,
        vector_field: vectorField,
        step_size: stepSize,
        max_length: maxLength,
        direction: direction,
        radius: radius,
        vary_radius_by: varyBy,
        tube_sides: sides,
        colormap: colormap
      });
    } else if (type === 'particles') {
      const particleSize = parseFloat(document.getElementById('particleSize')?.value) || 0.005;
      const particleSpeed = parseFloat(document.getElementById('particleSpeed')?.value) || 1.0;
      const particleCount = parseInt(document.getElementById('particleCount')?.value) || 100;

      await this.applyParticleStreamlines({
        seed: seedConfig,
        vector_field: vectorField,
        step_size: stepSize,
        max_length: maxLength,
        direction: direction,
        particle_size: particleSize,
        particle_speed: particleSpeed,
        particle_count: particleCount,
        colormap: colormap
      });
    } else if (type === 'ribbons') {
      const ribbonWidth = parseFloat(document.getElementById('ribbonWidth')?.value) || 0.02;
      const ribbonNormal = document.getElementById('ribbonNormal')?.value || 'vorticity';

      await this.applyRibbonStreamlines({
        seed: seedConfig,
        vector_field: vectorField,
        step_size: stepSize,
        max_length: maxLength,
        direction: direction,
        ribbon_width: ribbonWidth,
        normal_mode: ribbonNormal,
        colormap: colormap
      });
    }
  }

  /**
   * Get seed configuration from UI
   */
  getAdvancedStreamlineSeedConfig() {
    const seedTypeSelect = document.getElementById('advancedStreamlineSeedType');
    const seedType = seedTypeSelect ? seedTypeSelect.value : 'line';

    if (seedType === 'point') {
      return {
        type: 'point',
        center: [
          parseFloat(document.getElementById('advancedStreamlinePointCenterX')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePointCenterY')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePointCenterZ')?.value) || 0
        ],
        radius: parseFloat(document.getElementById('advancedStreamlinePointRadius')?.value) || 1,
        count: parseInt(document.getElementById('advancedStreamlinePointCount')?.value) || 50
      };
    } else if (seedType === 'line') {
      return {
        type: 'line',
        start: [
          parseFloat(document.getElementById('advancedStreamlineLineStartX')?.value) || -1,
          parseFloat(document.getElementById('advancedStreamlineLineStartY')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlineLineStartZ')?.value) || 0
        ],
        end: [
          parseFloat(document.getElementById('advancedStreamlineLineEndX')?.value) || 1,
          parseFloat(document.getElementById('advancedStreamlineLineEndY')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlineLineEndZ')?.value) || 0
        ],
        resolution: parseInt(document.getElementById('advancedStreamlineLineResolution')?.value) || 25
      };
    } else if (seedType === 'plane') {
      return {
        type: 'plane',
        origin: [
          parseFloat(document.getElementById('advancedStreamlinePlaneOriginX')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePlaneOriginY')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePlaneOriginZ')?.value) || 0
        ],
        normal: [
          parseFloat(document.getElementById('advancedStreamlinePlaneNormalX')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePlaneNormalY')?.value) || 0,
          parseFloat(document.getElementById('advancedStreamlinePlaneNormalZ')?.value) || 1
        ],
        width: parseFloat(document.getElementById('advancedStreamlinePlaneWidth')?.value) || 1,
        height: parseFloat(document.getElementById('advancedStreamlinePlaneHeight')?.value) || 1,
        resolution_u: parseInt(document.getElementById('advancedStreamlinePlaneResolutionU')?.value) || 5,
        resolution_v: parseInt(document.getElementById('advancedStreamlinePlaneResolutionV')?.value) || 5
      };
    }
  }

  /**
   * Apply stream tubes
   */
  async applyStreamTubes(config) {
    try {
      console.log('Applying stream tubes...', config);

      const response = await fetch('/api/streamlines/tubes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth.getToken()}`
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to generate stream tubes: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Stream tubes data received:', data);

      // Initialize StreamTubeRenderer if not exists
      if (!window.streamTubeRenderer) {
        window.streamTubeRenderer = new StreamTubeRenderer(this.engine);
      }

      // Create tubes
      window.streamTubeRenderer.createStreamTubes(data, {
        radius: config.radius,
        radialSegments: config.tube_sides,
        varyRadiusBy: config.vary_radius_by,
        colormap: config.colormap
      });

      this.activeFilters.set('advanced-streamlines-tubes', {
        type: 'stream_tubes',
        config: config
      });

    } catch (error) {
      console.error('Error applying stream tubes:', error);
      Utils.notify(`Failed to apply stream tubes: ${error.message}`, 'error');
    }
  }

  /**
   * Apply particle streamlines
   */
  async applyParticleStreamlines(config) {
    try {
      console.log('Applying particle streamlines...', config);

      const response = await fetch('/api/streamlines/particles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth.getToken()}`
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to generate particle streamlines: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Particle streamlines data received:', data);

      // Initialize ParticleAnimator if not exists
      if (!window.particleAnimator) {
        window.particleAnimator = new ParticleAnimator(this.engine);
      }

      // Initialize particles
      window.particleAnimator.initialize(data, {
        speed: config.particle_speed,
        particleSize: config.particle_size,
        particleCount: config.particle_count,
        colormap: config.colormap
      });

      // Start animation automatically
      window.particleAnimator.start();

      // Show animate button
      const btnAnimate = document.getElementById('btnAnimateParticles');
      if (btnAnimate) {
        btnAnimate.style.display = 'inline-block';
        btnAnimate.textContent = 'Stop Animation';
      }

      this.activeFilters.set('advanced-streamlines-particles', {
        type: 'particle_streamlines',
        config: config
      });

    } catch (error) {
      console.error('Error applying particle streamlines:', error);
      Utils.notify(`Failed to apply particle streamlines: ${error.message}`, 'error');
    }
  }

  /**
   * Apply ribbon streamlines
   */
  async applyRibbonStreamlines(config) {
    try {
      console.log('Applying ribbon streamlines...', config);

      const response = await fetch('/api/streamlines/ribbons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.Auth.getToken()}`
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to generate ribbon streamlines: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Ribbon streamlines data received:', data);

      // Render ribbons using THREE.js
      this.renderRibbonStreamlines(data, config);

      this.activeFilters.set('advanced-streamlines-ribbons', {
        type: 'ribbon_streamlines',
        config: config
      });

    } catch (error) {
      console.error('Error applying ribbon streamlines:', error);
      Utils.notify(`Failed to apply ribbon streamlines: ${error.message}`, 'error');
    }
  }

  /**
   * Render ribbon streamlines as THREE.js meshes
   */
  renderRibbonStreamlines(data, config) {
    if (!data || !data.ribbons || data.ribbons.length === 0) {
      console.warn('No ribbon data to render');
      return;
    }

    // Clear existing ribbons
    this.clearAdvancedStreamlines('ribbons');

    const colormap = window.Colormap?.presets?.[config.colormap] || window.Colormap?.presets?.jet;
    const ribbonMeshes = [];

    // Calculate scalar range
    let minSpeed = Infinity;
    let maxSpeed = -Infinity;
    data.ribbons.forEach(ribbon => {
      ribbon.speed?.forEach(s => {
        if (s < minSpeed) minSpeed = s;
        if (s > maxSpeed) maxSpeed = s;
      });
    });

    data.ribbons.forEach(ribbon => {
      if (!ribbon.vertices || ribbon.vertices.length < 4) return;

      // Create ribbon geometry
      const geometry = new THREE.BufferGeometry();
      const vertices = [];
      const colors = [];
      const indices = [];

      // Build vertices and colors
      for (let i = 0; i < ribbon.vertices.length; i++) {
        const v = ribbon.vertices[i];
        vertices.push(v[0], v[1], v[2]);

        // Color based on speed
        const speed = ribbon.speed?.[Math.floor(i / 2)] || 0;
        const normalizedSpeed = (speed - minSpeed) / (maxSpeed - minSpeed + 1e-10);
        const color = colormap(normalizedSpeed);
        colors.push(color[0], color[1], color[2]);
      }

      // Build triangle indices for ribbon strip
      for (let i = 0; i < ribbon.vertices.length - 2; i += 2) {
        indices.push(i, i + 1, i + 2);
        indices.push(i + 1, i + 3, i + 2);
      }

      geometry.setIndex(indices);
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.computeVertexNormals();

      const material = new THREE.MeshPhongMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
        shininess: 30,
        transparent: false
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.type = 'ribbonStreamline';
      ribbonMeshes.push(mesh);
      this.engine.scene.add(mesh);
    });

    // Store meshes for cleanup
    if (!window.advancedStreamlineMeshes) {
      window.advancedStreamlineMeshes = {};
    }
    window.advancedStreamlineMeshes.ribbons = ribbonMeshes;

    console.log(`Created ${ribbonMeshes.length} ribbon streamlines`);
  }

  /**
   * Clear advanced streamlines
   */
  clearAdvancedStreamlines(type = 'all') {
    if (type === 'all' || type === 'tubes') {
      if (window.streamTubeRenderer) {
        window.streamTubeRenderer.clear();
      }
      this.activeFilters.delete('advanced-streamlines-tubes');
    }

    if (type === 'all' || type === 'particles') {
      if (window.particleAnimator) {
        window.particleAnimator.clear();
      }
      this.activeFilters.delete('advanced-streamlines-particles');

      // Hide animate button
      const btnAnimate = document.getElementById('btnAnimateParticles');
      if (btnAnimate) {
        btnAnimate.style.display = 'none';
      }
    }

    if (type === 'all' || type === 'ribbons') {
      if (window.advancedStreamlineMeshes?.ribbons) {
        window.advancedStreamlineMeshes.ribbons.forEach(mesh => {
          if (mesh.geometry) mesh.geometry.dispose();
          if (mesh.material) mesh.material.dispose();
          this.engine.scene.remove(mesh);
        });
        window.advancedStreamlineMeshes.ribbons = [];
      }
      this.activeFilters.delete('advanced-streamlines-ribbons');
    }
  }

  /**
   * Toggle particle animation
   */
  toggleParticleAnimation() {
    if (!window.particleAnimator) return;

    const btnAnimate = document.getElementById('btnAnimateParticles');
    if (!btnAnimate) return;

    if (window.particleAnimator.isRunning()) {
      window.particleAnimator.stop();
      btnAnimate.textContent = 'Start Animation';
    } else {
      window.particleAnimator.start();
      btnAnimate.textContent = 'Stop Animation';
    }
  }

  /**
   * Render stream tubes from backend data
   */
  renderStreamTubes(tubeData, colormap, scalarRange, group) {
    const tubes = tubeData.tubes || [];

    tubes.forEach((tube, index) => {
      const centerline = tube.centerline || [];
      const radii = tube.radii || [];
      const scalars = tube.scalars || [];
      const sides = tube.sides || 8;

      if (centerline.length < 2) return;

      // Create tube geometry using THREE.TubeGeometry
      const points = centerline.map(p => new THREE.Vector3(p[0], p[1], p[2]));
      const curve = new THREE.CatmullRomCurve3(points);
      const tubularSegments = Math.max((centerline.length - 1) * 3, 12);
      const baseRadius = Number.isFinite(tube.base_radius)
        ? tube.base_radius
        : Number.isFinite(tubeData.base_radius)
          ? tubeData.base_radius
          : (radii[0] || 0.01);

      const tubeGeometry = new THREE.TubeGeometry(
        curve,
        tubularSegments,
        1,         // start with unit radius, re-scale per vertex below
        sides,
        false
      );

      const ringSize = sides + 1;
      const segmentCount = tubularSegments + 1;
      const radiusSamples = new Array(segmentCount);
      for (let s = 0; s < segmentCount; s++) {
        if (radii.length === 0) {
          radiusSamples[s] = baseRadius;
          continue;
        }
        const t = segmentCount === 1 ? 0 : s / (segmentCount - 1);
        const sampleIndex = Math.min(
          Math.round(t * (radii.length - 1)),
          radii.length - 1
        );
        const value = radii[sampleIndex];
        radiusSamples[s] = Number.isFinite(value) ? value : baseRadius;
      }

      const positions = tubeGeometry.attributes.position;
      const normalsAttr = tubeGeometry.attributes.normal;

      const centerCache = new Array(segmentCount);
      for (let s = 0; s < segmentCount; s++) {
        const t = segmentCount === 1 ? 0 : s / (segmentCount - 1);
        centerCache[s] = curve.getPointAt(t);
      }

      for (let s = 0; s < segmentCount; s++) {
        const radiusAtSegment = Math.max(radiusSamples[s], 1e-6);
        const centerPoint = centerCache[s];

        for (let r = 0; r < ringSize; r++) {
          const index = s * ringSize + r;
          if (index >= positions.count) break;

          const nx = normalsAttr.getX(index);
          const ny = normalsAttr.getY(index);
          const nz = normalsAttr.getZ(index);

          positions.setXYZ(
            index,
            centerPoint.x + nx * radiusAtSegment,
            centerPoint.y + ny * radiusAtSegment,
            centerPoint.z + nz * radiusAtSegment
          );
        }
      }
      positions.needsUpdate = true;

      // Apply vertex colors based on scalars
      const colors = [];

      // Get solid color for streamline colormap dropdown if using solid colormap
      const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('streamlineColormap') : null;

      for (let i = 0; i < positions.count; i++) {
        const segmentIndex = Math.floor(i / ringSize);
        const scalarIndex = Math.min(segmentIndex, scalars.length - 1);
        const scalar = scalars[scalarIndex];

        let color;
        if (scalar != null && scalarRange) {
          const normalized = Math.max(0, Math.min(1, (scalar - scalarRange.min) / (scalarRange.max - scalarRange.min)));
          color = Colormap.getColorThree(normalized, colormap, solidColorHex);
        } else {
          color = Colormap.getColorThree(0.5, colormap, solidColorHex);
        }

        colors.push(color.r, color.g, color.b);
      }

      tubeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const material = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide
      });

      const tubeMesh = new THREE.Mesh(tubeGeometry, material);
      tubeGeometry.computeBoundingBox();
      tubeGeometry.computeBoundingSphere();
      group.add(tubeMesh);
    });

    console.log(`Rendered ${tubes.length} stream tubes`);
  }

  /**
   * Render particle animation from backend data
   */
  renderParticleAnimation(particleData, colormap, scalarRange, group, options) {
    const particles = particleData.particles || [];

    // Create particle geometry
    const geometry = new THREE.BufferGeometry();
    const positions = [];
    const colors = [];
    const velocities = [];

    // Get solid color for streamline colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('streamlineColormap') : null;

    particles.forEach(particle => {
      const path = particle.path || [];
      if (path.length === 0) return;

      // Start particle at beginning of path
      const startPos = path[0];
      positions.push(startPos[0], startPos[1], startPos[2]);

      // Color based on speed if available
      const scalar = particle.scalars?.[0];
      let color;
      if (scalar != null && scalarRange) {
        const normalized = Math.max(0, Math.min(1, (scalar - scalarRange.min) / (scalarRange.max - scalarRange.min)));
        color = Colormap.getColorThree(normalized, colormap, solidColorHex);
      } else {
        color = Colormap.getColorThree(0.5, colormap, solidColorHex);
      }
      colors.push(color.r, color.g, color.b);

      // Store path for animation
      velocities.push(0, 0, 0);  // Will be updated during animation
    });

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.05,  // Increased size for visibility
      vertexColors: true,
      sizeAttenuation: true
    });

    const particleSystem = new THREE.Points(geometry, material);
    group.add(particleSystem);

    // Store particle data for animation - each particle has its own step
    particleSystem.userData.particles = particles;
    particleSystem.userData.particleSteps = particles.map(() => Math.random() * 100); // Random starting positions
    particleSystem.userData.animationSpeed = parseFloat(document.getElementById('streamlineParticleSpeed')?.value) || 1.0;

    // Set up animation
    if (!window.particleAnimator) {
      window.particleAnimator = {
        isAnimating: false,
        particleSystem: particleSystem,
        start: function () {
          this.isAnimating = true;
          this.animate();
          const btn = document.getElementById('btnToggleParticleAnimation');
          if (btn) {
            btn.style.display = 'inline-block';
            btn.textContent = 'Pause';
          }
        },
        stop: function () {
          this.isAnimating = false;
          const btn = document.getElementById('btnToggleParticleAnimation');
          if (btn) btn.textContent = 'Start';
        },
        animate: function () {
          if (!this.isAnimating || !this.particleSystem) return;

          const particles = this.particleSystem.userData.particles;
          const particleSteps = this.particleSystem.userData.particleSteps;
          const positions = this.particleSystem.geometry.attributes.position.array;
          const speed = this.particleSystem.userData.animationSpeed;

          // Update particle positions along their paths - each particle moves independently
          particles.forEach((particle, i) => {
            const path = particle.path;
            if (!path || path.length === 0) return;

            // Update this particle's step along its path
            particleSteps[i] = (particleSteps[i] + speed * 0.5) % path.length;
            const step = particleSteps[i];

            const index = Math.floor(step);
            const nextIndex = (index + 1) % path.length;
            const t = step - index;

            // Interpolate position along the path
            const p1 = path[index];
            const p2 = path[nextIndex];

            positions[i * 3] = p1[0] + (p2[0] - p1[0]) * t;
            positions[i * 3 + 1] = p1[1] + (p2[1] - p1[1]) * t;
            positions[i * 3 + 2] = p1[2] + (p2[2] - p1[2]) * t;
          });

          this.particleSystem.geometry.attributes.position.needsUpdate = true;
          requestAnimationFrame(() => this.animate());
        }
      };
    }

    window.particleAnimator.particleSystem = particleSystem;
    window.particleAnimator.start();

    console.log(`Rendered ${particles.length} particles for animation`);
  }

  /**
   * Render ribbon streamlines from backend data
   */
  renderRibbonStreamlines(ribbonData, colormap, scalarRange, group) {
    const ribbons = ribbonData.ribbons || [];
    const width = ribbonData.width || 0.02;

    // Get solid color for streamline colormap dropdown if using solid colormap
    const solidColorHex = Colormap.isSolidColor(colormap) ? Colormap.getSolidColor('streamlineColormap') : null;

    ribbons.forEach((ribbon, index) => {
      const centerline = ribbon.centerline || [];
      const scalars = ribbon.scalars || [];

      if (centerline.length < 2) return;

      // Create ribbon geometry
      const vertices = [];
      const indices = [];
      const colors = [];

      for (let i = 0; i < centerline.length - 1; i++) {
        const p1 = centerline[i];
        const p2 = centerline[i + 1];

        // Calculate tangent
        const tangent = new THREE.Vector3(
          p2[0] - p1[0],
          p2[1] - p1[1],
          p2[2] - p1[2]
        ).normalize();

        // Calculate normal (perpendicular to tangent)
        const normal = new THREE.Vector3();
        if (Math.abs(tangent.y) < 0.9) {
          normal.set(0, 1, 0).cross(tangent).normalize();
        } else {
          normal.set(1, 0, 0).cross(tangent).normalize();
        }

        // Create ribbon quad vertices
        const hw = width / 2;  // half width
        const v1 = new THREE.Vector3(p1[0], p1[1], p1[2]).add(normal.clone().multiplyScalar(hw));
        const v2 = new THREE.Vector3(p1[0], p1[1], p1[2]).add(normal.clone().multiplyScalar(-hw));

        // Add vertices
        const baseIndex = vertices.length / 3;
        vertices.push(v1.x, v1.y, v1.z);
        vertices.push(v2.x, v2.y, v2.z);

        // Add colors
        const scalar = scalars[i];
        let color;
        if (scalar != null && scalarRange) {
          const normalized = Math.max(0, Math.min(1, (scalar - scalarRange.min) / (scalarRange.max - scalarRange.min)));
          color = Colormap.getColorThree(normalized, colormap, solidColorHex);
        } else {
          color = Colormap.getColorThree(0.5, colormap, solidColorHex);
        }
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);

        // Add triangle indices (if not the first segment)
        if (i > 0) {
          const prevBase = baseIndex - 2;
          indices.push(prevBase, prevBase + 1, baseIndex);
          indices.push(prevBase + 1, baseIndex + 1, baseIndex);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide
      });

      const ribbonMesh = new THREE.Mesh(geometry, material);
      group.add(ribbonMesh);
    });

    console.log(`Rendered ${ribbons.length} ribbon streamlines`);
  }
}

// Export
window.Filters = Filters;
